import usePageTitle from '../hooks/usePageTitle';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchMyFactory, fetchFactoryProducts } from '../lib/catalogImport';
import { fetchMyOwnerThreads } from '../lib/factoryChat';
import FactoryCatalogQuickEdit from '../components/supplier/FactoryCatalogQuickEdit';
import { sb, SUPABASE_URL } from '../supabase';
import { supplierRequestTitle } from '../lib/supplierRequestTitle';
import { uploadWithProgress } from '../lib/uploadWithProgress';
import { readSwrCache, writeSwrCache } from '../lib/useStaleWhileRevalidate';
import OrderInvoiceModal from '../components/OrderInvoiceModal';
import {
  DISPLAY_CURRENCIES,
  DEFAULT_DISPLAY_CURRENCY,
  formatCurrencyAmount,
  formatPriceWithConversion,
  normalizeDisplayCurrency,
} from '../lib/displayCurrency';
import {
  PRODUCT_GALLERY_LIMIT,
  getProductGalleryImages,
  getPrimaryProductImage,
  normalizeProductDraftMedia,
} from '../lib/productMedia';
import { T, CATEGORIES, OFFER_STATUS, getSpecialtyLabel } from '../lib/supplierDashboardConstants';
import {
  VERIFICATION_IMAGE_LIMIT,
  VERIFICATION_VIDEO_LIMIT,
  VERIFICATION_VIDEO_MAX_BYTES,
  getCompanyDescription,
  normalizeProfileList,
  serializeProfileList,
  getProfileReadiness,
  buildSupplierJourneySteps,
  normalizeVerificationMedia,
  buildSettingsState,
  buildVerificationState,
  buildPayoutState,
  normalizeTextInput,
  normalizeOptionalNumber,
  normalizeOptionalInteger,
  stripEmptyFields,
  buildSettingsPayload,
  buildPayoutPayload,
  hasPersistedSupplierSettings,
  hasPersistedSupplierPayout,
  buildSettingsSaveFeedback,
  buildPayoutSaveFeedback,
  getSupplierVerificationDraftKey,
  getSupplierDashboardUiStateKey,
  getVerificationProgressState,
  formatDraftSavedAt,
} from '../lib/supplierFormHelpers';
import {
  SkeletonCard,
  StatCard,
  QuickAction,
  BackBtn,
  SaveFeedbackCard,
  SupplierJourneyStepper,
} from '../components/supplier/DashboardPrimitives';
import {
  VF_C,
  VF_CSS,
  VfChk,
  VfReviewDot,
  VfField,
  VfSaveError,
  VfSep,
  VfG2,
  VfDotStepper,
  VfProgressBar,
  VfStepBadges,
} from '../components/supplier/VerificationFormUI';
import {
  emptyProduct,
  PRODUCT_OPTIONAL_DB_FIELDS,
  buildProductWritePayload,
  getProductComposerValidationMessage,
  getProductFormPlaceholders,
  getProductCompletenessItems,
  ProductForm,
  ProductPreviewPanel,
  expandStoredSelect,
  COUNTRY_OF_ORIGIN_OPTIONS,
  PORT_OF_LOADING_OPTIONS,
} from '../components/supplier/ProductComposer';
import { emptyVariantData, regenerateVariants } from '../components/supplier/VariantBuilder';
import {
  emptyTierSet,
  loadProductPricingTiers,
  saveProductPricingTiers,
} from '../lib/productPricingTiers';
import { PRODUCT_TIER_EMBED, deriveProductPriceFrom } from '../lib/productPriceLookup';
import { BUCKET_PRODUCT_MEDIA, BUCKET_PRODUCT_CERTS, removeStorageObjectByUrl } from '../lib/productMediaCleanup';
import { loadProductCertifications, saveProductCertifications, emptyCertRow, CERT_TYPES, CERT_MAX_COUNT } from '../lib/productCertifications';
import SupplierOnboardingSequence from '../components/supplier/SupplierOnboardingSequence';
import { supplierQuotesReachTraders, fetchMyQuotesReachTraders, isQuoteHeldForVerification, heldQuoteCopy } from '../lib/supplierQuoteVisibility';
import ConnectFactoryPrompt from '../components/supplier/ConnectFactoryPrompt';
import SupplierPayoutLockBanner from '../components/supplier/SupplierPayoutLockBanner';
import SupplierRequestsPanel from '../components/supplier/SupplierRequestsPanel';
import QuoteFields, { SAMPLE_ASKED, validateQuoteCore } from '../components/quote/QuoteFields';
// The trader's own answers, read back. Same source the request forms write
// from, so a label can never drift from the value stored against it.
import { REQUEST_UNITS, labelFor } from '../lib/requestFormOptions';
import {
  supplierTermRows, supplierTermsCopy, prefillPriceBasis, deliveryAskText, deliveryShortfallText,
} from '../lib/supplierRequestTerms';
import { traderLabel } from '../lib/counterpartyName';
import { fetchSupplierReviews, reviewerLabel } from '../lib/supplierReviews';
import DeliveryCapability from '../components/quote/DeliveryCapability';
import {
  loadDeliveryTerms, saveDeliveryTerms, allowedBasesFor, defaultBasisFor,
} from '../lib/supplierDeliveryTerms';
import { quoteStrings } from '../components/quote/quoteStrings';
import SupplierHomePanel from '../components/supplier/SupplierHomePanel';
import ExclusiveSellerBanner from '../components/supplier/ExclusiveSellerBanner';
import ExclusiveCategoriesPanel from '../components/supplier/ExclusiveCategoriesPanel';
import SupplierRequestAttachments from '../components/supplier/SupplierRequestAttachments';
import { SHOW_SUPPLIER_REFERRALS } from '../lib/featureFlags';
import SupplierHeader from '../components/supplier/SupplierHeader';
import { runWithOptionalColumns } from '../lib/supabaseColumnFallback';
import { sendMaabarEmail } from '../lib/maabarEmail';
import { buildTranslatedProductFields } from '../lib/requestTranslation';
import { isOfferNoteContactError, offerNoteContactMessage } from '../lib/offerNoteContact';
import {
  getOfferEstimatedTotal,
  getOfferProductSubtotal,
  getOfferShippingCost,
  getOfferShippingMethod,
  hasOfferShippingCost,
} from '../lib/offerPricing';
import {
  getSupplierMaabarId,
  getSupplierOnboardingState,
  getSupplierStageLabel,
  getSupplierTradeLinks,
  normalizeSupplierDocStoragePath,
} from '../lib/supplierOnboarding';
import { attachDirectoryProfiles } from '../lib/profileVisibility';
import { SUPPLIER_PRIMARY_TABS, SUPPLIER_MORE_TABS, navLabel } from '../lib/navItems';
import {
  fetchProductInquiryThreads,
  getProductInquiryProductName,
  getProductInquiryStatusLabel,
} from '../lib/productInquiry';

// Function لترجمة الاستفسارات
const translateInquiryText = async (text, sourceLang, targetLang) => {
  if (!text || sourceLang === targetLang) return text;
  
  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/maabar-ai`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        task: 'chat_translation',
        payload: {
          text,
          sourceLanguage: sourceLang,
          targetLanguage: targetLang,
          conversationRole: 'product_inquiry',
        },
      }),
    });
    
    if (!response.ok) {
      console.error('Translation API error:', await response.text());
      return text; // نرجع النص الأصلي إذا فشلت الترجمة
    }
    
    const data = await response.json();
    return data.translatedText || text;
  } catch (error) {
    console.error('Translation error:', error);
    return text; // نرجع النص الأصلي إذا فشلت الترجمة
  }
};

// The My Offers query, lifted out of the call site so a test can hold it.
//
// ⚠️ THE EDIT MODAL PREFILLS FROM THIS ROW, so a column missing here is not a
// missing column — it is a field that silently disappears from the editor.
// That is exactly what happened to the sample block: `sample_requirement` was
// not selected, so `editOfferModal.requests.sample_requirement` was undefined,
// `SAMPLE_ASKED.includes('')` was false, and QuoteFields rendered no sample
// section at all — on offers whose request DID ask for a sample, and whose
// sample_price/sample_days the save path was already writing. The supplier
// could not see or correct the numbers being stored for them.
// The offers side is `*`, so every offer column reaches the prefill; it is the
// nested requests(...) list that has to be kept honest by hand.
export const MY_OFFERS_SELECT = '*,requests(title_ar,title_en,title_zh,buyer_id,status,request_kind,sourcing_mode,tracking_number,shipping_status,shipping_company,estimated_delivery,quantity,description,payment_plan,payment_pct,category,sample_requirement,delivery_preference)';

const SEND_EMAILS_URL = 'https://utzalmszfqfcofywfetv.supabase.co/functions/v1/send-email';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InV0emFsbXN6ZnFmY29meXdmZXR2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM2NjE4NDAsImV4cCI6MjA4OTIzNzg0MH0.SSqFCeBRhKRIrS8oQasBkTsZxSv7uZGCT9pqfK-YmX8';

const getTrackingUrl = (company, num) => {
  const urls = {
    'DHL':    `https://www.dhl.com/track?tracking-id=${num}`,
    'FedEx':  `https://www.fedex.com/tracking?tracknumbers=${num}`,
    'Aramex': `https://www.aramex.com/track/${num}`,
    'UPS':    `https://www.ups.com/track?tracknum=${num}`,
    'SMSA':   `https://www.smsaexpress.com/track?awbno=${num}`,
  };
  return urls[company] || `https://t.17track.net/en#nums=${num}`;
};

const STORAGE_URL = 'https://utzalmszfqfcofywfetv.supabase.co/storage/v1/object/public/product-images/';

export default function DashboardSupplier({ user, profile, lang, setLang, setUser, displayCurrency, setDisplayCurrency, setProfile, exchangeRates }) {
  const viewerCurrency = normalizeDisplayCurrency(displayCurrency || DEFAULT_DISPLAY_CURRENCY);
  const nav      = useNavigate();
  const location = useLocation();
  const t        = T[lang] || T.zh;
  const cats     = CATEGORIES[lang] || CATEGORIES.zh;
  // Shared quote-core labels — the same words the public RFQ form uses.
  const qt       = quoteStrings(lang);
  const isAr     = lang === 'ar';
  const verificationDraftKey = user?.id ? getSupplierVerificationDraftKey(user.id) : '';
  const dashboardUiStateKey = user?.id ? getSupplierDashboardUiStateKey(user.id) : '';

  const [stats, setStats]                   = useState({ products: 0, offers: 0, messages: 0, productInquiries: 0 });
  const [myOffers, setMyOffers]             = useState([]);
  const [myProducts, setMyProducts]         = useState([]);
  // Claimed-factory owner: their factory catalog + trader messages + RFQ invites
  // live outside the legacy supplier tables — surface them as banners/counters.
  const [ownerFactory, setOwnerFactory]     = useState(null);
  // Server's answer to "does my next quote reach the trader?" — null until known.
  const [quotesReachTraders, setQuotesReachTraders] = useState(null);
  const [ownerFacProducts, setOwnerFacProducts] = useState(0);
  const [ownerFacMsgs, setOwnerFacMsgs]     = useState(0);
  const [ownerFacInvites, setOwnerFacInvites] = useState(0);
  const [ownerCatalog, setOwnerCatalog]     = useState(null);   // factory_products (lazy, my-products tab)
  const [productInquiries, setProductInquiries] = useState([]);
  const [inquiryReplies, setInquiryReplies] = useState({});
  const [replyingInquiryId, setReplyingInquiryId] = useState(null);
  const [translatedInquiries, setTranslatedInquiries] = useState({}); // لتخزين الاستفسارات المترجمة
  const [requests, setRequests]             = useState([]);
  const [activeTab, setActiveTab]           = useState('overview');
  // activeCat/setActiveCat removed 2026-09-19 with the category filter chips:
  // they existed only to filter the open-RFQ book, and the book is gone. `cats`
  // stays — the product forms and the category label still use it.
  const [activeBottomTab, setActiveBottomTab] = useState('home');
  const [copiedKey, setCopiedKey] = useState('');
  const [moreMenuOpen, setMoreMenuOpen]     = useState(false);
  const [desktopMoreOpen, setDesktopMoreOpen] = useState(false);
  const [requestStatusFilter, setRequestStatusFilter] = useState('all');
  const [productStatusFilter, setProductStatusFilter] = useState('all');
  const [loadingOffers, setLoadingOffers]   = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);
  // Which single request the request-detail view is showing.
  const [detailRequestId, setDetailRequestId] = useState(null);
  const [directOrders, setDirectOrders] = useState([]);
  const [loadingDirectOrders, setLoadingDirectOrders] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState(null);   // { requestId, order } for the invoice modal
  const [directOrderActioning, setDirectOrderActioning] = useState({});
  const [directOrdersNowMs, setDirectOrdersNowMs] = useState(() => Date.now());
  const [confirmedDirectOrders, setConfirmedDirectOrders] = useState([]);
  const [loadingConfirmedDirectOrders, setLoadingConfirmedDirectOrders] = useState(false);
  const [paidDirectOrders, setPaidDirectOrders] = useState([]);
  const [loadingPaidDirectOrders, setLoadingPaidDirectOrders] = useState(false);
  const [directShippingActioning, setDirectShippingActioning] = useState({});
  const [shippingDirectOrders, setShippingDirectOrders] = useState([]);
  const [completedDirectOrders, setCompletedDirectOrders] = useState([]);
  const [loadingDirectHistory, setLoadingDirectHistory] = useState(false);
  const [trackingInputs, setTrackingInputs] = useState({});
  const [shippingCompany, setShippingCompany] = useState('DHL');
  const [product, setProduct]               = useState(emptyProduct);
  const [variantData, setVariantData]       = useState(emptyVariantData);
  // USD → SAR is the ADMIN rate (admin_settings.usd_sar_rate via App's
  // fetchDisplayRates) — the rate a trader's accepted offer locks — not the
  // market feed with a 3.75 fallback this used to fetch on its own, so the
  // "≈ SAR" a supplier sees under a price is what the trader is charged.
  const usdRate = Number(exchangeRates?.SAR) || null;
  const [editingProduct, setEditingProduct] = useState(null);
  const [editVariantData, setEditVariantData] = useState(emptyVariantData);
  // Snapshot of the cert rows that were on the product when editing began.
  // Used as `prevCerts` at save time so we know which rows to delete.
  const [editProductCertsSnapshot, setEditProductCertsSnapshot] = useState([]);
  const [saving, setSaving]                 = useState(false);
  const [pendingTracking, setPendingTracking] = useState([]);
  const [rejectedOffers, setRejectedOffers] = useState([]);
  const [completedPayments, setCompletedPayments] = useState(new Set());
  // Requests paid IN FULL (a second_paid row, or a 100% first_paid row) — decides
  // whether the supplier may ship now or must wait for the trader's balance.
  const [fullyPaidReqs, setFullyPaidReqs] = useState(new Set());
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [offerForms, setOfferForms]         = useState({});
  const [offers, setOffers]                 = useState({});
  const [pendingOfferIds, setPendingOfferIds] = useState(() => new Set());   // requests whose offer is "sending" (optimistic)
  const [offerToast, setOfferToast] = useState('');   // brief "offer sent" confirmation, shown ONLY after the server confirms
  const [submittingOfferId, setSubmittingOfferId] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [settings, setSettings] = useState(() => buildSettingsState(profile || {}, displayCurrency || 'USD'));
  const [verification, setVerification] = useState(() => buildVerificationState(profile || {}));
  const [payout, setPayout] = useState(() => buildPayoutState(profile || {}));
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSavedAt, setSettingsSavedAt] = useState('');
  const [settingsError, setSettingsError] = useState('');
  // Required keys the last save attempt found empty. Kept so the form can point
  // at the fields that blocked it instead of only printing a sentence.
  const [settingsMissingKeys, setSettingsMissingKeys] = useState([]);
  const [savingVerification, setSavingVerification] = useState(false);
  const [verificationSaved, setVerificationSaved] = useState(false);
  const [verificationMsg, setVerificationMsg] = useState('');
  const [uploadingVerificationDoc, setUploadingVerificationDoc] = useState({ license: false, images: false, videos: false });
  // Live upload percentage per media key (e.g. { videos: 42 }); null/absent = idle.
  const [verificationUploadPct, setVerificationUploadPct] = useState({});
  const [verificationStep, setVerificationStep] = useState(1);
  const [draftSavedAt, setDraftSavedAt] = useState('');
  const [savingPayout, setSavingPayout] = useState(false);
  const [payoutSavedAt, setPayoutSavedAt] = useState('');
  const [payoutError, setPayoutError] = useState('');
  const [productSaveMsg, setProductSaveMsg] = useState('');
  const [productComposerStep, setProductComposerStep] = useState('edit');
  const [editProductComposerStep, setEditProductComposerStep] = useState('edit');
  // The supplier's stored delivery capability (profiles.delivery_terms), asked
  // once and reused to prefill price_basis. `askCapability` is settled on load
  // rather than derived from the live selection, so the block does not vanish
  // under the supplier's hands as soon as they tick a box.
  const [deliveryTerms, setDeliveryTerms] = useState([]);
  const [askCapability, setAskCapability] = useState(false);
  const [editOfferModal, setEditOfferModal]   = useState(null);
  const [editOfferForm, setEditOfferForm]     = useState({});
  const [savingEditOffer, setSavingEditOffer] = useState(false);

  // Esc closes the edit-offer modal. Bound only while it is open, and removed
  // on close, so nothing listens on every keystroke of the rest of the page.
  //
  // It closes UNCONDITIONALLY, including mid-save, because that is exactly what
  // the Cancel button has always done and this change was not allowed to alter
  // behaviour. Esc, the backdrop and the new × therefore all do precisely what
  // Cancel does — no new restriction, no new capability.
  // (An earlier draft blocked all three while a save was in flight. That reads
  // better, but it is a behaviour change: it would take away a dismissal the
  // supplier has today. Worth doing deliberately, as its own decision.)
  useEffect(() => {
    if (!editOfferModal) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setEditOfferModal(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editOfferModal]);

  const [uploadingLogo, setUploadingLogo]   = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingFactory, setUploadingFactory] = useState(false);
  const [samples, setSamples] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [referralRewards, setReferralRewards] = useState([]);
  const [showVfSuccess, setShowVfSuccess] = useState(false);
  const verificationImages = normalizeVerificationMedia(verification.factory_images).slice(0, VERIFICATION_IMAGE_LIMIT);
  const verificationVideos = normalizeVerificationMedia(verification.factory_videos).slice(0, VERIFICATION_VIDEO_LIMIT);
  const verificationProgress = getVerificationProgressState({ settings, verification, verificationImages });
  // Compute stage/lock status from the DB profile only — NOT from local form state.
  // Merging local settings/verification into this call was causing the form to auto-lock
  // (show "under review") as soon as the supplier filled in the required fields locally,
  // because getSupplierResolvedStatus would see status='verification_under_review' (DB) +
  // isVerificationComplete=true (local form) and resolve to the locked stage before anything
  // was submitted. Stage transitions must only happen when the DB changes.
  const supplierState = getSupplierOnboardingState(profile || {}, user);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    // The messages tab is gone: it duplicated /inbox and did it worse, reading
    // only `.eq('receiver_id', me)` and so never seeing a buyer->factory message
    // (those carry receiver_id NULL — factoryChat.js:5-7). Old links, bookmarks
    // and notification targets still arrive here, so they are sent on rather
    // than landing on a tab that no longer exists.
    if (tab === 'messages') { nav('/inbox', { replace: true }); return; }
    if (tab) {
      setActiveTab(tab);
      return;
    }
    // Application-stage and under-review suppliers are never taken off the
    // verification tab by a stale sessionStorage entry. The save effect
    // deliberately skips writing for these stages, so any stored value is
    // from a prior session and should be ignored.
    if (!dashboardUiStateKey) return;
    if (supplierState.isApplicationStage || supplierState.isUnderReviewStage) return;

    const rawUiState = sessionStorage.getItem(dashboardUiStateKey);
    if (!rawUiState) return;

    try {
      const parsed = JSON.parse(rawUiState);
      if (parsed?.activeTab) setActiveTab(parsed.activeTab);
    } catch {
      sessionStorage.removeItem(dashboardUiStateKey);
    }
  }, [location.search, dashboardUiStateKey, supplierState.isApplicationStage, supplierState.isUnderReviewStage]); // eslint-disable-line react-hooks/exhaustive-deps

  // A field stays flagged only while it is still empty, so the red marker
  // clears itself as the supplier types instead of waiting for another save.
  const isSettingsFieldMissing = (key) => settingsMissingKeys.includes(key) && !String(settings?.[key] || '').trim();

  const profileReadiness = getProfileReadiness(settings);
  const isProfileReadyForVerification = profileReadiness.isReadyForVerification;
  const supplierJourneySteps = buildSupplierJourneySteps({ supplierState, profileReadiness, lang });
  const companyDescription = getCompanyDescription(settings);
  const settingsPayload = buildSettingsPayload(settings, companyDescription);
  const savedSettingsPayload = buildSettingsPayload(buildSettingsState(profile || {}, displayCurrency || 'USD'), getCompanyDescription(profile || {}));
  const isSettingsDirty = JSON.stringify(settingsPayload) !== JSON.stringify(savedSettingsPayload);
  const payoutPayload = buildPayoutPayload(payout);
  const savedPayoutPayload = buildPayoutPayload(buildPayoutState(profile || {}));
  const isPayoutDirty = JSON.stringify(payoutPayload) !== JSON.stringify(savedPayoutPayload);
  const hasSavedSettingsRecord = Boolean(settingsSavedAt) || hasPersistedSupplierSettings(profile || {});
  const hasSavedPayoutRecord = Boolean(payoutSavedAt) || hasPersistedSupplierPayout(profile || {});
  const resolvedSettingsSavedAt = settingsSavedAt || profile?.updated_at || profile?.created_at || '';
  const resolvedPayoutSavedAt = payoutSavedAt || profile?.updated_at || profile?.created_at || '';
  const settingsFeedback = buildSettingsSaveFeedback({
    lang,
    status: savingSettings ? 'saving' : (settingsError ? 'error' : (isSettingsDirty ? 'dirty' : (hasSavedSettingsRecord ? 'saved' : 'idle'))),
    savedAt: resolvedSettingsSavedAt,
    errorMessage: settingsError,
  });
  const payoutFeedback = buildPayoutSaveFeedback({
    lang,
    status: savingPayout ? 'saving' : (payoutError ? 'error' : (isPayoutDirty ? 'dirty' : (hasSavedPayoutRecord ? 'saved' : 'idle'))),
    savedAt: resolvedPayoutSavedAt,
    errorMessage: payoutError,
  });
  const needsVerification = !supplierState.isApprovedStage && !supplierState.isUnderReviewStage && !supplierState.isVerificationComplete;
  const needsPayoutSetup = supplierState.isApprovedStage && !supplierState.isPayoutComplete;
  // The Action required / Unverified card. Shown to every supplier who is not yet
  // verified — including one whose complete file is still with the reviewers, who
  // sees the calm "under review" face of the same card. A rejected or deactivated
  // account is NOT nagged to upload: its status is a decision, not a gap.
  const showVerificationCard = !supplierState.isApprovedStage
    && !supplierState.isRejectedStage
    && !supplierState.isInactiveStage;
  const showUploadProductsBanner = supplierState.isVerificationRequiredStatus || supplierState.isVerificationUnderReviewStatus;
  const showOnboardingSequence = supplierState.isApprovedStage && profile?.onboarding_completed !== true;
  const isOnboardingLimited = !supplierState.canAccessOperationalFeatures;

  // First-visit nudge: invite a not-yet-verified supplier to add a product now
  // (Products are unlocked pre-verification). Shows until they skip or save their
  // first product — tracked by profiles.first_product_prompt_dismissed, which is
  // deliberately NOT onboarding_completed (that gates the post-approval sequence).
  const showFirstProductNudge = supplierState.canAccessProducts
    && !supplierState.isVerifiedStatus
    && profile?.first_product_prompt_dismissed !== true;

  const dismissFirstProductNudge = () => {
    setProfile?.(prev => ({ ...(prev || {}), first_product_prompt_dismissed: true }));
    sb.from('profiles').update({ first_product_prompt_dismissed: true }).eq('id', user.id)
      .then(() => {}, (e) => console.error('[nudge] dismiss failed:', e?.message));
  };
  const isVerificationLocked = supplierState.isUnderReviewStage || supplierState.isApprovedStage;
  // The header's RED treatment — the "!" badge, the crimson pill and the
  // "Unverified — verify now" chip — is for an account that has NOT submitted.
  // showVerificationCard is true for an under-review account too, because that
  // account still gets a card (the calm one). Keying the header off the same
  // flag put a red "verify now" directly above a green "nothing needed from
  // you right now" on the same screen, 2026-09-19.
  //
  // Under review keeps the pill — it says "Verification under review", which
  // is worth showing — but in the neutral tone a field deserves rather than
  // the tone a warning deserves.
  const headerNeedsAction = showVerificationCard && !supplierState.isUnderReviewStage;
  const verificationDraftSavedLabel = draftSavedAt ? formatDraftSavedAt(draftSavedAt, lang) : '';
  const verificationStepLabels = [
    isAr ? 'بيانات الشركة' : lang === 'zh' ? '公司资料' : 'Company profile',
    isAr ? 'ملفات التحقق' : lang === 'zh' ? '认证文件' : 'Verification files',
    isAr ? 'المراجعة النهائية' : lang === 'zh' ? '最终确认' : 'Final review',
  ];
  const maxAccessibleVerificationStep = isVerificationLocked ? 3 : verificationProgress.maxReachableStep;
  const tabs = useMemo(() => [
    { id: 'overview',     label: t.overview },
    // A LINK, not a tab. `to` makes the tab bar navigate instead of switching
    // panes: there is no messages pane here any more, and reintroducing one
    // would rebuild the duplicate that was just removed. /inbox is the only
    // list, and the only one that shows factory conversations.
    //
    // Gated on canAccessMessaging, the same gate the route uses (App.js:430) —
    // wider than canAccessOperationalFeatures, so a supplier still awaiting
    // verification keeps their way into a waiting conversation. Absent rather
    // than locked when the gate fails: a locked tab implies it unlocks later,
    // and for a rejected or inactive supplier it does not.
    ...(supplierState?.canAccessMessaging
      ? [{ id: 'messages', label: navLabel('messages', lang), to: '/inbox', badge: stats.messages > 0 ? stats.messages : null }]
      : []),
    // NO verification tab. The eight fields are not a tab — they are a
    // one-time threshold that matters only when money is about to move, and
    // a permanent tab told every new supplier there was something
    // outstanding from day one, which is the exact thing the two-threshold
    // model exists to avoid. The form is still reachable from Payments and
    // from the payout-lock banner, its actual subjects, and from nowhere
    // else. Anyone mid-way through keeps that entry point.
    // It stays in knownTabs below so reaching it does not bounce to overview.
    { id: 'payout',       label: t.payoutTab, badge: needsPayoutSetup ? '!' : null },
    { id: 'requests',     label: isAr ? 'الطلبات' : lang === 'zh' ? '需求' : 'Requests' },
    { id: 'direct-orders', label: isAr ? 'طلبات الشراء المباشر' : lang === 'zh' ? '直接采购订单' : 'Direct Purchase Orders', badge: (directOrders.length + paidDirectOrders.length) > 0 ? (directOrders.length + paidDirectOrders.length) : null },
    { id: 'my-products',  label: t.myProducts },
    { id: 'offers',       label: t.offers },
    { id: 'add-product',  label: t.addProduct },
    { id: 'samples',      label: isAr ? 'العينات' : lang === 'zh' ? '样品' : 'Samples', badge: stats.pendingSamples > 0 ? stats.pendingSamples : null },
    { id: 'product-inquiries', label: isAr ? 'استفسارات المنتجات' : lang === 'zh' ? '产品咨询' : 'Product Inquiries', badge: stats.productInquiries > 0 ? stats.productInquiries : null },
    { id: 'reviews',      label: isAr ? 'تقييماتي' : lang === 'zh' ? '评价' : 'Reviews' },
    ...(SHOW_SUPPLIER_REFERRALS ? [{ id: 'wallet', label: isAr ? 'المحفظة والإحالات' : lang === 'zh' ? '钱包与推荐' : 'Wallet & Referrals' }] : []),
    { id: 'settings',     label: t.settings },
  ], [lang, t, needsVerification, needsPayoutSetup, stats.pendingSamples, stats.productInquiries, stats.messages, isAr, supplierState?.canAccessMessaging, directOrders.length, paidDirectOrders.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const lockedTabIds = isOnboardingLimited
    ? tabs.filter((tab) => !supplierState.limitedTabs.includes(tab.id)).map((tab) => tab.id)
    : [];
  const isRestrictedSupplierTab = lockedTabIds.includes(activeTab);

  const imageRef = useRef(null); const videoRef = useRef(null);
  const editImageRef = useRef(null); const editVideoRef = useRef(null);
  const logoRef = useRef(null); const coverRef = useRef(null); const factoryRef = useRef(null);
  const saveDraftFirstRunRef = useRef(true);
  const draftSaveTimerRef = useRef(null);
  const profileInitRef = useRef(null);
  const verificationTextDebounceRef = useRef(null);
  const verificationTextFirstRunRef = useRef(true);
  const verificationJustSavedRef = useRef(false);

  useEffect(() => {
    if (!user) { nav('/login/supplier'); return; }
    loadStats(); loadPendingTracking(); loadRejectedOffers(); loadDirectOrders(); loadConfirmedDirectOrders(); loadPaidDirectOrders(); loadDirectOrdersHistory();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // 'request-detail' is a valid non-nav sub-view (ONE routed request plus its
    // offer form, reached from the Requests-for-you tab) — keep it out of the
    // unknown-tab redirect below. It replaced 'browse-requests', the open-RFQ
    // pool, which was removed on 2026-09-19 along with the book itself.
    // 'verification' is a valid non-nav sub-view: the eight-field payout file,
    // removed from the tab list on 2026-09-19 but still opened from Payments
    // and the payout-lock banner. Without it here, every one of those buttons
    // would land on a tab id the effect below does not recognise and bounce
    // the supplier to overview.
    const knownTabs = [...tabs.map((tab) => tab.id), 'request-detail', 'verification'];
    if (!knownTabs.includes(activeTab)) {
      // Always overview. This used to send an application-stage supplier to
      // 'verification', which turned every stale or mistyped tab id into an
      // unexplained shove toward their own paperwork — the "reply to the
      // message" link above landed there for exactly that reason.
      //
      // Verification already has its own prompts: the tab carries a '!' badge,
      // and needsVerification drives banners elsewhere. It does not need a
      // catch-all redirect speaking for it, and an unrecognised tab is not
      // evidence that someone wants to verify.
      setActiveTab('overview');
    }
  }, [activeTab, tabs]);

  // Keep bottom nav in sync when activeTab is set programmatically
  useEffect(() => {
    if (['overview'].includes(activeTab)) setActiveBottomTab('home');
    else if (['requests', 'request-detail'].includes(activeTab)) setActiveBottomTab('requests');
    else if (['my-products', 'add-product'].includes(activeTab)) setActiveBottomTab('products');
    else if (activeTab === 'direct-orders') setActiveBottomTab('more');
  }, [activeTab]);

  useEffect(() => {
    if (!dashboardUiStateKey) return;
    if (!supplierState.isApplicationStage && !supplierState.isUnderReviewStage) {
      sessionStorage.setItem(dashboardUiStateKey, JSON.stringify({ activeTab }));
    }
  }, [dashboardUiStateKey, activeTab, supplierState.isApplicationStage, supplierState.isUnderReviewStage]);

  useEffect(() => {
    const requestedTab = new URLSearchParams(location.search).get('tab');
    if (!supplierState.isApprovedStage || activeTab !== 'verification') return;

    if (requestedTab === 'verification') {
      nav('/dashboard', { replace: true });
      return;
    }

    setActiveTab('overview');
  }, [supplierState.isApprovedStage, activeTab, location.search, nav]);

  // A claimed factory reaches traders without verification (2026-10-12), and
  // that is not visible in profile.status — so ask the server. Re-asked when
  // the status changes, since approval also flips the answer.
  useEffect(() => {
    if (!user?.id) return undefined;
    let alive = true;
    fetchMyQuotesReachTraders(sb).then((v) => { if (alive) setQuotesReachTraders(v); });
    return () => { alive = false; };
  }, [user?.id, profile?.status]);

  // Claimed-factory owner: load the linked factory + counts once, for the banners.
  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    (async () => {
      try {
        const fac = await fetchMyFactory();
        if (!alive || !fac) return;
        setOwnerFactory(fac);
        const [cnt, threads, inv] = await Promise.all([
          sb.from('factory_products').select('id', { count: 'exact', head: true }).eq('factory_id', fac.id),
          fetchMyOwnerThreads(user.id).catch(() => []),
          sb.rpc('get_my_factory_invites').then((r) => r.data || [], () => []),
        ]);
        if (!alive) return;
        setOwnerFacProducts(cnt?.count || 0);
        setOwnerFacMsgs((threads || []).length);
        setOwnerFacInvites((inv || []).length);
      } catch { /* not a factory owner */ }
    })();
    return () => { alive = false; };
  }, [user]);

  // Lazy-load the owner's factory catalog when the My Products tab opens.
  useEffect(() => {
    if (activeTab !== 'my-products' || !ownerFactory || ownerCatalog !== null) return undefined;
    let alive = true;
    fetchFactoryProducts(ownerFactory.id).then(
      (rows) => { if (alive) setOwnerCatalog(rows || []); },
      () => { if (alive) setOwnerCatalog([]); },
    );
    return () => { alive = false; };
  }, [activeTab, ownerFactory, ownerCatalog]);

  // Banner → the factory owner dashboard (/dashboard/factory). `tab='messages'`
  // opens its Conversations tab directly (used by the messages + RFQ banners).
  const factoryBanner = (label, count, tab) => (
    <div onClick={() => nav(`/dashboard/factory${tab ? `?tab=${tab}` : ''}`)} style={{ cursor: 'pointer', background: 'linear-gradient(135deg,#FCF8F0,#F1F7F1)', border: '1px solid rgba(139,115,85,.35)', borderRadius: 14, padding: '13px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13.5, fontWeight: 700, color: '#5a4e3a', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{label}{count != null ? ` (${count})` : ''}</span>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: '#8B7355', whiteSpace: 'nowrap' }}>{isAr ? 'فتح ←' : lang === 'zh' ? '打开 →' : 'Open →'}</span>
    </div>
  );
  const bannerLabels = {
    catalog: isAr ? 'منتجات كتالوجك — تعديل الأسعار' : lang === 'zh' ? '您的目录产品 — 更新价格' : 'Your catalog products — edit prices',
    msgs: isAr ? 'رسائل التجّار على مصنعك' : lang === 'zh' ? '工厂收到的买家消息' : 'Buyer messages to your factory',
    invites: isAr ? 'دعوات عرض سعر على مصنعك' : lang === 'zh' ? '工厂收到的报价请求' : 'Quote requests to your factory',
  };

  useEffect(() => {
    if (!user || isRestrictedSupplierTab) return;
    if (activeTab === 'offers')       loadMyOffers();

    if (activeTab === 'my-products')  loadMyProducts();
    if (activeTab === 'samples')      loadSamples();
    if (activeTab === 'product-inquiries') loadProductInquiries();
    if (activeTab === 'reviews')      loadMyReviews();
    if (activeTab === 'wallet')       loadReferrals();
    if (activeTab === 'add-product')  {
      setEditingProduct(null);
      setProductComposerStep('edit');
      setEditProductComposerStep('edit');
      const draft = sessionStorage.getItem('maabar_product_draft');
      if (draft) {
        try { setProduct(normalizeProductDraftMedia(JSON.parse(draft))); } catch { setProduct(emptyProduct); }
      } else {
        setProduct(emptyProduct);
      }
    }
  }, [activeTab, isRestrictedSupplierTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!profile) return;
    const hasDraft = verificationDraftKey ? Boolean(sessionStorage.getItem(verificationDraftKey)) : false;
    const next = displayCurrency || 'USD';
    if (profileInitRef.current === profile.id) {
      setSettings(prev => prev.preferred_display_currency === next ? prev : { ...prev, preferred_display_currency: next });
      return;
    }
    profileInitRef.current = profile.id;
    setSettings(buildSettingsState(profile, next));
    setVerification(buildVerificationState(profile));
    setPayout(buildPayoutState(profile));
  }, [profile, displayCurrency, verificationDraftKey]);

  useEffect(() => {
    if (!verificationDraftKey) return;

    const rawDraft = sessionStorage.getItem(verificationDraftKey);
    if (!rawDraft) {
      setVerificationStep(1);
      setDraftSavedAt('');
      return;
    }

    try {
      const parsed = JSON.parse(rawDraft);
      if (parsed?.settings) {
        setSettings((prev) => ({ ...prev, ...parsed.settings }));
      }
      if (parsed?.verification) {
        setVerification((prev) => ({
          ...prev,
          ...parsed.verification,
          factory_images: normalizeVerificationMedia(parsed.verification.factory_images ?? prev.factory_images).slice(0, VERIFICATION_IMAGE_LIMIT),
          factory_videos: normalizeVerificationMedia(parsed.verification.factory_videos ?? prev.factory_videos).slice(0, VERIFICATION_VIDEO_LIMIT),
        }));
      }
      setVerificationStep(Math.min(3, Math.max(1, Number(parsed?.step) || 1)));
      setDraftSavedAt(parsed?.savedAt || '');
    } catch {
      sessionStorage.removeItem(verificationDraftKey);
    }
  }, [verificationDraftKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (saveDraftFirstRunRef.current) {
      saveDraftFirstRunRef.current = false;
      return;
    }
    if (!verificationDraftKey || isVerificationLocked) return;
    clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(() => {
      const savedAt = new Date().toISOString();
      sessionStorage.setItem(verificationDraftKey, JSON.stringify({
        settings,
        verification: stripEmptyFields({
          ...verification,
          factory_images: normalizeVerificationMedia(verification.factory_images).slice(0, VERIFICATION_IMAGE_LIMIT),
          factory_videos: normalizeVerificationMedia(verification.factory_videos).slice(0, VERIFICATION_VIDEO_LIMIT),
        }),
        step: verificationStep,
        savedAt,
      }));
      setDraftSavedAt(savedAt);
    }, 0);
    return () => clearTimeout(draftSaveTimerRef.current);
  }, [verificationDraftKey, settings, verification, verificationStep, isVerificationLocked]);

  useEffect(() => {
    if (verificationTextFirstRunRef.current) {
      verificationTextFirstRunRef.current = false;
      return;
    }
    if (verificationJustSavedRef.current) {
      verificationJustSavedRef.current = false;
      return;
    }
    if (!user || isVerificationLocked) return;
    clearTimeout(verificationTextDebounceRef.current);
    verificationTextDebounceRef.current = setTimeout(async () => {
      const reg_number = normalizeTextInput(verification.reg_number);
      const years_experience = normalizeOptionalInteger(verification.years_experience);
      const num_employees = normalizeOptionalInteger(verification.num_employees);
      const debouncePayload = stripEmptyFields({ reg_number, years_experience, num_employees });
      if (Object.keys(debouncePayload).length === 0) return;
      const { error: debounceError } = await sb.from('profiles').update(debouncePayload).eq('id', user.id);
      if (debounceError) { console.error('[verificationDebounce] update error:', JSON.stringify(debounceError)); return; }
      setProfile?.(prev => ({ ...(prev || {}), ...debouncePayload }));
    }, 800);
    return () => clearTimeout(verificationTextDebounceRef.current);
  }, [verification.reg_number, verification.years_experience, verification.num_employees]); // eslint-disable-line react-hooks/exhaustive-deps



  // Save product form draft to sessionStorage on every change
  useEffect(() => {
    if (activeTab === 'add-product' && !editingProduct) {
      sessionStorage.setItem('maabar_product_draft', JSON.stringify(normalizeProductDraftMedia(product)));
    }
  }, [product, activeTab, editingProduct]);

  useEffect(() => { if (activeTab === 'request-detail') loadRequests(); }, [activeTab, detailRequestId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (activeTab === 'direct-orders') { loadDirectOrders(); loadConfirmedDirectOrders(); loadPaidDirectOrders(); loadDirectOrdersHistory(); } }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== 'direct-orders') return;
    const id = setInterval(() => setDirectOrdersNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [activeTab]);

  const loadStats = async () => {
    const [products, offersData, messages, acceptedOffers, payments, openProductInquiries, samplesResult] = await Promise.all([
      sb.from('products').select('id', { count: 'exact' }).eq('supplier_id', user.id).eq('is_active', true),
      sb.from('offers').select('id', { count: 'exact' }).eq('supplier_id', user.id),
      sb.from('messages').select('id', { count: 'exact' }).eq('receiver_id', user.id).eq('is_read', false),
      sb.from('offers').select('id', { count: 'exact' }).eq('supplier_id', user.id).eq('status', 'accepted'),
      sb.from('payments').select('amount').eq('supplier_id', user.id).eq('status', 'first_paid'),
      sb.from('product_inquiries').select('id', { count: 'exact' }).eq('supplier_id', user.id).eq('status', 'open'),
      sb.from('samples').select('id', { count: 'exact' }).eq('supplier_id', user.id).eq('status', 'pending'),
    ]);
    const totalSales = (payments.data || []).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const pendingSamples = samplesResult.count;
    setStats({
      products: products.count || 0,
      offers: offersData.count || 0,
      messages: messages.count || 0,
      productInquiries: openProductInquiries.count || 0,
      acceptedOffers: acceptedOffers.count || 0,
      totalSales: Math.round(totalSales),
      pendingSamples: pendingSamples || 0,
    });
  };

  const loadSamples = async () => {
    const key = `sup-samples:${user.id}`;
    const cached = readSwrCache(key);
    if (cached !== undefined) setSamples(cached);
    const { data } = await sb.from('samples').select('*,products(name_ar,name_en,name_zh)').eq('supplier_id', user.id).order('created_at', { ascending: false });
    if (data) {
      const result = await attachDirectoryProfiles(sb, data, 'buyer_id', 'profiles');
      writeSwrCache(key, result);
      setSamples(result);
    }
  };

  const loadProductInquiries = async () => {
    const key = `sup-inquiries:${user.id}`;
    const cached = readSwrCache(key);
    if (cached !== undefined) setProductInquiries(cached);
    try {
      const data = await fetchProductInquiryThreads(sb, { supplierId: user.id });
      const inquiriesWithProfiles = await attachDirectoryProfiles(sb, data, 'buyer_id', 'profiles');
      writeSwrCache(key, inquiriesWithProfiles);
      setProductInquiries(inquiriesWithProfiles);
      
      // نترجم الاستفسارات إذا لغة المورد مختلفة عن لغة النص
      if (inquiriesWithProfiles.length > 0) {
        const translations = {};
        for (const inquiry of inquiriesWithProfiles) {
          if (inquiry.question_text) {
            // نحاول نكتشف لغة النص (بسيط - نتحقق من وجود حروف عربية/صينية)
            const hasArabic = /[\u0600-\u06FF]/.test(inquiry.question_text);
            const hasChinese = /[\u4e00-\u9fff]/.test(inquiry.question_text);
            
            let sourceLang = 'en'; // default
            if (hasArabic) sourceLang = 'ar';
            else if (hasChinese) sourceLang = 'zh';
            
            // نترجم فقط إذا لغة المصدر مختلفة عن لغة المورد
            if (sourceLang !== lang) {
              const translated = await translateInquiryText(
                inquiry.question_text, 
                sourceLang,
                lang
              );
              translations[inquiry.id] = translated;
            }
          }
        }
        setTranslatedInquiries(translations);
      }
    } catch (error) {
      console.error('load product inquiries error:', error);
      setProductInquiries([]);
    }
  };

  const submitInquiryReply = async (inquiry) => {
    const message = String(inquiryReplies[inquiry.id] || '').trim();
    if (!message) {
      alert(isAr ? 'اكتب رد المورد أولاً' : lang === 'zh' ? '请先填写回复内容' : 'Please write the supplier reply first');
      return;
    }

    setReplyingInquiryId(inquiry.id);
    const { error } = await sb.from('product_inquiry_replies').insert({
      inquiry_id: inquiry.id,
      sender_id: user.id,
      receiver_id: inquiry.buyer_id,
      message,
    });

    if (error) {
      console.error('submit inquiry reply error:', error);
      setReplyingInquiryId(null);
      alert(isAr ? 'تعذر إرسال الرد الآن' : lang === 'zh' ? '暂时无法发送回复' : 'Unable to send the reply right now');
      return;
    }

    await sb.from('notifications').insert({
      user_id: inquiry.buyer_id,
      type: 'product_inquiry_reply',
      title_ar: `رد المورد على استفسارك عن ${inquiry.products?.name_ar || inquiry.products?.name_en || inquiry.products?.name_zh || 'المنتج'}`,
      title_en: `Supplier replied about ${inquiry.products?.name_en || inquiry.products?.name_ar || inquiry.products?.name_zh || 'the product'}`,
      title_zh: `供应商已回复您关于 ${inquiry.products?.name_zh || inquiry.products?.name_en || inquiry.products?.name_ar || '该产品'} 的咨询`,
      ref_id: inquiry.id,
      is_read: false,
    });

    try {
      await fetch(SEND_EMAILS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          type: 'product_inquiry_reply',
          data: {
            recipientUserId: inquiry.buyer_id,
            inquiryId: inquiry.id,
            productName: inquiry.products?.name_ar || inquiry.products?.name_en || inquiry.products?.name_zh || 'Product',
            supplierName: settings.company_name || profile?.company_name || user?.email?.split('@')[0] || 'Supplier',
            question: inquiry.question_text,
            replyMessage: message,
          },
        }),
      });
    } catch (emailError) {
      console.error('product inquiry reply email error:', emailError);
    }

    setInquiryReplies((current) => ({ ...current, [inquiry.id]: '' }));
    setReplyingInquiryId(null);
    await Promise.all([loadProductInquiries(), loadStats()]);
  };

  // Reviews are ANONYMOUS (owner, 2026-09-28): never a trader's name, not even
  // after acceptance. Read through supplier_reviews_public (no reviewer_id, no
  // request_id) and no profile is attached. This UNDOES the reviewer-name part
  // of b3e79aa, which made attachDirectoryProfiles' 4th argument work and so,
  // here, started showing reviewers' names.
  const loadMyReviews = async () => {
    setMyReviews(await fetchSupplierReviews(sb, user.id));
  };

  const loadReferrals = async () => {
    const [{ data: refs }, { data: rewards }] = await Promise.all([
      sb.from('referrals')
        .select('*, referred:profiles!referrals_referred_id_fkey(company_name, status)')
        .eq('referrer_id', user.id)
        .order('created_at', { ascending: false }),
      sb.from('referral_rewards')
        .select('*')
        .eq('referrer_id', user.id)
        .order('earned_at', { ascending: false }),
    ]);
    setReferrals(refs || []);
    setReferralRewards(rewards || []);
  };

  const updateSampleStatus = async (sampleId, status) => {
    const sample = samples.find(s => s.id === sampleId);
    const { error } = await sb.from('samples').update({ status }).eq('id', sampleId);
    if (error) {
      alert(isAr ? 'حدث خطأ أثناء تحديث حالة العينة' : lang === 'zh' ? '更新样品状态时出错' : 'Error updating sample status');
      return;
    }

    if (sample?.buyer_id) {
      await sb.from('notifications').insert({
        user_id: sample.buyer_id,
        type: status === 'approved' ? 'sample_approved' : 'sample_rejected',
        title_ar: status === 'approved' ? 'تمت الموافقة على طلب العينة' : 'تم رفض طلب العينة',
        title_en: status === 'approved' ? 'Your sample request was approved' : 'Your sample request was rejected',
        title_zh: status === 'approved' ? '您的样品申请已获批准' : '您的样品申请被拒绝',
        ref_id: sampleId,
        is_read: false,
      });

      try {
        await fetch(SEND_EMAILS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
          body: JSON.stringify({
            type: status === 'approved' ? 'sample_approved' : 'sample_rejected',
            data: {
              recipientUserId: sample.buyer_id,
              name: 'Trader',
              productName: sample?.products?.name_ar || sample?.products?.name_en || sample?.products?.name_zh || 'Product',
              totalPrice: sample?.total_price || 0,
            },
          }),
        });
      } catch (e) { console.error('sample status email error:', e); }
    }

    loadSamples(); loadStats();
  };

  const loadPendingTracking = async () => {
    const { data } = await sb.from('offers').select('*,requests(title_ar,title_en,title_zh,buyer_id,status,tracking_number)').eq('supplier_id', user.id).eq('status', 'accepted');
    if (data) setPendingTracking(data.filter(o => o.requests?.status !== 'shipping' && o.requests?.status !== 'delivered'));
  };

  const loadRejectedOffers = async () => {
    const { data } = await sb.from('offers').select('*,requests(title_ar,title_en,title_zh)').eq('supplier_id', user.id).eq('status', 'rejected').eq('seen', false);
    if (data) setRejectedOffers(data);
  };

  const loadSettings = async () => {
    const { data } = await sb.from('profiles').select('*').eq('id', user.id).single();
    if (data) {
      setSettings({
        company_name: data.company_name || '',
        whatsapp: data.whatsapp || '',
        wechat: data.wechat || '',
        city: data.city || '',
        country: data.country || '',
        trade_link: data.trade_link || '',
        speciality: data.speciality || '',
        min_order_value: data.min_order_value || '',
        business_type: data.business_type || '',
        year_established: data.year_established || '',
        languages: serializeProfileList(data.languages),
        customization_support: data.customization_support || '',
        export_markets: serializeProfileList(data.export_markets),
        company_address: data.company_address || '',
        company_website: data.company_website || '',
        company_description: getCompanyDescription(data),
        preferred_display_currency: data.preferred_display_currency || displayCurrency || 'USD',
        avatar_url: data.avatar_url || '',
        factory_images: data.factory_images || [],
      });
    }
  };

  const loadVerification = async () => {
    const { data } = await sb.from('profiles').select('reg_number,years_experience,num_employees,license_photo,legal_rep_id_photo,address_proof_photo,factory_photo').eq('id', user.id).single();
    if (data) {
      setVerification({
        reg_number: data.reg_number || '',
        years_experience: data.years_experience || '',
        num_employees: data.num_employees || '',
        license_photo: data.license_photo || '',
        legal_rep_id_photo: data.legal_rep_id_photo || '',
        address_proof_photo: data.address_proof_photo || '',
        factory_photo: data.factory_photo || '',
      });
    }
  };

  const loadPayout = async () => {
    const { data } = await sb.from('profiles').select('*').eq('id', user.id).single();
    if (data) {
      setPayout(buildPayoutState(data));
    }
  };

  const clearVerificationDraft = () => {
    if (verificationDraftKey) sessionStorage.removeItem(verificationDraftKey);
    setDraftSavedAt('');
  };

  const openVerificationDoc = async (rawValue) => {
    const objectPath = normalizeSupplierDocStoragePath(rawValue);
    if (!objectPath) return;
    const { data, error } = await sb.storage.from('supplier-docs').createSignedUrl(objectPath, 60 * 10);
    if (error || !data?.signedUrl) {
      alert(isAr ? 'تعذر فتح الملف الآن' : lang === 'zh' ? '暂时无法打开文件' : 'Could not open the file right now');
      return;
    }
    const signedUrl = data.signedUrl.startsWith('http') ? data.signedUrl : `${SUPABASE_URL}${data.signedUrl}`;
    window.open(signedUrl, '_blank', 'noopener,noreferrer');
  };

  // Single-file verification document types — each maps to one column on
  // profiles. The legacy 'license' / new 'legal_rep_id' / new 'address_proof'
  // share the same upload-then-update-column pattern; everything else
  // (factory images, factory videos) falls through to the multi-file path.
  const SINGLE_DOC_COLUMN_BY_TYPE = {
    license: 'license_photo',
    legal_rep_id: 'legal_rep_id_photo',
    address_proof: 'address_proof_photo',
  };

  const uploadVerificationDoc = async (file, type) => {
    if (!file) return;
    const isSingleDoc = Object.prototype.hasOwnProperty.call(SINGLE_DOC_COLUMN_BY_TYPE, type);
    const key = isSingleDoc ? type : 'images';
    setUploadingVerificationDoc(prev => ({ ...prev, [key]: true }));
    const extension = file.name.split('.').pop() || (file.type === 'application/pdf' ? 'pdf' : 'jpg');
    const path = `${user.id}/${type}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${extension}`;
    const { error } = await sb.storage.from('supplier-docs').upload(path, file, { upsert: true });
    setUploadingVerificationDoc(prev => ({ ...prev, [key]: false }));
    if (error) {
      alert(isAr ? 'فشل رفع الملف' : lang === 'zh' ? '文件上传失败' : 'Failed to upload file');
      return;
    }

    if (isSingleDoc) {
      const column = SINGLE_DOC_COLUMN_BY_TYPE[type];
      await sb.from('profiles').update({ [column]: path }).eq('id', user.id);
      setVerification(prev => ({ ...prev, [column]: path }));
      setProfile?.(prev => ({ ...(prev || {}), [column]: path }));
      return;
    }

    const nextFactoryImages = [...normalizeVerificationMedia(verification.factory_images), path].slice(0, VERIFICATION_IMAGE_LIMIT);
    const nextFactoryPhoto = verification.factory_photo || path;
    await sb.from('profiles').update({
      factory_images: nextFactoryImages,
      factory_photo: nextFactoryPhoto,
    }).eq('id', user.id);
    setVerification(prev => ({
      ...prev,
      factory_images: [...normalizeVerificationMedia(prev.factory_images), path].slice(0, VERIFICATION_IMAGE_LIMIT),
      factory_photo: prev.factory_photo || path,
    }));
    setProfile?.(prev => ({ ...(prev || {}), factory_images: nextFactoryImages, factory_photo: nextFactoryPhoto }));
  };

  const uploadVerificationMedia = async (fileList, mediaType) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const isVideo = mediaType === 'video';
    const currentItems = isVideo ? verificationVideos : verificationImages;
    const limit = isVideo ? VERIFICATION_VIDEO_LIMIT : VERIFICATION_IMAGE_LIMIT;
    const remainingSlots = Math.max(0, limit - currentItems.length);

    if (remainingSlots <= 0) {
      alert(isAr
        ? (isVideo ? 'الحد الأقصى مقطعان فيديو للتحقق.' : 'الحد الأقصى 5 صور للتحقق.')
        : lang === 'zh'
          ? (isVideo ? '认证视频最多上传 2 个。' : '认证图片最多上传 5 张。')
          : (isVideo ? 'You can upload up to 2 verification videos.' : 'You can upload up to 5 verification images.'));
      return;
    }

    const acceptedFiles = files.slice(0, remainingSlots);
    const stateKey = isVideo ? 'videos' : 'images';
    setUploadingVerificationDoc(prev => ({ ...prev, [stateKey]: true }));
    setVerificationUploadPct(prev => ({ ...prev, [stateKey]: 0 }));

    const uploadedPaths = [];
    const totalFiles = acceptedFiles.length;
    for (let idx = 0; idx < acceptedFiles.length; idx++) {
      const file = acceptedFiles[idx];
      if (isVideo && file.size > VERIFICATION_VIDEO_MAX_BYTES) {
        alert(t.maxVideo);
        continue;
      }

      const extension = file.name.split('.').pop() || (isVideo ? 'mp4' : 'jpg');
      const path = `${user.id}/verification_${isVideo ? 'video' : 'image'}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${extension}`;
      // Byte-level progress via XHR (fetch, which supabase-js .upload() uses,
      // reports none). For multi-file image batches the bar spans the whole set:
      // files already done + the fraction of the one in flight.
      const { error } = await uploadWithProgress('supplier-docs', path, file, {
        upsert: true,
        onProgress: (pct) => setVerificationUploadPct(prev => ({
          ...prev,
          [stateKey]: Math.round(((idx + pct / 100) / totalFiles) * 100),
        })),
      });
      if (!error) uploadedPaths.push(path);
    }

    setUploadingVerificationDoc(prev => ({ ...prev, [stateKey]: false }));
    setVerificationUploadPct(prev => ({ ...prev, [stateKey]: null }));

    if (!uploadedPaths.length) {
      alert(isAr ? 'فشل رفع الملفات المحددة' : lang === 'zh' ? '所选文件上传失败' : 'Failed to upload the selected files');
      return;
    }

    const currentImages = normalizeVerificationMedia(verification.factory_images);
    const currentVideos = normalizeVerificationMedia(verification.factory_videos);
    const nextImages = isVideo ? currentImages : [...currentImages, ...uploadedPaths].slice(0, VERIFICATION_IMAGE_LIMIT);
    const nextVideos = isVideo ? [...currentVideos, ...uploadedPaths].slice(0, VERIFICATION_VIDEO_LIMIT) : currentVideos;
    const nextFactoryPhoto = nextImages[0] || verification.factory_photo || '';
    setVerification(prev => ({
      ...prev,
      factory_images: nextImages,
      factory_videos: nextVideos,
      factory_photo: nextFactoryPhoto,
    }));
    await runWithOptionalColumns({
      table: 'profiles',
      payload: { factory_images: nextImages, factory_photo: nextFactoryPhoto, factory_videos: nextVideos },
      optionalKeys: ['factory_videos'],
      execute: (p) => sb.from('profiles').update(p).eq('id', user.id),
    });
    setProfile?.(prev => ({ ...(prev || {}), factory_images: nextImages, factory_photo: nextFactoryPhoto, factory_videos: nextVideos }));
  };

  const removeVerificationMedia = async (mediaType, pathToRemove) => {
    setVerification(prev => {
      const nextImages = mediaType === 'image'
        ? normalizeVerificationMedia(prev.factory_images).filter((item) => item !== pathToRemove)
        : normalizeVerificationMedia(prev.factory_images);
      const nextVideos = mediaType === 'video'
        ? normalizeVerificationMedia(prev.factory_videos).filter((item) => item !== pathToRemove)
        : normalizeVerificationMedia(prev.factory_videos);

      const nextState = {
        ...prev,
        factory_images: nextImages,
        factory_videos: nextVideos,
        factory_photo: nextImages[0] || '',
      };

      sb.from('profiles').update({
        factory_images: nextState.factory_images,
        factory_videos: nextState.factory_videos,
        factory_photo: nextState.factory_photo,
      }).eq('id', user.id).then(({ error }) => {
        if (error) console.error('[removeVerificationMedia] DB error:', JSON.stringify(error));
      });

      return nextState;
    });
  };

  const saveVerification = async () => {
    if (isVerificationLocked) {
      return;
    }

    if (verificationProgress.missingProfileFields.length > 0) {
      setVerificationSaved(false);
      setVerificationMsg(t.verificationProfileRequired);
      return;
    }

    if (!verificationProgress.hasVerificationBasics) {
      setVerificationSaved(false);
      setVerificationMsg(t.verificationMissing);
      return;
    }

    if (!verificationVideos.length) {
      setVerificationSaved(false);
      setVerificationMsg(t.verificationVideoRequired);
      return;
    }

    setSavingVerification(true);
    setVerificationSaved(false);
    setVerificationMsg('');

    const payload = {
      ...buildSettingsPayload(settings, companyDescription),
      reg_number: normalizeTextInput(verification.reg_number),
      years_experience: normalizeOptionalInteger(verification.years_experience),
      num_employees: normalizeOptionalInteger(verification.num_employees),
      license_photo: normalizeTextInput(verification.license_photo),
      legal_rep_id_photo: normalizeTextInput(verification.legal_rep_id_photo),
      address_proof_photo: normalizeTextInput(verification.address_proof_photo),
      factory_photo: verificationImages[0] || '',
      factory_images: verificationImages,
      factory_videos: verificationVideos,
    };

    const { error, payload: persistedPayload } = await runWithOptionalColumns({
      table: 'profiles',
      payload: stripEmptyFields(payload),
      optionalKeys: ['business_type', 'year_established', 'languages', 'customization_support', 'export_markets', 'company_address', 'company_website', 'company_description', 'preferred_display_currency', 'factory_videos', 'certifications', 'legal_rep_id_photo', 'address_proof_photo'],
      execute: (nextPayload) => sb.from('profiles').update(nextPayload).eq('id', user.id),
    });

    if (error) {
      setSavingVerification(false);
      console.error('DB error:', error);
      setVerificationMsg(isAr ? 'تعذر حفظ بيانات التحقق. حاول مرة أخرى.' : lang === 'zh' ? '认证资料保存失败，请重试。' : 'Failed to save verification details. Please try again.');
      return;
    }

    let submittedStatus = profile?.status;

    if (!supplierState.isApprovedStage) {
      const { data: submitResult, error: submitError } = await sb.rpc('submit_supplier_verification');
      if (submitError) {
        setSavingVerification(false);
        setVerificationMsg(isAr ? 'تعذر إرسال التحقق للمراجعة. حاول مرة أخرى.' : lang === 'zh' ? '提交认证审核失败，请重试。' : 'Failed to submit verification for review. Please try again.');
        return;
      }

      submittedStatus = Array.isArray(submitResult)
        ? submitResult[0]?.status
        : submitResult?.status;
    }

    setSavingVerification(false);

    await setDisplayCurrency?.(settings.preferred_display_currency || 'USD');
    clearVerificationDraft();
    const mergedProfile = {
      ...profile,
      ...persistedPayload,
      ...(submittedStatus ? { status: submittedStatus } : {}),
    };
    verificationJustSavedRef.current = true;
    setProfile?.(mergedProfile);
    setSettings(buildSettingsState(mergedProfile, settings.preferred_display_currency || 'USD'));
    setVerification(buildVerificationState(mergedProfile));

    try {
      await sb.from('notifications').insert({
        user_id: user.id,
        type: 'verification_submitted',
        title_ar: 'تم إرسال التحقق وبات الحساب الآن تحت المراجعة',
        title_en: 'Your verification was submitted and is now under review',
        title_zh: '您的认证已提交，账户现已进入审核中',
        ref_id: user.id,
        is_read: false,
      });
    } catch (notificationError) {
      console.error('verification submitted notification error:', notificationError);
    }

    try {
      await sendMaabarEmail({
        type: 'supplier_application_received',
        to: mergedProfile.email || user.email,
        data: {
          companyName: mergedProfile.company_name || mergedProfile.full_name || '',
          lang,
          recipientUserId: user.id,
        },
      });
    } catch (emailError) {
      console.error('supplier_application_received email error:', emailError);
    }

    try {
      await sendMaabarEmail({
        type: 'admin_supplier_verification',
        data: {
          companyName: mergedProfile.company_name || mergedProfile.full_name || '',
          supplierId: user.id,
          maabarSupplierId: mergedProfile.maabar_supplier_id || '',
        },
      });
    } catch (emailError) {
      console.error('admin_supplier_verification email error:', emailError);
    }

    try {
      await sendMaabarEmail({
        type: 'admin_new_supplier',
        data: {
          companyName: mergedProfile.company_name || mergedProfile.full_name || '',
          email: mergedProfile.email || user.email || '',
          country: mergedProfile.country || '',
          city: mergedProfile.city || '',
          speciality: mergedProfile.speciality || '',
          whatsapp: mergedProfile.whatsapp || '',
          wechat: mergedProfile.wechat || '',
          tradeLink: mergedProfile.trade_link || '',
          tradeLinks: getSupplierTradeLinks(mergedProfile),
        },
      });
    } catch (emailError) {
      console.error('admin_new_supplier email error:', emailError);
    }

    setVerificationSaved(true);
    setVerificationMsg(t.verificationSubmitted);
    setVerificationStep(3);
    if (dashboardUiStateKey) sessionStorage.removeItem(dashboardUiStateKey);
  };

  const savePayout = async () => {
    const requiredPayoutFields = [
      payout.payout_beneficiary_name,
      payout.bank_name,
      payout.payout_account_number,
      payout.swift_code,
      payout.preferred_display_currency,
    ].map(value => String(value || '').trim());

    if (requiredPayoutFields.some(value => !value)) {
      setPayoutError(isAr ? 'أكمل جميع بيانات الدفعات المطلوبة قبل الحفظ.' : lang === 'zh' ? '请先填写所有必填收款信息，再保存。' : 'Please complete all required payout details before saving.');
      return;
    }

    setPayoutError('');
    setSavingPayout(true);
    const payload = buildPayoutPayload(payout);
    const { error, payload: persistedPayload } = await runWithOptionalColumns({
      table: 'profiles',
      payload,
      optionalKeys: ['bank_name', 'swift_code', 'pay_method', 'alipay_account', 'preferred_display_currency', 'payout_beneficiary_name', 'payout_account_number', 'payout_bank_address', 'payout_branch_name'],
      execute: (nextPayload) => sb.from('profiles').update(nextPayload).eq('id', user.id),
    });
    setSavingPayout(false);
    if (error) {
      setPayoutError(isAr ? 'تعذر حفظ بيانات الدفعات. حاول مرة أخرى.' : lang === 'zh' ? '收款资料保存失败，请重试。' : 'Failed to save payout details. Please try again.');
      return;
    }

    const savedAt = new Date().toISOString();
    const mergedProfile = { ...profile, ...persistedPayload };
    setProfile?.(mergedProfile);
    setSettings(prev => ({ ...prev, preferred_display_currency: persistedPayload.preferred_display_currency || prev.preferred_display_currency || 'USD' }));
    setPayout(buildPayoutState(mergedProfile));
    await setDisplayCurrency?.(persistedPayload.preferred_display_currency || payload.preferred_display_currency || 'USD');
    setPayoutSavedAt(savedAt);
  };

  const saveSettings = async ({ nextVerificationStep = null, navigateToVerification = false } = {}) => {
    const missingApplicationFields = [
      ['company_name', isAr ? 'اسم الشركة' : lang === 'zh' ? '公司名称' : 'Company name'],
      ['city', t.city],
      ['country', t.country],
      ['trade_link', t.tradeLink],
    ].filter(([key]) => !String(settings?.[key] || '').trim());

    if (missingApplicationFields.length > 0) {
      // Name the fields that are actually empty — a generic "complete the
      // basics" line sent suppliers hunting through a long form.
      const missingLabels = missingApplicationFields.map(([, label]) => label).join(isAr ? '، ' : lang === 'zh' ? '、' : ', ');
      setSettingsMissingKeys(missingApplicationFields.map(([key]) => key));
      setSettingsError(isAr
        ? `أكمل الحقول المطلوبة أولاً: ${missingLabels}.`
        : lang === 'zh'
          ? `请先完成必填项：${missingLabels}。`
          : `Please complete the required fields first: ${missingLabels}.`);
      return false;
    }

    setSettingsMissingKeys([]);
    setSettingsError('');
    setSavingSettings(true);
    const payload = buildSettingsPayload(settings, companyDescription);

    const { data: savedRows, error } = await runWithOptionalColumns({
      table: 'profiles',
      payload,
      optionalKeys: ['business_type', 'year_established', 'languages', 'customization_support', 'export_markets', 'company_address', 'company_website', 'company_description', 'bio_en', 'min_order_value', 'preferred_display_currency', 'certifications'],
      execute: (nextPayload) => sb.from('profiles').update(nextPayload).eq('id', user.id).select('id'),
    });

    setSavingSettings(false);

    if (error) {
      console.error('[saveSettings] error:', JSON.stringify(error));
      setSettingsError(isAr ? 'تعذر حفظ ملف الشركة. حاول مرة أخرى.' : lang === 'zh' ? '公司资料保存失败，请重试。' : 'Failed to save company profile. Please try again.');
      return false;
    }

    if (!savedRows || savedRows.length === 0) {
      console.error('[saveSettings] 0 rows updated — RLS may be blocking the update');
      setSettingsError(isAr ? 'تعذر حفظ ملف الشركة. حاول مرة أخرى.' : lang === 'zh' ? '公司资料保存失败，请重试。' : 'Failed to save company profile. Please try again.');
      return false;
    }

    const savedAt = new Date().toISOString();
    await setDisplayCurrency?.(payload.preferred_display_currency || 'USD');

    const mergedProfile = {
      ...profile,
      ...payload,
    };
    setProfile?.(mergedProfile);
    setSettings(buildSettingsState(mergedProfile, payload.preferred_display_currency || 'USD'));
    setSettingsSavedAt(savedAt);

    const shouldAdvanceIntoVerification = !supplierState.isUnderReviewStage
      && !supplierState.isApprovedStage
      && (navigateToVerification || typeof nextVerificationStep === 'number');

    if (shouldAdvanceIntoVerification) {
      setVerificationSaved(true);
      setVerificationMsg(isAr ? 'تم حفظ ملف الشركة. الآن أرفق مستندات التحقق ثم أكمل الإرسال النهائي.' : lang === 'zh' ? '公司资料已保存。现在请上传认证文件，然后完成最终提交。' : 'Company profile saved. Now add your verification files, then complete the final submission.');
      setActiveTab('verification');
      if (typeof nextVerificationStep === 'number') {
        setVerificationStep(Math.min(3, Math.max(1, nextVerificationStep)));
      }
    }

    return true;
  };

  const uploadLogo = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadingLogo(true);
    const path = `${user.id}/logo_${Date.now()}.${file.name.split('.').pop()}`;
    const { error } = await sb.storage.from('product-images').upload(path, file, { upsert: true });
    if (!error) { const url = STORAGE_URL + path; await sb.from('profiles').update({ avatar_url: url }).eq('id', user.id); setSettings(prev => ({ ...prev, avatar_url: url })); }
    setUploadingLogo(false);
  };

  // Cover photo — single image, optional. Mirrors uploadLogo: writes the
  // URL straight to profiles so this works independently of the larger
  // settings save flow. Migration 20260506000001 adds the column.
  const uploadCoverPhoto = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadingCover(true);
    const path = `${user.id}/cover_${Date.now()}.${file.name.split('.').pop()}`;
    const { error } = await sb.storage.from('product-images').upload(path, file, { upsert: true });
    if (!error) { const url = STORAGE_URL + path; await sb.from('profiles').update({ cover_photo_url: url }).eq('id', user.id); setSettings(prev => ({ ...prev, cover_photo_url: url })); }
    setUploadingCover(false);
  };
  const removeCoverPhoto = async () => {
    await sb.from('profiles').update({ cover_photo_url: null }).eq('id', user.id);
    setSettings(prev => ({ ...prev, cover_photo_url: '' }));
  };

  const uploadFactoryImage = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    if ((settings.factory_images || []).length >= 3) { alert(isAr ? 'الحد الأقصى 3 صور' : lang === 'zh' ? '最多只能上传 3 张图片' : 'Max 3 images'); return; }
    setUploadingFactory(true);
    const path = `${user.id}/factory_${Date.now()}.${file.name.split('.').pop()}`;
    const { error } = await sb.storage.from('product-images').upload(path, file, { upsert: true });
    if (!error) { const url = STORAGE_URL + path; const newImages = [...(settings.factory_images || []), url]; await sb.from('profiles').update({ factory_images: newImages }).eq('id', user.id); setSettings(prev => ({ ...prev, factory_images: newImages })); }
    setUploadingFactory(false);
  };

  const removeFactoryImage = async (url) => {
    const newImages = (settings.factory_images || []).filter(img => img !== url);
    await sb.from('profiles').update({ factory_images: newImages }).eq('id', user.id);
    setSettings(prev => ({ ...prev, factory_images: newImages }));
  };

  const dismissRejected = async (offerId) => {
    await sb.from('offers').update({ seen: true }).eq('id', offerId);
    setRejectedOffers(prev => prev.filter(o => o.id !== offerId));
  };

  const loadMyOffers = async () => {
    // Hydrate from the session cache instantly, then revalidate. Mutations
    // re-call this loader, so the cache stays fresh (no explicit invalidation).
    const key = `sup-offers:${user.id}`;
    const cached = readSwrCache(key);
    if (cached !== undefined) { setMyOffers(cached.offers); setCompletedPayments(new Set(cached.completed)); } else setLoadingOffers(true);
    const { data } = await sb.from('offers').select(MY_OFFERS_SELECT).eq('supplier_id', user.id).order('created_at', { ascending: false });
    const { data: payData } = await sb.from('payments').select('request_id').eq('supplier_id', user.id).eq('status', 'completed');
    // Paid in full = a second_paid row, or a first_paid row of 100% — the same
    // test the database guard uses (request_is_fully_paid,
    // supabase/migrations/20260929010500_shipping_requires_full_payment.sql).
    const { data: stagePays, error: stagePaysErr } = await sb.from('payments')
      .select('request_id, status, payment_pct').eq('supplier_id', user.id).in('status', ['first_paid', 'second_paid']);
    if (stagePaysErr) console.error('[loadMyOffers] payments read failed:', stagePaysErr.code || '', stagePaysErr.message);
    setFullyPaidReqs(new Set((stagePays || [])
      .filter(p => p.status === 'second_paid' || Number(p.payment_pct) >= 100)
      .map(p => p.request_id)));
    if (data) {
      const completed = (payData || []).map(p => p.request_id);
      // The trader's name from profile_directory (gated: shown once the offer
      // is accepted), attached where the cards read it: o.requests.profiles.
      // Index-aligned: one entry per offer ({} for an offer with no request).
      const reqs = await attachDirectoryProfiles(sb, data.map(o => o.requests || {}), 'buyer_id', 'profiles');
      const offersWithNames = data.map((o, i) => (o.requests ? { ...o, requests: reqs[i] } : o));
      writeSwrCache(key, { offers: offersWithNames, completed });
      setMyOffers(offersWithNames);
      setCompletedPayments(new Set(completed));
    }
    setLoadingOffers(false);
  };

  const loadMyProducts = async () => {
    const key = `sup-products:${user.id}`;
    const cached = readSwrCache(key);
    if (cached !== undefined) setMyProducts(cached); else setLoadingProducts(true);
    const { data } = await sb.from('products').select(`*, ${PRODUCT_TIER_EMBED}`).eq('supplier_id', user.id).order('created_at', { ascending: false });
    if (data) { writeSwrCache(key, data); setMyProducts(data); }
    setLoadingProducts(false);
  };

  const loadDirectOrders = async () => {
    if (!user) return;
    const key = `sup-direct-orders:${user.id}`;
    const cached = readSwrCache(key);
    if (cached !== undefined) setDirectOrders(cached); else setLoadingDirectOrders(true);

    const productsRes = await sb.from('products').select('id').eq('supplier_id', user.id);
    console.log('[loadDirectOrders] products query response:', productsRes);
    const myProductIds = (productsRes.data || []).map(p => p.id);
    if (myProductIds.length === 0) {
      writeSwrCache(key, []);
      setDirectOrders([]);
      setLoadingDirectOrders(false);
      return;
    }

    const ordersRes = await sb
      .from('requests')
      .select('*')
      .eq('status', 'pending_supplier_confirmation')
      .in('product_ref', myProductIds)
      .order('created_at', { ascending: false });
    console.log('[loadDirectOrders] requests query response:', ordersRes);

    // Trader names via profile_directory — null here by the owner's rule (a
    // pending order is not yet confirmed), so the cards read "Trader".
    const pendingRows = await attachDirectoryProfiles(sb, ordersRes.data || [], 'buyer_id', 'profiles');
    const refIds = [...new Set(pendingRows.map(r => r.product_ref).filter(Boolean))];
    const productsByIdRes = refIds.length
      ? await sb.from('products').select(`id, name_ar, name_en, name_zh, currency, ${PRODUCT_TIER_EMBED}`).in('id', refIds)
      : { data: [], error: null };
    console.log('[loadDirectOrders] product details response:', productsByIdRes);
    const productsById = (productsByIdRes.data || []).reduce((acc, p) => { acc[p.id] = p; return acc; }, {});

    const directResult = pendingRows.map(r => ({ ...r, product: productsById[r.product_ref] || null }));
    writeSwrCache(key, directResult);
    setDirectOrders(directResult);
    setLoadingDirectOrders(false);
  };

  // Confirmed-but-not-yet-paid: read-only for the supplier (the buyer must pay
  // next). Without this the order vanishes from the supplier's view between
  // 'confirm' and the buyer's payment.
  const loadConfirmedDirectOrders = async () => {
    const { data: { user } } = await sb.auth.getUser();
    if (!user) return;
    const key = `sup-confirmed-direct-orders:${user.id}`;
    const cached = readSwrCache(key);
    if (cached !== undefined) setConfirmedDirectOrders(cached); else setLoadingConfirmedDirectOrders(true);

    const productsRes = await sb.from('products').select('id').eq('supplier_id', user.id);
    const myProductIds = (productsRes.data || []).map(p => p.id);
    if (myProductIds.length === 0) {
      writeSwrCache(key, []);
      setConfirmedDirectOrders([]);
      setLoadingConfirmedDirectOrders(false);
      return;
    }

    const ordersRes = await sb
      .from('requests')
      .select('*')
      .eq('status', 'supplier_confirmed')
      .in('product_ref', myProductIds)
      .order('updated_at', { ascending: false });

    // Confirmed → the trader's name is now visible (profile_directory gate).
    const confirmedRows = await attachDirectoryProfiles(sb, ordersRes.data || [], 'buyer_id', 'profiles');
    const refIds = [...new Set(confirmedRows.map(r => r.product_ref).filter(Boolean))];
    const productsByIdRes = refIds.length
      ? await sb.from('products').select(`id, name_ar, name_en, name_zh, currency, ${PRODUCT_TIER_EMBED}`).in('id', refIds)
      : { data: [], error: null };
    const productsById = (productsByIdRes.data || []).reduce((acc, p) => { acc[p.id] = p; return acc; }, {});

    const result = confirmedRows.map(r => ({ ...r, product: productsById[r.product_ref] || null }));
    writeSwrCache(key, result);
    setConfirmedDirectOrders(result);
    setLoadingConfirmedDirectOrders(false);
  };

  const confirmDirectOrder = async (request) => {
    if (!request?.id || !request?.buyer_id) return;
    setDirectOrderActioning(prev => ({ ...prev, [request.id]: 'confirming' }));

    // supplierRequestTitle, not a hand-written chain: this line used to put
    // Arabic FIRST regardless of who was reading, so a Chinese factory with
    // the interface in Chinese got the Arabic title. Three copies of it.
    const productName = supplierRequestTitle(request, lang);

    const updRes = await sb
      .from('requests')
      .update({ status: 'supplier_confirmed' })
      .eq('id', request.id)
      .select()
      .single();
    console.log('[confirmDirectOrder] update response:', updRes);
    if (updRes.error) {
      setDirectOrderActioning(prev => ({ ...prev, [request.id]: null }));
      alert(isAr ? 'تعذّر تأكيد الطلب — حاول مرة أخرى' : lang === 'zh' ? '无法确认订单 — 请重试' : 'Could not confirm order — try again');
      return;
    }

    // Notification + email are best-effort side effects — fire them in the
    // background so the confirm responds instantly (not blocked on the email
    // edge-function round-trip). The buyer still gets both a moment later.
    sb.from('notifications').insert({
      user_id: request.buyer_id,
      type: 'supplier_confirmed',
      title_ar: `أكد المورد طلبك — يمكنك الدفع الآن: ${productName}`,
      title_en: `Supplier confirmed — you can pay now: ${productName}`,
      title_zh: `供应商已确认您的订单 — 请付款：${productName}`,
      ref_id: request.id,
      is_read: false,
    }).then(undefined, () => {});
    fetch(SEND_EMAILS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
      body: JSON.stringify({
        type: 'direct_order_confirmed',
        data: { recipientUserId: request.buyer_id, productName, quantity: request.quantity },
      }),
    }).catch((emailError) => console.error('[confirmDirectOrder] email error:', emailError));

    setDirectOrderActioning(prev => ({ ...prev, [request.id]: null }));
    loadDirectOrders();
    loadConfirmedDirectOrders();   // moved pending → confirmed; surface it in its new section
  };

  const rejectDirectOrder = async (request) => {
    if (!request?.id || !request?.buyer_id) return;
    if (!window.confirm(isAr ? 'تأكيد رفض هذا الطلب؟' : lang === 'zh' ? '确认拒绝此订单？' : 'Reject this order?')) return;
    setDirectOrderActioning(prev => ({ ...prev, [request.id]: 'rejecting' }));

    // supplierRequestTitle, not a hand-written chain: this line used to put
    // Arabic FIRST regardless of who was reading, so a Chinese factory with
    // the interface in Chinese got the Arabic title. Three copies of it.
    const productName = supplierRequestTitle(request, lang);

    const updRes = await sb
      .from('requests')
      .update({ status: 'supplier_rejected' })
      .eq('id', request.id)
      .select()
      .single();
    console.log('[rejectDirectOrder] update response:', updRes);
    if (updRes.error) {
      setDirectOrderActioning(prev => ({ ...prev, [request.id]: null }));
      alert(isAr ? 'تعذّر رفض الطلب — حاول مرة أخرى' : lang === 'zh' ? '无法拒绝订单 — 请重试' : 'Could not reject order — try again');
      return;
    }

    const notifRes = await sb.from('notifications').insert({
      user_id: request.buyer_id,
      type: 'supplier_rejected',
      title_ar: `لم يتمكن المورد من تنفيذ طلبك: ${productName}`,
      title_en: `Supplier could not fulfill your order: ${productName}`,
      title_zh: `供应商无法接受订单：${productName}`,
      ref_id: request.id,
      is_read: false,
    });   // no .select() — the row is the buyer's; supplier readback is RLS-blocked (would 42501)
    console.log('[rejectDirectOrder] notification response:', notifRes);

    try {
      const r = await fetch(SEND_EMAILS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          type: 'direct_order_rejected',
          data: { recipientUserId: request.buyer_id, productName, quantity: request.quantity },
        }),
      });
      const body = await r.json().catch(() => null);
      console.log('[rejectDirectOrder] email response:', { status: r.status, body });
    } catch (emailError) {
      console.error('[rejectDirectOrder] email error:', emailError);
    }

    setDirectOrderActioning(prev => ({ ...prev, [request.id]: null }));
    loadDirectOrders();
  };

  const loadPaidDirectOrders = async () => {
    if (!user) return;
    setLoadingPaidDirectOrders(true);

    const productsRes = await sb.from('products').select('id').eq('supplier_id', user.id);
    console.log('[loadPaidDirectOrders] products query response:', productsRes);
    const myProductIds = (productsRes.data || []).map(p => p.id);
    if (myProductIds.length === 0) {
      setPaidDirectOrders([]);
      setLoadingPaidDirectOrders(false);
      return;
    }

    const ordersRes = await sb
      .from('requests')
      .select('*')
      .eq('status', 'paid')
      .in('product_ref', myProductIds)
      .order('updated_at', { ascending: false });
    console.log('[loadPaidDirectOrders] requests query response:', ordersRes);

    const rows = await attachDirectoryProfiles(sb, ordersRes.data || [], 'buyer_id', 'profiles');
    if (rows.length === 0) {
      setPaidDirectOrders([]);
      setLoadingPaidDirectOrders(false);
      return;
    }

    const refIds = [...new Set(rows.map(r => r.product_ref).filter(Boolean))];
    const productsByIdRes = refIds.length
      ? await sb.from('products').select(`id, name_ar, name_en, name_zh, currency, spec_lead_time_days, ${PRODUCT_TIER_EMBED}`).in('id', refIds)
      : { data: [], error: null };
    console.log('[loadPaidDirectOrders] product details response:', productsByIdRes);
    const productsById = (productsByIdRes.data || []).reduce((acc, p) => { acc[p.id] = p; return acc; }, {});

    const reqIds = rows.map(r => r.id);
    const paymentsRes = reqIds.length
      ? await sb.from('payments').select('id, request_id, amount, status, created_at').eq('supplier_id', user.id).in('request_id', reqIds).order('created_at', { ascending: false })
      : { data: [], error: null };
    console.log('[loadPaidDirectOrders] payments query response:', paymentsRes);
    const paymentByRequest = (paymentsRes.data || []).reduce((acc, p) => {
      if (!acc[p.request_id]) acc[p.request_id] = p;
      return acc;
    }, {});

    setPaidDirectOrders(rows.map(r => ({
      ...r,
      product: productsById[r.product_ref] || null,
      payment: paymentByRequest[r.id] || null,
    })));
    setLoadingPaidDirectOrders(false);
  };

  const loadDirectOrdersHistory = async () => {
    if (!user) return;
    setLoadingDirectHistory(true);

    const productsRes = await sb.from('products').select('id').eq('supplier_id', user.id);
    console.log('[loadDirectOrdersHistory] products query response:', productsRes);
    const myProductIds = (productsRes.data || []).map(p => p.id);
    if (myProductIds.length === 0) {
      setShippingDirectOrders([]);
      setCompletedDirectOrders([]);
      setLoadingDirectHistory(false);
      return;
    }

    const ordersRes = await sb
      .from('requests')
      .select('*')
      .in('status', ['shipping', 'arrived', 'delivered'])
      .in('product_ref', myProductIds)
      .order('created_at', { ascending: false });
    console.log('[loadDirectOrdersHistory] requests query response:', ordersRes);

    const rows = await attachDirectoryProfiles(sb, ordersRes.data || [], 'buyer_id', 'profiles');
    if (rows.length === 0) {
      setShippingDirectOrders([]);
      setCompletedDirectOrders([]);
      setLoadingDirectHistory(false);
      return;
    }

    const refIds = [...new Set(rows.map(r => r.product_ref).filter(Boolean))];
    const productsByIdRes = refIds.length
      ? await sb.from('products').select(`id, name_ar, name_en, name_zh, currency, ${PRODUCT_TIER_EMBED}`).in('id', refIds)
      : { data: [], error: null };
    console.log('[loadDirectOrdersHistory] product details response:', productsByIdRes);
    const productsById = (productsByIdRes.data || []).reduce((acc, p) => { acc[p.id] = p; return acc; }, {});

    const reqIds = rows.map(r => r.id);
    const paymentsRes = reqIds.length
      ? await sb.from('payments').select('id, request_id, amount, status, created_at').eq('supplier_id', user.id).in('request_id', reqIds).order('created_at', { ascending: false })
      : { data: [], error: null };
    console.log('[loadDirectOrdersHistory] payments query response:', paymentsRes);
    const paymentByRequest = (paymentsRes.data || []).reduce((acc, p) => {
      if (!acc[p.request_id]) acc[p.request_id] = p;
      return acc;
    }, {});

    const enriched = rows.map(r => ({
      ...r,
      product: productsById[r.product_ref] || null,
      payment: paymentByRequest[r.id] || null,
    }));

    setShippingDirectOrders(enriched.filter(r => r.status === 'shipping' || r.status === 'arrived'));
    setCompletedDirectOrders(enriched.filter(r => r.status === 'delivered'));
    setLoadingDirectHistory(false);
  };

  const submitDirectTracking = async (request) => {
    if (!request?.id || !request?.buyer_id) return;
    const num = trackingInputs[request.id];
    if (!num || !String(num).trim()) {
      alert(isAr ? 'أدخل رقم التتبع أولاً' : lang === 'zh' ? '请先输入物流单号' : 'Enter the tracking number first');
      return;
    }
    setDirectShippingActioning(prev => ({ ...prev, [request.id]: 'shipping' }));

    // supplierRequestTitle, not a hand-written chain: this line used to put
    // Arabic FIRST regardless of who was reading, so a Chinese factory with
    // the interface in Chinese got the Arabic title. Three copies of it.
    const productName = supplierRequestTitle(request, lang);
    const deliveryDays = Number(request.product?.spec_lead_time_days || 0);
    const estimatedDelivery = deliveryDays > 0
      ? new Date(Date.now() + (deliveryDays * 24 * 60 * 60 * 1000)).toISOString()
      : null;

    const updRes = await sb.from('requests').update({
      tracking_number: String(num).trim(),
      shipping_company: shippingCompany,
      status: 'shipping',
      shipping_status: 'shipping',
      ...(estimatedDelivery ? { estimated_delivery: estimatedDelivery } : {}),
    }).eq('id', request.id).select().single();
    console.log('[submitDirectTracking] update response:', updRes);
    if (updRes.error) {
      setDirectShippingActioning(prev => ({ ...prev, [request.id]: null }));
      alert(isAr ? 'تعذّر تسجيل رقم التتبع — حاول مرة أخرى' : lang === 'zh' ? '无法保存物流信息 — 请重试' : 'Could not save tracking — try again');
      return;
    }

    const notifRes = await sb.from('notifications').insert({
      user_id: request.buyer_id,
      type: 'shipped',
      title_ar: `طلبك في الطريق — رقم التتبع: ${num}`,
      title_en: `Your order is on the way — Tracking: ${num}`,
      title_zh: `您的订单已发货 — 跟踪号：${num}`,
      ref_id: request.id,
      is_read: false,
    });   // no .select() — cross-user notification row; readback is RLS-blocked (would 42501)
    console.log('[submitDirectTracking] notification response:', notifRes);

    try {
      const r = await fetch(SEND_EMAILS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          type: 'direct_order_shipped',
          data: {
            recipientUserId: request.buyer_id,
            productName,
            trackingNumber: String(num).trim(),
            shippingCompany,
          },
        }),
      });
      const body = await r.json().catch(() => null);
      console.log('[submitDirectTracking] email response:', { status: r.status, body });
    } catch (emailError) {
      console.error('[submitDirectTracking] email error:', emailError);
    }

    setTrackingInputs(prev => ({ ...prev, [request.id]: '' }));
    setDirectShippingActioning(prev => ({ ...prev, [request.id]: null }));
    loadPaidDirectOrders();
    loadDirectOrdersHistory();
  };

  const formatDirectCountdown = (createdAt, nowMs) => {
    if (!createdAt) return { expired: true, hours: 0, mins: 0 };
    const expiresAt = new Date(createdAt).getTime() + 24 * 60 * 60 * 1000;
    const remaining = expiresAt - nowMs;
    if (remaining <= 0) return { expired: true, hours: 0, mins: 0 };
    const hours = Math.floor(remaining / (60 * 60 * 1000));
    const mins = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
    return { expired: false, hours, mins };
  };

  // ONE request, by id — not a book. A supplier reaches a request because it
  // was routed to them — by an admin, by holding a link token, or, since
  // 2026-10-11, automatically as an open direct request (source 'open_pool',
  // 20261011224335). RLS enforces that independently, so a request id a
  // supplier was never given simply returns nothing.
  const loadRequests = async () => {
    if (!user || !detailRequestId) { setRequests([]); setLoadingRequests(false); return; }
    setLoadingRequests(true);
    const query = sb.from('requests')
      .select('*')
      .eq('id', detailRequestId)
      .in('status', ['open', 'offers_received']);
    const { data, error } = await query;
    if (error) {
      setRequests([]);
      setLoadingRequests(false);
      return;
    }
    if (data) {
      // Fetch order_line_items for requests that have them.
      // order_line_items.order_id references requests.id (per the variants
      // migration line 248) — the column is `order_id`, not `request_id`.
      const requestIds = data.map(r => r.id);
      const { data: lineItemsData } = requestIds.length
        ? await sb.from('order_line_items').select('*, product_variants(sku, option_values)').in('order_id', requestIds)
        : { data: [] };
      const linesByRequest = (lineItemsData || []).reduce((acc, li) => {
        acc[li.order_id] = [...(acc[li.order_id] || []), li];
        return acc;
      }, {});
      setRequests(data.map(r => ({ ...r, lineItems: linesByRequest[r.id] || [] })));
    }
    setLoadingRequests(false);
  };

  const uploadFile = async (file, type) => {
    if (!file) return null;
    const isVideo = type === 'video';
    if (isVideo && file.size > VERIFICATION_VIDEO_MAX_BYTES) { alert(t.maxVideo); return null; }
    isVideo ? setUploadingVideo(true) : setUploadingImage(true);
    const path = `${user.id}/${type}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${file.name.split('.').pop()}`;
    const { error } = await sb.storage.from('product-images').upload(path, file, { upsert: true });
    isVideo ? setUploadingVideo(false) : setUploadingImage(false);
    if (error) { alert(isAr ? 'فشل الرفع' : lang === 'zh' ? '上传失败' : 'Upload failed'); return null; }
    return STORAGE_URL + path;
  };

  const applyProductMedia = (setter, mediaUpdater) => {
    setter(prev => normalizeProductDraftMedia(mediaUpdater(prev || emptyProduct)));
  };

  const handleImageUpload = async (e, isEdit = false) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const currentImages = getProductGalleryImages(isEdit ? editingProduct : product);
    if (currentImages.length + files.length > PRODUCT_GALLERY_LIMIT) {
      alert(t.maxImagesError);
      e.target.value = '';
      return;
    }

    const nextUrls = [];
    for (const file of files) {
      const url = await uploadFile(file, 'image');
      if (url) nextUrls.push(url);
    }

    if (!nextUrls.length) return;
    const setter = isEdit ? setEditingProduct : setProduct;
    applyProductMedia(setter, prev => ({
      ...prev,
      gallery_images: [...getProductGalleryImages(prev), ...nextUrls].slice(0, PRODUCT_GALLERY_LIMIT),
    }));
    e.target.value = '';
  };

  const removeImageAt = (index, isEdit = false) => {
    const setter = isEdit ? setEditingProduct : setProduct;
    const source = isEdit ? editingProduct : product;
    const removedUrl = getProductGalleryImages(source)[index];
    applyProductMedia(setter, prev => ({
      ...prev,
      gallery_images: getProductGalleryImages(prev).filter((_, imgIndex) => imgIndex !== index),
    }));
    // Phase 4 — orphan cleanup: drop the storage object too.
    if (removedUrl) {
      removeStorageObjectByUrl(sb, BUCKET_PRODUCT_MEDIA, removedUrl);
    }
  };

  const handleVideoUpload = async (e, isEdit = false) => {
    const file = e.target.files?.[0];
    const url = await uploadFile(file, 'video');
    if (!url) return;
    const setter = isEdit ? setEditingProduct : setProduct;
    applyProductMedia(setter, prev => ({ ...prev, video_url: url }));
    e.target.value = '';
  };

  const removeVideo = (isEdit = false) => {
    const setter = isEdit ? setEditingProduct : setProduct;
    const source = isEdit ? editingProduct : product;
    const removedUrl = source?.video_url;
    applyProductMedia(setter, prev => ({ ...prev, video_url: null }));
    // Phase 4 — orphan cleanup: drop the storage object too.
    if (removedUrl) {
      removeStorageObjectByUrl(sb, BUCKET_PRODUCT_MEDIA, removedUrl);
    }
  };

  const openProductPreview = () => {
    const validationMessage = getProductComposerValidationMessage(product, lang);
    if (validationMessage) {
      setProductSaveMsg(validationMessage);
      return;
    }
    setProductSaveMsg('');
    setProduct(normalizeProductDraftMedia(product));
    setProductComposerStep('preview');
  };

  const openEditProductPreview = () => {
    if (editingProduct?.tiersLoading) { setProductSaveMsg(t.tiersStillLoading); return; }
    const validationMessage = getProductComposerValidationMessage(editingProduct, lang);
    if (validationMessage) {
      setProductSaveMsg(validationMessage);
      return;
    }
    setProductSaveMsg('');
    setEditingProduct(prev => normalizeProductDraftMedia(prev));
    setEditProductComposerStep('preview');
  };

  // ── Variant data persistence ───────────────────────────────────────────────
  const saveVariantData = async (productId, vd) => {
    const { options = [], variants = [], tiers = [], shipping = [] } = vd || {};

    // Wipe and rewrite — simplest safe approach for Phase 2.
    // NOTE: product-level pricing tiers (variant_id IS NULL) are owned by the
    // ProductComposer flow now — saveProductPricingTiers handles those. Here
    // we only delete variant-specific tiers (variant_id IS NOT NULL) to avoid
    // wiping the product-level rows that were just written by the parent flow.
    await sb.from('product_options').delete().eq('product_id', productId);
    await sb.from('product_pricing_tiers').delete().eq('product_id', productId).not('variant_id', 'is', null);
    await sb.from('product_shipping_options').delete().eq('product_id', productId);
    // product_variants cascade-deletes with options, so no need to delete separately.

    if (!options.length) return;

    const keyMap = {}; // _key → DB uuid

    for (let oi = 0; oi < options.length; oi++) {
      const opt = options[oi];
      const { data: optRow, error: optErr } = await sb.from('product_options').insert({
        product_id: productId,
        name_zh: opt.name_zh,
        name_en: opt.name_en || null,
        name_ar: opt.name_ar || null,
        input_type: opt.input_type || 'select',
        display_order: oi,
      }).select('id').single();
      if (optErr) { console.error('saveVariantData option error:', optErr); continue; }
      keyMap[opt._key] = optRow.id;

      for (let vi = 0; vi < opt.values.length; vi++) {
        const val = opt.values[vi];
        const { data: valRow, error: valErr } = await sb.from('product_option_values').insert({
          option_id: optRow.id,
          value_zh: val.value_zh,
          value_en: val.value_en || null,
          value_ar: val.value_ar || null,
          color_hex: opt.input_type === 'color_swatch' ? (val.color_hex || null) : null,
          image_url: val.image_url || null,
          display_order: vi,
        }).select('id').single();
        if (valErr) { console.error('saveVariantData value error:', valErr); continue; }
        keyMap[val._key] = valRow.id;
      }
    }

    for (const variant of variants) {
      if (!variant.combo?.length) continue;
      const optionValues = variant.combo
        .map(({ optionKey, valueKey }) => ({ option_id: keyMap[optionKey], value_id: keyMap[valueKey] }))
        .filter(o => o.option_id && o.value_id);
      if (!optionValues.length) continue;
      const { error: varErr } = await sb.from('product_variants').insert({
        product_id: productId,
        sku: variant.sku || null,
        option_values: optionValues,
        price: variant.price ? parseFloat(variant.price) : null,
        moq: variant.moq ? parseInt(variant.moq, 10) : null,
        stock: (variant.stock !== '' && variant.stock !== undefined && variant.stock !== null) ? parseInt(variant.stock, 10) : null,
        lead_time_days: variant.lead_time_days ? parseInt(variant.lead_time_days, 10) : null,
        image_url: variant.image_url || null,
        is_active: variant.is_active !== false,
      });
      if (varErr) console.error('saveVariantData variant error:', varErr);
    }

    // Product-level tiers (variant_id IS NULL) are written by the
    // ProductComposer flow via saveProductPricingTiers. The variants
    // subsystem owns only variant-level tiers, which the UI does not
    // currently expose — leave the variant-tier slot wired but unused.
    void tiers;

    for (const ship of shipping) {
      if (!ship.is_available) continue;
      await sb.from('product_shipping_options').insert({
        product_id: productId,
        method: ship.method,
        is_available: true,
        lead_time_min_days: ship.lead_time_min_days ? parseInt(ship.lead_time_min_days, 10) : null,
        lead_time_max_days: ship.lead_time_max_days ? parseInt(ship.lead_time_max_days, 10) : null,
        cost_per_unit_usd: ship.cost_per_unit_usd ? parseFloat(ship.cost_per_unit_usd) : null,
      });
    }
  };

  const loadVariantData = async (productId) => {
    // Product-level tiers (variant_id IS NULL) are loaded by the ProductComposer
    // flow via loadProductPricingTiers, not here. We only fetch variant-level
    // tiers, which the current UI does not surface but kept loaded for safety.
    const [
      { data: optRows },
      { data: varRows },
      { data: tierRows },
      { data: shipRows },
    ] = await Promise.all([
      sb.from('product_options').select('*, product_option_values(*)').eq('product_id', productId).order('display_order'),
      sb.from('product_variants').select('*').eq('product_id', productId),
      sb.from('product_pricing_tiers').select('*').eq('product_id', productId).not('variant_id', 'is', null).order('qty_from'),
      sb.from('product_shipping_options').select('*').eq('product_id', productId),
    ]);

    const options = (optRows || []).map(opt => ({
      _key: opt.id,
      name_zh: opt.name_zh || '',
      name_en: opt.name_en || '',
      name_ar: opt.name_ar || '',
      input_type: opt.input_type || 'select',
      values: ((opt.product_option_values || [])
        .sort((a, b) => a.display_order - b.display_order)
        .map(val => ({
          _key: val.id,
          value_zh: val.value_zh || '',
          value_en: val.value_en || '',
          value_ar: val.value_ar || '',
          color_hex: val.color_hex || 'var(--green)',
          image_url: val.image_url || null,
        }))),
    }));

    // Rebuild variant combos from DB option_values jsonb
    const dbValToUiKey = {}; // DB value uuid → _key (= same uuid in loaded data)
    for (const opt of options) {
      for (const val of opt.values) dbValToUiKey[val._key] = val._key;
    }
    const optionDbToKey = {};
    for (const opt of options) optionDbToKey[opt._key] = opt._key;

    const variants = (varRows || []).map(v => {
      const combo = (v.option_values || []).map(({ option_id, value_id }) => ({
        optionKey: optionDbToKey[option_id] || option_id,
        valueKey: dbValToUiKey[value_id] || value_id,
      }));
      return {
        _key: combo.map(c => `${c.optionKey}:${c.valueKey}`).join('|') || v.id,
        combo,
        sku: v.sku || '',
        price: v.price != null ? String(v.price) : '',
        moq: v.moq != null ? String(v.moq) : '',
        stock: v.stock != null ? String(v.stock) : '',
        lead_time_days: v.lead_time_days != null ? String(v.lead_time_days) : '',
        image_url: v.image_url || null,
        is_active: v.is_active !== false,
      };
    });

    const tiers = (tierRows || []).map(t => ({
      _key: t.id,
      qty_from: t.qty_from != null ? String(t.qty_from) : '',
      qty_to: t.qty_to != null ? String(t.qty_to) : '',
      unit_price: t.unit_price != null ? String(t.unit_price) : '',
    }));

    const defaultShipping = emptyVariantData().shipping;
    const shipping = defaultShipping.map(def => {
      const row = (shipRows || []).find(s => s.method === def.method);
      if (!row) return def;
      return {
        ...def,
        is_available: row.is_available,
        lead_time_min_days: row.lead_time_min_days != null ? String(row.lead_time_min_days) : '',
        lead_time_max_days: row.lead_time_max_days != null ? String(row.lead_time_max_days) : '',
        cost_per_unit_usd: row.cost_per_unit_usd != null ? String(row.cost_per_unit_usd) : '',
      };
    });

    return { options, variants, tiers, shipping };
  };

  const addProduct = async (asDraft) => {
    // Guard against the click event being passed as the arg — only an explicit
    // `true` (from the Save-as-Draft button) means draft.
    const isDraft = asDraft === true;
    if (!isDraft) {
      const validationMessage = getProductComposerValidationMessage(product, lang);
      if (validationMessage) {
        setProductSaveMsg(validationMessage);
        return;
      }
    }
    setSaving(true);
    setProductSaveMsg('');
    // Translation is enrichment, not a condition for a valid row: the write
    // payload self-fills every empty language column from the source name
    // (buildProductWritePayload's fallbackName), so insert immediately with the
    // source text and translate in the background (below). A slow or failed Grok
    // call can no longer delay — or block — publishing the product.
    const payload = buildProductWritePayload(product, user.id, { asDraft: isDraft, isVerified: supplierState.isVerifiedStatus });
    const { data: insertedRows, error, strippedColumns } = await runWithOptionalColumns({
      table: 'products',
      payload,
      optionalKeys: PRODUCT_OPTIONAL_DB_FIELDS,
      execute: (nextPayload) => sb.from('products').insert(nextPayload).select('id').single(),
    });
    const newProductId = insertedRows?.id;
    let addTierFailed = false;
    if (!error && newProductId) {
      // Save the product-level pricing tiers (variant_id IS NULL). A failure
      // is shown to the supplier, not only logged (2026-10-04).
      try {
        const tierErr = await saveProductPricingTiers(sb, newProductId, product.tiers, product.moq);
        if (tierErr) { console.error('addProduct saveProductPricingTiers error:', tierErr); addTierFailed = true; }
      } catch (tErr) { console.error('addProduct saveProductPricingTiers exception:', tErr); addTierFailed = true; }

      // Phase 4 — save certifications (uploads PDFs + inserts DB rows).
      if (Array.isArray(product.certifications) && product.certifications.length > 0) {
        try {
          await saveProductCertifications(sb, {
            productId: newProductId,
            userId: user.id,
            nextCerts: product.certifications,
            prevCerts: [],
          });
        } catch (cErr) { console.error('addProduct saveProductCertifications error:', cErr); }
      }

      // Save variant data if variants are enabled (options/variants/shipping only;
      // tiers are now owned by the product-level flow above).
      if (product.has_variants) {
        try { await saveVariantData(newProductId, variantData); }
        catch (vErr) { console.error('addProduct saveVariantData error:', vErr); }
      }

      // Enrich the row with real translations in the background — best-effort.
      // The product is already saved and valid (source-filled); a failed
      // translation just leaves the fallback text, a successful one refreshes the list.
      buildTranslatedProductFields({
        nameAr: product.name_ar, nameEn: product.name_en, nameZh: product.name_zh,
        descEn: product.desc_en, descAr: product.desc_ar, lang,
      }).then((tf) => {
        if (tf && Object.keys(tf).length) {
          sb.from('products').update(tf).eq('id', newProductId).then(() => loadMyProducts(), (e) => console.error('addProduct translation update error:', e));
        }
      }, (e) => console.error('addProduct translation error:', e?.message || e));
    }
    setSaving(false);
    if (error) {
      console.error('addProduct error:', error);
      setProductSaveMsg(isAr ? 'حدث خطأ أثناء الحفظ. حاول مرة أخرى.' : lang === 'zh' ? '保存产品时出错，请重试。' : 'Error saving product. Please try again.');
      return;
    }
    sessionStorage.removeItem('maabar_product_draft');
    setProduct({ ...emptyProduct, tiers: emptyTierSet(), certifications: [] });
    setVariantData(emptyVariantData());
    setProductComposerStep('edit');
    setProductSaveMsg(addTierFailed ? t.tierSaveFailed : strippedColumns.length > 0 ? t.productSavedWithFallback : (isAr ? 'تم إضافة المنتج بنجاح' : lang === 'zh' ? '产品添加成功' : 'Product added successfully'));
    // First product saved → retire the "add your first product" nudge for good.
    if (profile?.first_product_prompt_dismissed !== true) {
      setProfile?.(prev => ({ ...(prev || {}), first_product_prompt_dismissed: true }));
      sb.from('profiles').update({ first_product_prompt_dismissed: true }).eq('id', user.id).then(() => {}, () => {});
    }
    setTimeout(() => { setProductSaveMsg(''); setActiveTab('my-products'); }, addTierFailed ? 6000 : 1800);
    loadStats();
  };

  const updateProduct = async (asDraft) => {
    if (!editingProduct) return;
    const isDraft = asDraft === true;
    if (editingProduct.tiersLoading) { setProductSaveMsg(t.tiersStillLoading); return; }
    // Its own check, not only the preview's (2026-10-04): publishing must
    // never depend on which button led here.
    if (!isDraft) {
      const validationMessage = getProductComposerValidationMessage(editingProduct, lang);
      if (validationMessage) {
        setEditProductComposerStep('edit');
        setProductSaveMsg(validationMessage);
        return;
      }
    }
    setSaving(true);
    // Translation is enrichment, not a condition for a valid row (see addProduct):
    // save the edit immediately with the source text — the payload self-fills empty
    // language columns — and re-translate in the background below.
    const payload = buildProductWritePayload(editingProduct, undefined, { asDraft: isDraft, isVerified: supplierState.isVerifiedStatus });
    delete payload.supplier_id;
    let tierFailed = false;
    const { error } = await runWithOptionalColumns({
      table: 'products',
      payload,
      optionalKeys: PRODUCT_OPTIONAL_DB_FIELDS,
      execute: (nextPayload) => sb.from('products').update(nextPayload).eq('id', editingProduct.id),
    });
    if (!error) {
      // Save the product-level pricing tiers (variant_id IS NULL). A failure
      // is shown to the supplier, not only logged (2026-10-04).
      try {
        const tierErr = await saveProductPricingTiers(sb, editingProduct.id, editingProduct.tiers, editingProduct.moq);
        if (tierErr) { console.error('updateProduct saveProductPricingTiers error:', tierErr); tierFailed = true; }
      } catch (tErr) { console.error('updateProduct saveProductPricingTiers exception:', tErr); tierFailed = true; }

      // Phase 4 — diff certifications against the snapshot loaded at edit time.
      try {
        await saveProductCertifications(sb, {
          productId: editingProduct.id,
          userId: user.id,
          nextCerts: editingProduct.certifications || [],
          prevCerts: editProductCertsSnapshot,
        });
      } catch (cErr) { console.error('updateProduct saveProductCertifications error:', cErr); }

      if (editingProduct.has_variants) {
        try { await saveVariantData(editingProduct.id, editVariantData); }
        catch (vErr) { console.error('updateProduct saveVariantData error:', vErr); }
      }

      // Re-translate in the background — best-effort, never blocks the save.
      const editedId = editingProduct.id;
      buildTranslatedProductFields({
        nameAr: editingProduct.name_ar, nameEn: editingProduct.name_en, nameZh: editingProduct.name_zh,
        descEn: editingProduct.desc_en, descAr: editingProduct.desc_ar, lang,
      }).then((tf) => {
        if (tf && Object.keys(tf).length) {
          sb.from('products').update(tf).eq('id', editedId).then(() => loadMyProducts(), (e) => console.error('updateProduct translation update error:', e));
        }
      }, (e) => console.error('updateProduct translation error:', e?.message || e));
    }
    setSaving(false);
    if (error) {
      console.error('updateProduct error:', error);
      setProductSaveMsg(isAr ? 'حدث خطأ أثناء الحفظ. حاول مرة أخرى.' : lang === 'zh' ? '保存产品时出错，请重试。' : 'Error saving product. Please try again.');
      return;
    }
    if (tierFailed) {
      // Keep the editor open on the form so the supplier can save the price again.
      setEditProductComposerStep('edit');
      setProductSaveMsg(t.tierSaveFailed);
      loadMyProducts();
      return;
    }
    setEditProductComposerStep('edit');
    setEditingProduct(null);
    setEditVariantData(emptyVariantData());
    setEditProductCertsSnapshot([]);
    loadMyProducts(); loadStats();
  };

  // Open a product in the editor (the list's Edit button and the "Add your
  // price" button both land here).
  const startEditProduct = (p) => {
    // Expand stored country/port into the dropdown+other form pair.
    const country = expandStoredSelect(p.country_of_origin, COUNTRY_OF_ORIGIN_OPTIONS);
    const port    = expandStoredSelect(p.port_of_loading,   PORT_OF_LOADING_OPTIONS);
    setEditingProduct(normalizeProductDraftMedia({
      ...p,
      tiers: emptyTierSet(),
      // The real tiers load below; until they arrive the form must not save
      // (2026-10-04) — a save in that window used to write the empty set.
      tiersLoading: true,
      certifications: [],
      country_of_origin: country.selectValue || 'China',
      country_of_origin_other: country.otherValue,
      port_of_loading: port.selectValue,
      port_of_loading_other: port.otherValue,
      incoterms: Array.isArray(p.incoterms) ? p.incoterms : [],
      price_validity_days: p.price_validity_days || 30,
    }));
    setEditProductCertsSnapshot([]);
    setEditProductComposerStep('edit');
    setProductSaveMsg('');
    loadProductPricingTiers(sb, p.id)
      .then(loadedTiers => setEditingProduct(prev => (prev && prev.id === p.id ? { ...prev, tiers: loadedTiers, tiersLoading: false } : prev)))
      .catch((err) => {
        console.error('tier load error:', err);
        setEditingProduct(prev => (prev && prev.id === p.id ? { ...prev, tiersLoading: false } : prev));
      });
    // Phase 4 — load the full certification rows so the supplier
    // can edit them. The snapshot lets save-time diff deletions.
    loadProductCertifications(sb, p.id)
      .then(certs => {
        setEditingProduct(prev => prev && prev.id === p.id ? { ...prev, certifications: certs } : prev);
        setEditProductCertsSnapshot(certs);
      })
      .catch(err => { console.error('cert load error:', err); setEditProductCertsSnapshot([]); });
    if (p.has_variants) loadVariantData(p.id).then(setEditVariantData).catch(console.error);
  };

  const toggleProductActive = async (p) => { await sb.from('products').update({ is_active: !p.is_active }).eq('id', p.id); loadMyProducts(); loadStats(); };
  // A product a purchase request, invoice, sample or order line points at
  // cannot be deleted (foreign keys without cascade — those records must
  // outlive the listing): Postgres answers 23503. This used to fail silently.
  // Now it says why and offers to hide it instead. Mirror of the app.
  const DELETE_HAS_ORDERS = {
    ar: { msg: 'هذا المنتج عليه طلبات شراء وفواتير، فما يمكن حذفه حتى تبقى سجلاتها.', hide: 'تقدر تخفيه من العرض بدل الحذف. تخفيه الحين؟', hidden: 'وهو مخفي عن التجار أصلاً.', failed: 'تعذّر حذف المنتج، حاول مرة ثانية.' },
    en: { msg: 'This product has purchase orders and invoices, so it cannot be deleted — their records must stay.', hide: 'You can hide it instead. Hide it now?', hidden: 'It is already hidden from traders.', failed: 'Could not delete the product. Please try again.' },
    zh: { msg: '该产品已有采购订单和发票，为保留记录无法删除。', hide: '您可以将其隐藏。现在隐藏吗？', hidden: '它已对采购商隐藏。', failed: '无法删除该产品，请重试。' },
  };
  const deleteProduct = async (p) => {
    if (!window.confirm(t.confirmDelete)) return;
    const { error } = await sb.from('products').delete().eq('id', p.id);
    if (error) {
      console.error('[DashboardSupplier] product delete failed:', error.code, error.message);
      const c = DELETE_HAS_ORDERS[lang] || DELETE_HAS_ORDERS.ar;
      if (error.code === '23503') {
        if (p.is_active) { if (window.confirm(`${c.msg}\n\n${c.hide}`)) await toggleProductActive(p); }
        else window.alert(`${c.msg} ${c.hidden}`);
      } else {
        window.alert(c.failed);
      }
      return;
    }
    loadMyProducts(); loadStats();
  };

  const saveEditOffer = async () => {
    if (!editOfferModal) return;

    // ═══ REBUILT ON THE SHARED CORE, 2026-09-21 ═══════════════════════════
    // This editor was the last surface still on the pre-2026-09-19 model. It
    // REQUIRED a shipping cost that the create form had stopped collecting,
    // wrote shipping_cost / shipping_method (deprecated by 20260919105244),
    // defaulted origin to 'China', and could not touch price_basis,
    // valid_days or the sample trio at all. So a supplier who picked the
    // wrong price basis had no way to correct it, and every edit quietly
    // re-populated two dead columns.
    //
    // Same fields, same validateQuoteCore, same component as both create
    // doors. "Same fields" has to mean the same rules, or the surfaces
    // diverge again by the back way.
    // ══════════════════════════════════════════════════════════════════════
    const badField = validateQuoteCore({
      price: editOfferForm.price,
      leadTime: editOfferForm.leadTime,
      priceBasis: editOfferForm.priceBasis,
    });
    const moq = String(editOfferForm.moq || '').trim();
    if (badField || !moq) {
      alert(badField ? qt[badField]
        : (isAr ? 'أدخل الحد الأدنى للطلب' : lang === 'zh' ? '请填写最小起订量' : 'Enter the minimum order quantity'));
      return;
    }

    setSavingEditOffer(true);
    // ═══ REMOVED 2026-09-21 — the client-side note translation ═══════════════
    // An in-browser translateTextToAllLanguages() ran here and wrote
    // note_ar / note_en / note_zh alongside the edit. Two things were wrong
    // with it, and only one was cosmetic:
    //
    //   1. guard_offer_write's supplier allowlist has NEVER contained those
    //      three columns, so on any offer whose note_ar was null the write
    //      changed a column outside the allowlist and the whole UPDATE was
    //      refused with P0001. Three of six live offers were in that state,
    //      which is the bug the owner hit.
    //   2. It was the ONLY writer of those columns anywhere in the product.
    //      submit_public_rfq_quote and submit_factory_offer both insert `note`
    //      alone, so a note was translated if and only if the supplier later
    //      EDITED the offer. Nothing reads the columns today.
    //
    // NOTHING VISIBLE IS LOST. The trader's offer card already translates the
    // note at RENDER time — DashboardBuyer.jsx:2099 wraps it in
    // <TranslatedText>, which calls aiTranslate, caches the result and shows
    // the ✨ «ترجمة آلية» badge. That path never read note_ar/en/zh, so the
    // columns this used to write were write-only.
    //
    // Owner's decision, 2026-09-21: drop the write. What remains is a genuine
    // open QUESTION rather than a gap — keep translating at runtime, or move to
    // stored columns filled server-side (an offers trigger calling
    // translate-offer-note, the shape 20260921112056 gave requests). Logged in
    // docs/TECH_DEBT.md and docs/NEXT_SESSION_PLAN.md.
    //
    // ⚠️ DO NOT REINTRODUCE A CLIENT TRANSLATION CALL HERE. Beyond the guard,
    // it is the same defect the request form already retired: a tab closed
    // mid-flight leaves the columns half written, and the source language gets
    // decided by the UI rather than by the script.
    // ═══════════════════════════════════════════════════════════════════════
    // Never changes on edit: the offer's own currency, or USD. The server
    // refuses a change to anything but USD (trg_offers_currency_usd).
    const editCurrency = editOfferForm.currency || editOfferModal.currency || 'USD';
    const sampleOn = !!editOfferForm.sampleAvailable;
    const { error } = await runWithOptionalColumns({
      table: 'offers',
      payload: {
        price: Number(editOfferForm.price),
        currency: editCurrency,
        moq,
        delivery_days: Number.parseInt(editOfferForm.leadTime, 10),
        // No 'China' fallback. A stored value nobody chose is worse than a
        // null on a field that travels onto a certificate of origin
        // (owner, 2026-09-19) — the create path dropped this default then,
        // and this surface was missed.
        origin: String(editOfferForm.origin || '').trim() || null,
        note: editOfferForm.note || null,
        price_basis: editOfferForm.priceBasis || null,
        valid_days: editOfferForm.validDays ? Number(editOfferForm.validDays) : null,
        sample_available: sampleOn,
        sample_price: sampleOn && editOfferForm.samplePrice !== '' && editOfferForm.samplePrice != null ? Number(editOfferForm.samplePrice) : null,
        sample_days: sampleOn && editOfferForm.sampleDays !== '' && editOfferForm.sampleDays != null ? Number.parseInt(editOfferForm.sampleDays, 10) : null,
      },
      // ⚠️ CORRECTED 2026-09-21. This comment used to claim guard_offer_write's
      // UPDATE branch "already allows exactly these columns ... so no migration
      // was needed". IT DOES NOT, and the claim cost a live P0001: 'Suppliers
      // can only edit offer content fields.'
      //
      // What is true: price_basis, valid_days and the sample trio HAVE been on
      // the allowlist since 20260918230823. What was never true is the "exactly"
      // — the allowlist has never contained `currency`, nor note_ar/en/zh.
      // Verified against the LIVE definition with pg_get_functiondef, not
      // against a migration file, because the two can disagree (golden rule 10).
      //
      // The guard compares to_jsonb(new) minus the allowlist against the same
      // slice of old, so an unlisted column only bites when its VALUE CHANGES.
      // That is why this went unnoticed: it fires on a currency switch, and on
      // any offer whose note_ar was null and gets filled — 3 of 6 live offers
      // are in exactly that state.
      //
      // ⚠️ DO NOT "fix" a future P0001 here by deleting keys from optionalKeys.
      // That list is the SCHEMA-CACHE fallback: it strips a column when the DB
      // says the column does not exist. It has no effect on a trigger raising
      // P0001, and trimming it would only hide which columns are being sent.
      // What is on the allowlist is decided in the database.
      optionalKeys: ['origin', 'currency', 'price_basis', 'valid_days', 'sample_available', 'sample_price', 'sample_days'],
      execute: (nextPayload) => sb.from('offers').update(nextPayload).eq('id', editOfferModal.id),
    });
    setSavingEditOffer(false);
    if (error) {
      console.error('saveEditOffer error:', error);
      // The database refuses a note with contact details (SECURITY_BACKLOG #17).
      if (isOfferNoteContactError(error)) { alert(offerNoteContactMessage(lang)); return; }
      alert(isAr ? 'حدث خطأ أثناء حفظ العرض' : lang === 'zh' ? '保存报价时出错' : 'Error saving offer');
      return;
    }
    setEditOfferModal(null);
    loadMyOffers();
  };

  const deleteOffer = async (o) => {
    if (o.status !== 'pending') {
      alert(isAr ? 'لا يمكن حذف عرض تم قبوله أو رفضه' : lang === 'zh' ? '已接受或已拒绝的报价无法删除' : 'Cannot delete an accepted or rejected offer');
      return;
    }
    if (!window.confirm(isAr ? 'هل تريد حذف هذا العرض؟' : lang === 'zh' ? '确认删除这个报价吗？' : 'Delete this offer?')) return;
    await sb.from('offers').delete().eq('id', o.id);
    loadMyOffers(); loadStats();
  };

  const cancelOffer = async (o) => {
    const requestStatus = o.requests?.status || '';
    const isAcceptedBeforePayment = o.status === 'accepted' && !['paid', 'ready_to_ship', 'shipping', 'arrived', 'delivered'].includes(requestStatus);

    if (!window.confirm(isAcceptedBeforePayment
      ? (isAr ? 'هل تريد سحب العرض المقبول وإعادة الطلب للتاجر؟' : lang === 'zh' ? '确认撤回这个已接受报价，并让需求重新开放给买家吗？' : 'Withdraw this accepted offer and reopen the request for the trader?')
      : (isAr ? 'هل تريد إلغاء هذا العرض؟' : lang === 'zh' ? '确认取消这个报价吗？' : 'Cancel this offer?'))) return;

    // Stop if the withdrawal itself is refused (e.g. the order was paid in the
    // meantime): reopening the request or telling the trader would then be false.
    const { error: cancelError } = await sb.from('offers').update({ status: 'cancelled' }).eq('id', o.id);
    if (cancelError) {
      console.error('[DashboardSupplier] cancel offer failed:', cancelError);
      alert(isAr ? 'تعذّر سحب العرض. حدّث الصفحة وحاول مرة أخرى.' : lang === 'zh' ? '撤回报价失败，请刷新后重试。' : 'Could not withdraw the offer. Refresh and try again.');
      return;
    }
    if (isAcceptedBeforePayment) {
      await sb.from('requests').update({ status: 'open', shipping_status: 'open' }).eq('id', o.request_id);
    }

    const buyerId = o.requests?.buyer_id;
    if (buyerId) {
      const title = getTitle(o.requests);
      await sb.from('notifications').insert({
        user_id: buyerId,
        type: 'offer_cancelled',
        title_ar: isAcceptedBeforePayment
          ? `قام المورد بسحب العرض المقبول على طلبك: ${o.requests?.title_ar || title}`
          : `قام المورد بسحب عرضه على طلبك: ${o.requests?.title_ar || title}`,
        title_en: isAcceptedBeforePayment
          ? `Supplier withdrew the accepted offer on: ${o.requests?.title_en || title}`
          : `Supplier withdrew their offer on: ${o.requests?.title_en || title}`,
        title_zh: isAcceptedBeforePayment
          ? `供应商撤回了已接受的报价: ${o.requests?.title_zh || title}`
          : `供应商撤回了报价: ${o.requests?.title_zh || title}`,
        ref_id: o.request_id,
        is_read: false,
      });
      await sb.from('messages').insert({
        sender_id: user.id,
        receiver_id: buyerId,
        content: isAcceptedBeforePayment
          ? (isAr ? `قام المورد بسحب العرض المقبول على الطلب: ${title}` : lang === 'zh' ? `供应商撤回了已接受的报价：${title}` : `The supplier withdrew the accepted offer on: ${title}`)
          : (isAr ? `قام المورد بسحب عرضه على الطلب: ${title}` : lang === 'zh' ? `供应商撤回了报价：${title}` : `The supplier withdrew the offer on: ${title}`),
        is_read: false,
      });
    }

    loadMyOffers();
    loadPendingTracking();
    loadStats();
  };

  // Fire-and-forget, like every other enrichment on this page: a failure costs
  // the prefill and nothing else.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const terms = await loadDeliveryTerms(sb, user.id);
      if (cancelled) return;
      setDeliveryTerms(terms);
      setAskCapability(terms.length === 0);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const toggleOfferForm = (id) => {
    setOfferForms(prev => ({ ...prev, [id]: !prev[id] }));
    setOffers(prev => ({
      ...prev,
      // ═══ CORRECTED 2026-09-21 ═══════════════════════════════════════
      // This seed had drifted from the form it seeds, in three ways:
      //
      //  · `origin: 'China'` — submitOffer's comment says origin is "no
      //    longer defaulted to 'China'" (owner, 2026-09-19) because a stored
      //    value nobody chose is worse than a null on a field that travels
      //    onto a certificate of origin. But the DEFAULT WAS STILL HERE, so
      //    the form opened pre-filled with 'China' and submitOffer read it
      //    straight back. The rule was removed from the parser and left in
      //    the seed, which is where it actually took effect.
      //  · `days` — QuoteFields reads `leadTime`. `days` was seeding nothing.
      //  · `shippingCost` / `shippingMethod` — deprecated 20260919105244 and
      //    written by nothing since.
      //
      // Now exactly the shared quote-core shape, with 30 days' validity as
      // the owner's default.
      [id]: prev[id] || {
        price: '',
        currency: 'USD',   // USD only (2026-10-06), not the display currency
        moq: '',
        leadTime: '',
        origin: '',
        note: '',
        // What the TRADER asked for first (2026-09-28: door→DDP, port→CIF,
        // factory→EXW). Otherwise the supplier's capability, and only when
        // they have exactly one — two or more and they must choose per deal.
        priceBasis: prefillPriceBasis(
          requests.find((x) => x.id === id)?.delivery_preference,
          defaultBasisFor(deliveryTerms),
        ),
        validDays: 30,
        sampleAvailable: false,
        samplePrice: '',
        sampleDays: '',
      },
    }));
  };

  const submitOffer = async (requestId, buyerId) => {
    const o = offers[requestId] || {};
    const price = parseFloat(o.price);
    const days = parseInt(o.leadTime, 10);
    const moq = String(o.moq || '').trim();
    // No longer defaulted to 'China'. A stored value nobody chose is worse
    // than a null on a compliance field that travels onto a certificate of
    // origin (owner, 2026-09-19).
    const origin = String(o.origin || '').trim();
    const note = String(o.note || '').trim();
    // (Removed 2026-09-21) `requestItem` and `estimatedTotal`, computed here only to be put in
    // the new_offer email. The database sends that email now and computes the
    // total itself from requests.quantity, so a second implementation on the
    // client would be a second thing to keep in step. Est. Total remains a
    // computed DISPLAY in the form below, where it is rendered from state.

    // The SAME rule the public form runs — one core, one validation.
    const badField = validateQuoteCore({ price: o.price, leadTime: o.leadTime, priceBasis: o.priceBasis });
    if (badField || !moq) {
      alert(badField ? qt[badField]
        : (isAr ? 'أدخل الحد الأدنى للطلب' : lang === 'zh' ? '请填写最小起订量' : 'Enter the minimum order quantity'));
      return;
    }

    // Optimistic: close the form and mark the request as "sending" immediately.
    // We never claim success — the offer flips to its real state only after the
    // server confirms, and the form reopens (values intact) on error. No alert.
    toggleOfferForm(requestId);
    setSubmittingOfferId(null);
    setPendingOfferIds(prev => { const n = new Set(prev); n.add(requestId); return n; });
    const clearPending = () => setPendingOfferIds(prev => { const n = new Set(prev); n.delete(requestId); return n; });

    try {
      // Persist the capability before the quote, so it is answered once even
      // if this particular submit is refused. Best-effort by design.
      if (askCapability && deliveryTerms.length > 0) {
        await saveDeliveryTerms(sb, user.id, deliveryTerms);
      }

      const { data: existing, error: existingError } = await sb.from('offers').select('id').eq('request_id', requestId).eq('supplier_id', user.id).not('status', 'eq', 'cancelled').limit(1).maybeSingle();
      if (existingError) throw existingError;
      if (existing) {
        clearPending();
        alert(isAr ? 'لقد قدمت عرضاً على هذا الطلب مسبقاً' : lang === 'zh' ? '您已提交过此需求的报价' : 'You already submitted an offer on this request');
        return;
      }

      // USD only (owner, 2026-10-06); the server refuses anything else
      // (trg_offers_currency_usd). It used to be the supplier's display currency.
      const offerCurrency = 'USD';
      // Save the offer with the SOURCE note only — no dependency on the external
      // translator on this commercial path. A single-audience note is translated
      // on the buyer's side at read time (TranslatedText, cached).
      const { error } = await runWithOptionalColumns({
        table: 'offers',
        payload: {
          request_id: requestId,
          supplier_id: user.id,
          price,
          currency: offerCurrency,
          moq,
          delivery_days: days,
          origin: origin || null,
          note: note || null,
          status: 'pending',
          // The shared quote core — identical to what the public form writes,
          // so a supplier cannot price two ways depending on which door they
          // came through. shipping_cost / shipping_method are DEPRECATED
          // (20260919105244) and deliberately no longer written: price_basis
          // encodes who carries freight.
          price_basis: o.priceBasis || null,
          valid_days: o.validDays ? Number(o.validDays) : null,
          sample_available: o.sampleAvailable ?? null,
          sample_price: o.sampleAvailable && o.samplePrice !== '' && o.samplePrice != null ? Number(o.samplePrice) : null,
          sample_days: o.sampleAvailable && o.sampleDays !== '' && o.sampleDays != null ? Number.parseInt(o.sampleDays, 10) : null,
        },
        optionalKeys: ['origin', 'currency', 'price_basis', 'valid_days', 'sample_available', 'sample_price', 'sample_days'],
        execute: (nextPayload) => sb.from('offers').insert(nextPayload),
      });
      if (error) throw error;

      await sb.from('requests').update({ status: 'offers_received' }).eq('id', requestId).eq('status', 'open');
      // The in-app notification stays HERE. It is cheap, it is idempotent
      // enough, and an unverified supplier's offer still produces a dashboard
      // badge the trader can act on once it is revealed.
      sb.from('notifications').insert({ user_id: buyerId, type: 'new_offer', title_ar: 'وصلك عرض جديد على طلبك', title_en: 'You received a new offer', title_zh: '您收到了新报价', ref_id: requestId, is_read: false }).then(undefined, () => {});

      // ═══ THE EMAIL MOVED TO THE DATABASE, 2026-09-21 ═══════════════════
      // A fetch() to send-email used to sit here, firing the moment the row
      // was inserted. That was wrong in both directions at once:
      //
      //   · IT EMAILED TRADERS ABOUT OFFERS THEY COULD NOT SEE.
      //     guard_offer_write sets managed_visibility='admin_only' when the
      //     supplier is not verified — the COMMON case, since 'pending' is
      //     the most frequent live supplier status — and offers_select then
      //     hides the row. The trader got an email, opened the dashboard,
      //     and found nothing.
      //   · AND IT COVERED ONLY THIS DOOR. The public RFQ link sent no email
      //     ever; submit_factory_offer forces admin_only. Fixing the check
      //     here would have left three doors wrong, and the next door wrong
      //     by default.
      //
      // VISIBILITY IS THE EVENT, so the database owns it: trigger
      // trg_notify_buyer_offer_visible (migration 20260921125311) fires when
      // managed_visibility becomes 'buyer_visible' — at insert for a verified
      // supplier, or later when an admin approves them and
      // reveal_quotes_on_supplier_approval flips the column. One place, four
      // doors, and idempotent through offer_buyer_notifications.
      //
      // ⚠️ DO NOT ADD A CLIENT-SIDE new_offer SEND BACK HERE. 'new_offer' is
      // in SERVER_ONLY_TYPES in send-email, so it would 401 — and if it did
      // not, the trader would get the email twice.
      // ═══════════════════════════════════════════════════════════════════
      // Confirmed — reconcile: the real submitted offer replaces the "sending"
      // state, and NOW (after the server confirmed, never before) a clear,
      // non-blocking toast tells the supplier it was sent.
      await loadRequests();
      clearPending();
      // Held when the supplier's quotes don't reach traders (not verified and
      // no claimed factory): say so, rather than "sent" — guard_offer_write
      // saved it admin_only (golden rule 2).
      setOfferToast(supplierQuotesReachTraders(profile?.status, quotesReachTraders)
        ? (isAr ? '✓ تم إرسال عرضك' : lang === 'zh' ? '✓ 报价已发送' : '✓ Offer sent')
        : heldQuoteCopy('savedHeld', lang));
      setTimeout(() => setOfferToast(''), 2800);
    } catch (error) {
      console.error('submitOffer error:', error);
      clearPending();
      toggleOfferForm(requestId);   // reopen the form (values preserved) to retry
      alert(isOfferNoteContactError(error)
        ? offerNoteContactMessage(lang)
        : (isAr ? 'حدث خطأ أثناء إرسال العرض' : lang === 'zh' ? '发送报价时出错' : 'Error sending offer'));
    }
  };

  // Paid in full for this accepted offer: the payment rows say so, or the
  // request's own payment_pct (synced by telr-verify since 7ج) reaches 100.
  const isFullyPaidOffer = (o) => fullyPaidReqs.has(o.request_id) || Number(o.requests?.payment_pct) >= 100;

  // paid → ready_to_ship after a DEPOSIT: the goods are ready, the 70% balance is
  // now due (settled decision). Same write and notification as the app's
  // SupplierOffersScreen.markReadyToShip.
  const markReadyToShip = async (o) => {
    const { error } = await sb.from('requests').update({ status: 'ready_to_ship' }).eq('id', o.request_id);
    if (error) {
      console.error('[markReadyToShip] update failed:', error.code || '', error.message);
      alert(isAr ? 'تعذّر التحديث — حاول مرة أخرى' : lang === 'zh' ? '更新失败 — 请重试' : 'Could not update — try again');
      return;
    }
    if (o.requests?.buyer_id) {
      sb.from('notifications').insert({
        user_id: o.requests.buyer_id,
        type: 'ready_to_ship',
        title_ar: 'شحنتك جاهزة — ادفع الدفعة الثانية',
        title_en: 'Your shipment is ready — pay the 2nd installment',
        title_zh: '您的货物已准备就绪 — 请支付尾款',
        ref_id: o.request_id,
        is_read: false,
      }).then(undefined, (e) => console.error('[markReadyToShip] notification failed:', e?.message || e));
    }
    loadMyOffers();
  };

  // Tracking is offered ONLY once the order is paid in full (Phase 1,
  // 2026-09-28): at shipping (the balance was paid — telr-verify moved it
  // there), or at paid / ready_to_ship when the whole amount was paid up front.
  // It moves the status to shipping only when it is not there already. The
  // database guard (trg_requests_shipping_requires_full_payment) refuses an
  // unpaid shipment whatever the UI does; its refusal is shown, not swallowed.
  const submitTracking = async (requestId, buyerId, deliveryDays, currentStatus) => {
    const num = trackingInputs[requestId]; if (!num) return;
    const estimatedDelivery = deliveryDays
      ? new Date(Date.now() + (deliveryDays * 24 * 60 * 60 * 1000)).toISOString()
      : null;
    const moveToShipping = currentStatus !== 'shipping';
    const { error: trackErr } = await sb.from('requests').update({
      tracking_number: num,
      shipping_company: shippingCompany,
      ...(moveToShipping ? { status: 'shipping', shipping_status: 'shipping' } : {}),
      ...(estimatedDelivery ? { estimated_delivery: estimatedDelivery } : {}),
    }).eq('id', requestId);
    if (trackErr) {
      console.error('[submitTracking] update failed:', trackErr.code || '', trackErr.message);
      alert(/REQUEST_BALANCE_UNPAID/.test(trackErr.message || '')
        ? (isAr ? 'لا يمكن الشحن قبل سداد التاجر للدفعة الثانية.' : lang === 'zh' ? '买家付清尾款前无法发货。' : 'This order cannot ship until the trader pays the balance.')
        : (isAr ? 'تعذّر حفظ رقم التتبع — حاول مرة أخرى' : lang === 'zh' ? '无法保存追踪号 — 请重试' : 'Could not save the tracking number — try again'));
      return;
    }
    await sb.from('notifications').insert({ user_id: buyerId, type: 'shipped', title_ar: 'طلبك في الطريق — رقم التتبع: ' + num, title_en: 'Your order is on the way — Tracking: ' + num, title_zh: '您的订单已发货 — 跟踪号：' + num, ref_id: requestId, is_read: false });
    try {
      await fetch(SEND_EMAILS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          type: 'shipment_tracking',
          data: {
            recipientUserId: buyerId,
            name: 'Trader',
            trackingNumber: num,
            shippingCompany,
          },
        }),
      });
    } catch (e) { console.error('tracking email error:', e); }
    loadMyOffers(); loadPendingTracking();
  };

  // One rule, shared with the other four call sites. The hand-written version
  // this replaces had the right SHAPE but the wrong tail: an English viewer
  // fell back to Arabic before Chinese, so a factory reading the interface in
  // English saw Arabic on any request whose English title was missing.
  const getTitle = (item) => supplierRequestTitle(item, lang);

  const fmtDate = (d) => {
    if (!d) return '';
    const diff = Math.floor((Date.now() - new Date(d)) / 1000);
    if (diff < 3600)  return isAr ? Math.floor(diff / 60) + ' د'  : lang === 'zh' ? Math.floor(diff / 60) + '分钟前' : Math.floor(diff / 60) + 'm';
    if (diff < 86400) return isAr ? Math.floor(diff / 3600) + ' س' : lang === 'zh' ? Math.floor(diff / 3600) + '小时前' : Math.floor(diff / 3600) + 'h';
    return isAr ? Math.floor(diff / 86400) + ' ي' : lang === 'zh' ? Math.floor(diff / 86400) + '天前' : Math.floor(diff / 86400) + 'd';
  };

  // An ABSOLUTE date, for the offers deadline. fmtDate() above is relative
  // ("3d") and describes how long ago something happened — the opposite of
  // what a deadline needs, and unreadable as one.
  //
  // Copied deliberately from PublicRfqQuote.jsx:61-62, including both of its
  // decisions:
  //   · `ar-EG-u-ca-gregory-nu-latn` — Gregorian calendar, Latin digits. An
  //     Arabic locale left to itself can render a Hijri date, which is not the
  //     date the trader picked.
  //   · timeZone 'Asia/Riyadh', NOT the reader's. The deadline is stored as
  //     end-of-day Riyadh, so a factory in China (UTC+8) formatting locally
  //     reads every deadline as the following day — a guaranteed off-by-one
  //     against exactly the audience this modal serves.
  // requests.unit / requests.delivery_preference resolved to the viewer's
  // language. Both look the value up in the SAME array the request form wrote
  // it from, so an unknown key (an older row, or a value added to the DB before
  // the constant) falls through to the raw string rather than rendering blank.
  const unitLabelFor = (key) => {
    const def = REQUEST_UNITS.find((u) => u.key === key);
    return def ? labelFor(def, lang) : (key || '');
  };
  // The trader's terms as a supplier reads them — the shared builder
  // (2026-09-28), so this dashboard, /f/, /rfq and the app say the same words.
  const termValue = (req, key) => supplierTermRows(req, lang, { only: [key] })[0]?.value || '';
  const termsCopy = supplierTermsCopy(lang);

  const fmtDeadline = (v) => {
    if (!v) return '';
    const dateLocale = lang === 'zh' ? 'zh-CN' : isAr ? 'ar-EG-u-ca-gregory-nu-latn' : 'en-GB';
    return new Date(v).toLocaleDateString(dateLocale, {
      day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Riyadh',
    });
  };

  const relativeTime = (dateStr) => {
    if (!dateStr) return '';
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (diff < 3600)  return isAr ? `منذ ${Math.floor(diff / 60)} د`   : lang === 'zh' ? `${Math.floor(diff / 60)}分钟前` : `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return isAr ? `منذ ${Math.floor(diff / 3600)} س`  : lang === 'zh' ? `${Math.floor(diff / 3600)}小时前` : `${Math.floor(diff / 3600)}h ago`;
    const days = Math.floor(diff / 86400);
    if (days < 30)    return isAr ? `منذ ${days} ي`                      : lang === 'zh' ? `${days}天前` : `${days}d ago`;
    return isAr ? `منذ ${Math.floor(days / 30)} ش` : lang === 'zh' ? `${Math.floor(days / 30)}个月前` : `${Math.floor(days / 30)}mo ago`;
  };

  const SUPPLIER_STEP_MAP = {
    closed: 1, supplier_confirmed: 2,
    paid: 3, ready_to_ship: 4,
    shipping: 4, arrived: 5, delivered: 6,
  };
  const SUPPLIER_STEPS_AR = ['قدّمت العرض', 'العرض قُبل', 'تأكيد الجاهزية', 'الإنتاج', 'الشحن', 'مكتمل'];
  const SUPPLIER_STEPS_EN = ['Offer Sent', 'Accepted', 'Ready', 'Production', 'Shipping', 'Done'];
  const SUPPLIER_STEPS_ZH = ['报价已提交', '已接受', '确认就绪', '生产中', '运输中', '已完成'];

  const SupplierStatusTimeline = ({ step }) => {
    const steps = lang === 'zh' ? SUPPLIER_STEPS_ZH : isAr ? SUPPLIER_STEPS_AR : SUPPLIER_STEPS_EN;
    const SAGE = 'var(--green)';
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
        {steps.map((label, i) => {
          const done    = i < step;
          const current = i === step;
          const future  = i > step;
          const dotColor   = done ? SAGE : current ? 'var(--text-primary)' : 'var(--border)';
          const leftLine   = i <= step ? SAGE : 'var(--border)';
          const rightLine  = i < step  ? SAGE : 'var(--border)';
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 46 }}>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {i > 0 && <div style={{ flex: 1, height: 1, background: leftLine }} />}
                <div style={{
                  width: 8, height: 8, borderRadius: 'var(--radius-full)', background: dotColor, flexShrink: 0,
                  ...(current ? { borderColor: 'var(--border-strong)' } : {}),
                }} />
                {i < steps.length - 1 && <div style={{ flex: 1, height: 1, background: rightLine }} />}
              </div>
              <p style={{
                fontSize: 9, marginTop: 5, textAlign: 'center', whiteSpace: 'nowrap',
                color: future ? 'var(--text-disabled)' : done ? SAGE : 'var(--text-primary)',
                fontWeight: current ? 600 : done ? 500 : 400,
                fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
              }}>{label}</p>
            </div>
          );
        })}
      </div>
    );
  };

  const SupplierPaymentPills = ({ requestStatus, paymentPlan }) => {
    // CHANGED 2026-09-28: no invented plan. This defaulted a missing plan to
    // 50 — a value nobody chose (and no longer offered). Unknown → no pills.
    const plan = Number(paymentPlan);
    if (!(plan > 0 && plan <= 100)) return null;
    const rest = 100 - plan;
    const tf = 'var(--font-ar)';

    // State helpers
    const inst1State = (() => {
      if (['paid','ready_to_ship','shipping','arrived','delivered'].includes(requestStatus)) return 'black';
      return 'amber'; // closed, supplier_confirmed
    })();
    const inst2State = (() => {
      if (['arrived','delivered'].includes(requestStatus)) return 'black';
      if (requestStatus === 'shipping') return 'amber';
      return 'grey'; // closed, supplier_confirmed, paid, ready_to_ship
    })();

    const PILL_STYLES = {
      black: { background: 'var(--text-primary)', color: 'var(--bg-base)',           border: '1px solid var(--text-primary)' },
      amber: { background: 'transparent',         color: 'var(--warn)',                  border: '1px solid rgba(160,136,80,0.40)' },
      grey:  { background: 'transparent',         color: 'var(--text-tertiary)',      border: '1px solid var(--border-subtle)' },
    };

    const stateLabel = (state) => {
      if (state === 'black') return isAr ? 'محوّلة لك'         : lang === 'zh' ? '已转账'     : 'Transferred';
      if (state === 'amber') return isAr ? 'في انتظار التاجر'  : lang === 'zh' ? '等待买家'   : 'Awaiting Buyer';
      return                         isAr ? 'بعد الشحن'        : lang === 'zh' ? '发货后'     : 'After Shipping';
    };

    const Pill = ({ pct, state, suffix }) => (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontSize: 10, padding: '3px 10px', borderRadius: 'var(--radius-pill)',
        fontFamily: isAr ? tf : 'var(--font-sans)',
        letterSpacing: isAr ? 0 : 0.3,
        ...PILL_STYLES[state],
      }}>
        <span style={{ fontVariantNumeric: 'lining-nums' }}>{pct}٪</span>
        <span style={{ opacity: 0.55 }}>{suffix}</span>
        <span>·</span>
        <span>{stateLabel(state)}</span>
      </span>
    );

    const suffix1 = isAr ? 'مقدم'      : lang === 'zh' ? '定金'   : 'upfront';
    const suffix2 = isAr ? 'عند الشحن' : lang === 'zh' ? '发货后' : 'on ship';

    return (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        <Pill pct={plan} state={inst1State} suffix={suffix1} />
        {/* A full (100%) plan has no second instalment — no "0% on ship". */}
        {rest > 0 && <Pill pct={rest} state={inst2State} suffix={suffix2} />}
      </div>
    );
  };

  const name = profile?.company_name || profile?.full_name || user?.email?.split('@')[0];
  const supplierMaabarId = getSupplierMaabarId(profile || {});
  const pendingCount = pendingTracking.length + rejectedOffers.length;
  const closedOffers = myOffers.filter(o => o.status === 'accepted' && o.requests?.status === 'closed');
  const paidOffers   = myOffers.filter(o => o.status === 'accepted' && o.requests?.status === 'paid');

  const arFont = { fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' };
  const section = { animation: 'fadeIn 0.35s ease' };
  const verificationStatusHeadline = supplierState.isApprovedStage
    ? (isAr ? 'تم اعتماد حسابك كمورد موثّق' : lang === 'zh' ? '您的账户已通过认证' : 'Your supplier account is verified')
    : supplierState.isUnderReviewStage
      ? (isAr ? 'التحقق الآن تحت المراجعة' : lang === 'zh' ? '认证资料正在审核中' : 'Your verification is now under review')
      : (isAr ? 'أكمل ملف الشركة ثم أرسل التحقق' : lang === 'zh' ? '先完善公司资料，再提交认证' : 'Complete your company profile, then submit verification');
  const verificationStatusBody = supplierState.isApprovedStage
    ? (isAr ? 'تم فتح الوصول الكامل للمورد داخل مَعبر. يمكنك الآن استخدام الطلبات والمنتجات والعروض وباقي الإجراءات الأساسية.' : lang === 'zh' ? '完整供应商能力现已解锁，您可以开始使用需求、产品、报价和核心操作。' : 'The full supplier experience is now unlocked. You can use requests, products, offers, and core actions.')
    : supplierState.isUnderReviewStage
      ? (isAr ? 'تم استلام التحقق بنجاح. لن يظهر النموذج القابل للتعديل مرة أخرى أثناء المراجعة، وستظل الإجراءات الأساسية مقفلة حتى اعتماد الحساب.' : lang === 'zh' ? '认证资料已成功提交。审核期间不会再次显示可编辑表单，核心操作会保持锁定直到通过审核。' : 'Your verification was submitted successfully. The editable form stays locked during review, and core actions remain blocked until approval.')
      : (isAr ? 'رحلتك الآن: احفظ ملف الشركة، أرفق مستندات التحقق، ثم انتظر المراجعة. عند الموافقة سيظهر Badge المورّد الموثّق وتفتح المزايا الأساسية.' : lang === 'zh' ? '当前路径：先完善公司资料，再上传认证文件并提交，随后等待审核。通过后会显示认证徽章并解锁核心能力。' : 'Your current path: finish the company profile, upload verification documents, submit once, then wait for review. After approval, a verified badge appears and core features unlock.');
  const settingsCtaLabel = supplierState.isUnderReviewStage || supplierState.isApprovedStage
    ? (isAr ? 'حفظ تحديثات الملف' : lang === 'zh' ? '保存资料更新' : 'Save profile updates')
    : profileReadiness.isReadyForVerification
      ? (isAr ? 'احفظ وانتقل للخطوة التالية' : lang === 'zh' ? '保存并进入下一步' : 'Save and go to next step')
      : (isAr ? 'أكمل ملف الشركة' : lang === 'zh' ? '完成公司资料' : 'Complete company profile');
  const settingsPrimaryButtonLabel = savingSettings
    ? t.saving
    : isSettingsDirty
      ? settingsCtaLabel
      : hasSavedSettingsRecord
        ? (isAr ? 'تم الحفظ' : lang === 'zh' ? '已保存' : 'Saved')
        : settingsCtaLabel;
  const settingsSecondaryButtonLabel = savingSettings
    ? t.saving
    : isSettingsDirty
      ? t.saveSettings
      : hasSavedSettingsRecord
        ? (isAr ? 'تم حفظ الملف' : lang === 'zh' ? '资料已保存' : 'Profile saved')
        : t.saveSettings;
  const payoutButtonLabel = savingPayout
    ? t.saving
    : isPayoutDirty
      ? t.savePayout
      : hasSavedPayoutRecord
        ? (isAr ? 'تم حفظ الدفعات' : lang === 'zh' ? '收款资料已保存' : 'Payout saved')
        : t.savePayout;
  const openVerificationFlow = () => {
    setActiveTab('verification');
    // Only adjust the step when navigating from a different tab.
    // Never decrease the current step — the supplier may already be further along
    // (e.g. at step 3 review). Also cap at 2 so we don't skip the review screen.
    if (!isVerificationLocked && activeTab !== 'verification') {
      const target = Math.min(verificationProgress.firstIncompleteStep, 2);
      setVerificationStep(prev => Math.max(prev, target));
    }
  };

  return (
    <div className="dashboard-wrap">

      {/* Non-blocking "offer sent" toast — shown only after the server confirms. */}
      {offerToast && (
        <div role="status" style={{
          position: 'fixed', bottom: 92, left: '50%', transform: 'translateX(-50%)', zIndex: 1000,
          background: 'var(--green, #2d7a4f)', color: '#fff', padding: '11px 20px', borderRadius: 'var(--radius-pill, 999px)',
          fontSize: 13.5, fontWeight: 600, boxShadow: '0 6px 20px rgba(0,0,0,0.18)', maxWidth: '90vw', textAlign: 'center',
          fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', animation: 'fadeInDown 0.25s ease',
        }}>
          {offerToast}
        </div>
      )}

      <SupplierHeader
        lang={lang} setLang={setLang}
        companyName={name}
        subtitle={profile?.speciality || profile?.city || ''}
        avatarUrl={settings?.avatar_url || profile?.avatar_url}
        onProfile={() => setActiveTab('settings')}
        onVerification={() => nav('/supplier/verify')}
        onBell={() => nav('/inbox')}
        onLogout={async () => { try { await sb.auth.signOut(); } catch { /* ignore */ } window.location.href = '/'; }}
      />

      {showOnboardingSequence && (
        <SupplierOnboardingSequence
          user={user}
          profile={profile}
          lang={lang}
          supplierMaabarId={supplierMaabarId}
          setProfile={setProfile}
          onComplete={() => { /* parent state already updated via setProfile inside the sequence */ }}
          onNavigateToTab={(target) => setActiveTab(target)}
        />
      )}

      {/* ══════════════════════════════════════
          HEADER
      ══════════════════════════════════════ */}
      <div className="dash-header-pad" style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border-subtle)' }}>
        {/* Redundant on mobile (SupplierHeader + SupplierHomePanel cover it); kept for desktop until C10. */}
        <div className="sup-hdr-identity">
        <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 20, fontWeight: 500 }}>{t.tag}</p>
        <h1 style={{ fontSize: isAr ? 34 : 40, fontWeight: 300, ...arFont, color: 'var(--text-primary)', letterSpacing: isAr ? 0 : -1, lineHeight: 1.2, marginBottom: 10 }}>
          {t.welcome} {name}
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-tertiary)', marginBottom: 16, lineHeight: 1.7, ...arFont, maxWidth: 420 }}>{t.desc}</p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
          {/* Unverified reads the same here as it does on the card below: one signal,
              two places. The neutral grey pill said "Verification required" in the
              same tone it would have said "Riyadh" — a field, not a warning. */}
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px',
            borderRadius: 'var(--radius-chip)', fontSize: 11,
            border: headerNeedsAction ? '1px solid #8E1F24' : '1px solid var(--border-subtle)',
            background: headerNeedsAction ? '#8E1F24' : 'var(--bg-subtle)',
            color: headerNeedsAction ? '#FFF' : 'var(--text-secondary)',
          }}>
            {headerNeedsAction && (
              <span aria-hidden="true" style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 15, height: 15, borderRadius: '50%', border: '1px solid rgba(255,255,255,.7)',
                fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 10, lineHeight: 1, paddingBottom: 1,
              }}>!</span>
            )}
            <span style={{ color: headerNeedsAction ? 'rgba(255,255,255,.75)' : 'var(--text-disabled)' }}>{t.supplierStageLabel}</span>
            <strong style={{ color: headerNeedsAction ? '#FFF' : 'var(--text-primary)', fontWeight: 700 }}>{getSupplierStageLabel(supplierState.stage, lang)}</strong>
          </span>
          {headerNeedsAction && (
            <button
              onClick={() => nav('/supplier/verify')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                borderRadius: 'var(--radius-chip)', border: '1px solid #EFC9C6', background: '#FDF1F0',
                color: '#8E1F24', fontSize: 11, fontWeight: 700, cursor: 'pointer', ...arFont,
              }}
            >
              {isAr ? 'غير موثّق — وثّق الآن ←' : lang === 'zh' ? '未认证 — 立即认证 →' : 'Unverified — verify now →'}
            </button>
          )}
          {supplierState.isApprovedStage && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 'var(--radius-chip)', border: '1px solid rgba(45,122,79,0.22)', background: 'rgba(45,122,79,0.10)', color: 'var(--green)', fontSize: 11 }}>
              <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{isAr ? 'مورد موثّق' : lang === 'zh' ? '认证供应商' : 'Verified supplier'}</strong>
            </span>
          )}
          {supplierMaabarId && supplierState.isApprovedStage && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 'var(--radius-chip)', border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--text-secondary)', fontSize: 11 }}>
              <span style={{ color: 'var(--text-secondary)' }}>{t.supplierIdLabel}</span>
              <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{supplierMaabarId}</strong>
            </span>
          )}
        </div>
        </div>

        {/* Desktop: the SAME four places as the phone tab bar (lib/navItems.js),
            then «المزيد» for the rest — it used to be all 13 tabs in one faint
            row (owner, 2026-10-20: «ما أعرف وين أروح»). «إضافة منتج» is not a
            place: it is the button inside منتجاتي, which stays selected for it. */}
        {(() => {
          const primary = SUPPLIER_PRIMARY_TABS.map((id) => tabs.find((tb) => tb.id === id)).filter(Boolean);
          const rank = (id) => { const i = SUPPLIER_MORE_TABS.indexOf(id); return i < 0 ? 99 : i; };
          const secondary = tabs.filter((tb) => !SUPPLIER_PRIMARY_TABS.includes(tb.id) && tb.id !== 'add-product')
            .sort((a, b) => rank(a.id) - rank(b.id));
          const primaryLabel = { overview: navLabel('supHome', lang), requests: navLabel('supRequests', lang), 'my-products': navLabel('supProducts', lang), messages: navLabel('messages', lang) };
          const isActive = (tab) => activeTab === tab.id || (tab.id === 'my-products' && activeTab === 'add-product');
          const activeSecondary = secondary.find((tb) => tb.id === activeTab);
          const secondaryBadge = secondary.some((tb) => tb.badge);
          const tabStyle = (active, locked) => ({
            padding: '10px 18px', background: 'none', border: 'none',
            borderBottom: active ? '2px solid var(--text-primary)' : '2px solid transparent',
            color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
            fontWeight: active ? 600 : 400,
            opacity: locked ? 0.72 : 1,
            fontSize: 13.5, cursor: 'pointer', transition: 'all 0.2s', position: 'relative',
            ...arFont, letterSpacing: 0, whiteSpace: 'nowrap', minHeight: 44,
          });
          const Badge = ({ v }) => (
            <span style={{ position: 'absolute', top: 6, insetInlineEnd: 2, background: 'rgba(224,92,92,0.10)', border: '1px solid rgba(224,92,92,0.28)', color: 'var(--red)', fontSize: 8, fontWeight: 700, borderRadius: 'var(--radius-full)', minWidth: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{v}</span>
          );
          const Locked = () => (
            <span style={{ marginInlineStart: 6, fontSize: 10, color: 'var(--text-tertiary)' }}>{isAr ? 'مقفل' : lang === 'zh' ? '锁定' : 'Locked'}</span>
          );
          return (
            <div className="desktop-tab-row" data-supplier-tabs="1">
              {primary.map((tab) => {
                const tabLocked = lockedTabIds.includes(tab.id);
                return (
                  <button key={tab.id} data-tab={tab.id} onClick={() => { setDesktopMoreOpen(false); if (tab.to) nav(tab.to); else setActiveTab(tab.id); }} style={tabStyle(isActive(tab), tabLocked)}>
                    {primaryLabel[tab.id] || tab.label}
                    {tabLocked && !tab.badge && <Locked />}
                    {tab.badge && <Badge v={tab.badge} />}
                  </button>
                );
              })}
              {secondary.length > 0 && (
                <div style={{ position: 'relative' }}>
                  <button data-tab="more" aria-expanded={desktopMoreOpen} aria-haspopup="menu" onClick={() => setDesktopMoreOpen((o) => !o)} style={tabStyle(!!activeSecondary, false)}>
                    {navLabel('more', lang)}{activeSecondary ? ` · ${activeSecondary.label}` : ''} ▾
                    {secondaryBadge && !activeSecondary && <Badge v="•" />}
                  </button>
                  {desktopMoreOpen && (
                    <>
                      <div onClick={() => setDesktopMoreOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
                      <div role="menu" style={{ position: 'absolute', top: '100%', insetInlineStart: 0, zIndex: 31, minWidth: 230, background: 'var(--bg-raised, #fff)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card, 14px)', boxShadow: '0 10px 30px rgba(26,24,20,.10)', padding: 6 }}>
                        {secondary.map((tab) => {
                          const tabLocked = lockedTabIds.includes(tab.id);
                          return (
                            <button key={tab.id} role="menuitem" data-tab={tab.id} onClick={() => { setDesktopMoreOpen(false); if (tab.to) nav(tab.to); else setActiveTab(tab.id); }}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%', padding: '11px 12px', background: activeTab === tab.id ? 'var(--bg-subtle)' : 'none', border: 'none', borderRadius: 10, cursor: 'pointer', color: 'var(--text-primary)', fontSize: 14, textAlign: isAr ? 'right' : 'left', opacity: tabLocked ? 0.72 : 1, ...arFont }}>
                              <span>{tab.label}{tabLocked && <Locked />}</span>
                              {tab.badge && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--red)' }}>{tab.badge}</span>}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Mobile: contextual sub-tabs driven by active bottom tab */}
        {(() => {
          const subTabStyle = (active) => ({
            padding: '10px 16px', background: 'none', border: 'none',
            borderBottom: active ? '1px solid var(--text-primary)' : '1px solid transparent',
            color: active ? 'var(--text-primary)' : 'var(--text-disabled)',
            fontSize: 11, cursor: 'pointer', transition: 'all 0.2s',
            ...arFont, letterSpacing: lang === 'zh' ? 0 : 1.4,
            textTransform: lang === 'zh' ? 'none' : 'uppercase',
            whiteSpace: 'nowrap', minHeight: 44,
          });
          if (activeBottomTab === 'requests') return (
            <div className="mobile-sub-tabs">
              {[
                { id: 'all',        label: isAr ? 'الكل' : lang === 'zh' ? '全部' : 'All' },
                { id: 'open',       label: isAr ? 'مفتوح' : lang === 'zh' ? '开放中' : 'Open' },
                { id: 'closed',     label: isAr ? 'مغلق' : lang === 'zh' ? '已关闭' : 'Closed' },
                { id: 'high_value', label: isAr ? 'قيمة عالية' : lang === 'zh' ? '高价值' : 'High Value' },
              ].map(s => (
                <button key={s.id} style={subTabStyle(requestStatusFilter === s.id)} onClick={() => setRequestStatusFilter(s.id)}>{s.label}</button>
              ))}
            </div>
          );
          if (activeBottomTab === 'products') return (
            <div className="mobile-sub-tabs">
              {[
                { id: 'all',    label: isAr ? 'الكل' : lang === 'zh' ? '全部' : 'All' },
                { id: 'active', label: isAr ? 'نشط' : lang === 'zh' ? '上架' : 'Active' },
                { id: 'draft',  label: isAr ? 'موقوف' : lang === 'zh' ? '下架' : 'Draft' },
              ].map(s => (
                <button key={s.id} style={subTabStyle(productStatusFilter === s.id)} onClick={() => { setProductStatusFilter(s.id); setActiveTab('my-products'); }}>{s.label}</button>
              ))}
              <button style={subTabStyle(activeTab === 'add-product')} onClick={() => setActiveTab('add-product')}>
                {isAr ? '+ إضافة' : lang === 'zh' ? '+ 添加产品' : '+ Add Product'}
              </button>
            </div>
          );
          // No messages sub-tabs: setActiveBottomTab('messages') no longer exists,
          // so this branch had become unreachable when the bottom-nav item
          // started navigating to /inbox instead of switching panes.
          return null;
        })()}
      </div>

      {/* ══════════════════════════════════════
          CONTENT
      ══════════════════════════════════════ */}
      <div style={{ background: 'var(--bg-base)', minHeight: 'calc(var(--app-dvh) - 280px)' }}>
        <div className="dash-content">

          {isRestrictedSupplierTab && (
            <div style={{ ...section, maxWidth: 760 }}>
              <SupplierPayoutLockBanner
                lang={lang}
                actions={<>
                  <button onClick={openVerificationFlow} className="btn-dark-sm" style={{ fontSize: 11, minHeight: 36 }}>
                    {supplierState.isUnderReviewStage ? (isAr ? 'عرض حالة التحقق' : lang === 'zh' ? '查看认证状态' : 'View verification status') : t.verificationCtaAction}
                  </button>
                  <button onClick={() => setActiveTab('overview')} className="btn-outline" style={{ fontSize: 11, minHeight: 36 }}>
                    {isAr ? 'العودة للوحة المورد' : lang === 'zh' ? '返回控制台' : 'Back to dashboard'}
                  </button>
                </>}
              />
            </div>
          )}

          {/* ── OVERVIEW ── */}
          {activeTab === 'overview' && (
            <div style={section}>
              {ownerFactory && factoryBanner(bannerLabels.catalog, ownerFacProducts)}
              {ownerFactory && ownerFacMsgs > 0 && factoryBanner(bannerLabels.msgs, ownerFacMsgs, 'messages')}
              {ownerFactory && ownerFacInvites > 0 && factoryBanner(bannerLabels.invites, ownerFacInvites, 'messages')}
              <ExclusiveSellerBanner lang={lang} onAddProduct={() => setActiveTab('add-product')} />
              <ExclusiveCategoriesPanel lang={lang} supplierId={user.id} />
              <SupplierHomePanel
                sb={sb} supplierId={user.id} lang={lang} companyName={name}
                offersCount={stats.offers} productsCount={stats.products}
                messagesCount={stats.messages} inquiriesCount={stats.productInquiries}
                needsVerification={needsVerification}
                profile={profile}
                showVerificationCard={showVerificationCard}
                isUnderReview={supplierState.isUnderReviewStage}
                onOpenRequests={() => setActiveTab('requests')}
                onOpenMessages={() => nav('/inbox')}
                onOpenOffers={() => setActiveTab('offers')}
                onOpenProducts={() => setActiveTab('my-products')}
                onOpenInquiries={() => setActiveTab('product-inquiries')}
                // The light (stage-1) bar — licence + contact + category — NOT
                // the eight-field payout file. This card is what a new
                // supplier is pushed by, and it used to push them at the
                // wrong threshold entirely.
                onVerify={() => nav('/supplier/verify')}
              />
              {/* Secondary promos below the work (decision #2: home opens on the work). */}
              <div style={{ marginTop: 28 }}>
                <ConnectFactoryPrompt user={user} lang={lang} onLinked={() => window.location.reload()} />
                {SHOW_SUPPLIER_REFERRALS && profile.maabar_supplier_id && (
                  <div onClick={() => setActiveTab('wallet')} style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #FCF8F0, #F7EDD6)', border: '1px solid rgba(176,141,46,.5)', borderRadius: 16, padding: '16px 18px', marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#8A6D1E', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                        {isAr ? '💰 ابدأ تكسب ‏$60 عن كل مورد تدعوه' : lang === 'zh' ? '💰 每邀请一位供应商赚取 $60' : '💰 Earn $60 for every supplier you invite'}
                      </p>
                      <p style={{ margin: '5px 0 0', fontSize: 13, color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                        {isAr ? 'شارك كودك الآن — التفاصيل في المحفظة' : lang === 'zh' ? '立即分享您的推荐码 — 详情见钱包' : 'Share your code now — details in your wallet'}
                      </p>
                    </div>
                    <span className="btn-primary" style={{ padding: '9px 16px', fontSize: 13, whiteSpace: 'nowrap' }}>
                      {isAr ? 'شارك واربح ←' : lang === 'zh' ? '分享赚钱 →' : 'Share & earn →'}
                    </span>
                  </div>
                )}
              </div>
              {/* Legacy overview retained (dead) for rollback; superseded by SupplierHomePanel. */}
              {false && (isOnboardingLimited ? (
                <>
                  {showFirstProductNudge && (
                    <div style={{ marginBottom: 24, padding: '20px 24px', background: 'linear-gradient(135deg, #FCF8F0, #F1F7F1)', border: '1px solid rgba(45,122,79,0.28)', borderRadius: 'var(--radius-xl)' }}>
                      <p style={{ fontSize: 10, letterSpacing: 2.8, textTransform: 'uppercase', color: '#2d7a4f', marginBottom: 8, fontWeight: 600, ...arFont }}>
                        {isAr ? 'ابدأ الآن' : lang === 'zh' ? '开始' : 'Get started'}
                      </p>
                      <h3 style={{ fontSize: isAr ? 19 : 20, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 8, ...arFont }}>
                        {isAr ? 'تقدر تضيف منتجاتك من الآن — بدون انتظار التوثيق'
                          : lang === 'zh' ? '现在就可以上架产品 — 无需先完成认证'
                          : 'You can start listing products right away — no need to finish verification first'}
                      </h3>
                      <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: 16, ...arFont }}>
                        {isAr ? 'منتجاتك تبقى مخفية عن المشترين حتى يكتمل توثيقك، فابدأ بتجهيزها الآن.'
                          : lang === 'zh' ? '在您通过认证前，产品对买家不可见 — 所以现在就可以先准备好。'
                          : "Your products stay hidden from buyers until you're verified — so you can prepare them now."}
                      </p>
                      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button onClick={() => setActiveTab('add-product')} className="btn-dark-sm" style={{ fontSize: 12, minHeight: 38 }}>
                          {isAr ? 'أضف أول منتج' : lang === 'zh' ? '添加第一个产品' : 'Add your first product'}
                        </button>
                        <button onClick={dismissFirstProductNudge} style={{ background: 'none', border: 'none', color: 'var(--text-disabled)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline', ...arFont }}>
                          {isAr ? 'لاحقاً' : lang === 'zh' ? '稍后' : 'Skip for now'}
                        </button>
                      </div>
                    </div>
                  )}
                  <div style={{ marginBottom: 24, padding: '24px 26px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div style={{ maxWidth: 720 }}>
                        <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 10, fontWeight: 500 }}>
                          {supplierState.status}
                        </p>
                        <h2 style={{ fontSize: isAr ? 28 : 36, fontWeight: 300, marginBottom: 12, color: 'var(--text-primary)', ...arFont, letterSpacing: isAr ? 0 : -0.6 }}>
                          {verificationStatusHeadline}
                        </h2>
                        <p style={{ fontSize: 13, color: 'var(--text-disabled)', lineHeight: 1.8, marginBottom: 16, ...arFont }}>
                          {verificationStatusBody}
                        </p>
                      </div>
                      <div style={{ minWidth: 220, maxWidth: 280, flex: '1 1 220px' }}>
                        <p style={{ fontSize: 11, color: 'var(--text-disabled)', marginBottom: 8, ...arFont }}>{t.onboardingProgress}</p>
                        <p style={{ fontSize: 32, color: 'var(--text-primary)', marginBottom: 6 }}>{profileReadiness.completedRequiredCount}/{profileReadiness.totalRequiredCount}</p>
                        <div style={{ width: '100%', height: 8, borderRadius: 'var(--radius-chip)', background: 'var(--border)', overflow: 'hidden', marginBottom: 10 }}>
                          <div style={{ width: `${supplierState.isUnderReviewStage || supplierState.isApprovedStage ? 100 : profileReadiness.progressPercent}%`, height: '100%', background: 'var(--text-primary)' }} />
                        </div>
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7, ...arFont }}>
                          {(supplierState.isUnderReviewStage || supplierState.isApprovedStage ? 100 : profileReadiness.progressPercent)}% · {supplierState.isUnderReviewStage ? (isAr ? 'بانتظار قرار فريق مَعبر' : lang === 'zh' ? '等待 Maabar 团队审核结果' : 'Waiting for Maabar review') : supplierState.isApprovedStage ? (isAr ? 'تم فتح الوصول الكامل' : lang === 'zh' ? '完整访问已解锁' : 'Full access unlocked') : t.onboardingVerificationReady}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 24 }}>
                    <SupplierJourneyStepper steps={supplierJourneySteps} isAr={isAr} lang={lang} />
                  </div>

                  <div style={{ padding: '22px 24px', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-xl)', marginBottom: 24 }}>
                    <p style={{ fontSize: 10, letterSpacing: 2.8, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 10 }}>{isAr ? 'الخطوة التالية' : lang === 'zh' ? '下一步' : 'Next step'}</p>
                    <h3 style={{ fontSize: 20, fontWeight: 400, color: 'var(--text-primary)', marginBottom: 8, ...arFont }}>
                      {supplierState.isUnderReviewStage
                        ? (isAr ? 'تم إرسال التحقق — راقب حالة المراجعة' : lang === 'zh' ? '认证已提交 — 查看审核状态' : 'Verification submitted — follow the review status')
                        : verificationProgress.firstIncompleteStep === 1
                          ? (isAr ? 'ابدأ بحفظ ملف الشركة ثم انتقل لملفات التحقق' : lang === 'zh' ? '先保存公司资料，再进入认证文件' : 'Save the company profile first, then continue to verification files')
                          : verificationProgress.firstIncompleteStep === 2
                            ? (isAr ? 'أكمل ملفات التحقق ثم راجع الطلب' : lang === 'zh' ? '补全认证文件，然后进入最终确认' : 'Complete the verification files, then review the request')
                            : (isAr ? 'كل شيء جاهز — افتح المراجعة النهائية قبل الإرسال' : lang === 'zh' ? '资料已齐全 — 先打开最终确认，再提交' : 'Everything is ready — open the final review before submitting')}
                    </h3>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: 16, ...arFont }}>
                      {supplierState.isUnderReviewStage
                        ? (isAr ? 'النموذج مقفل الآن حتى ينتهي فريق مَعبر من المراجعة.' : lang === 'zh' ? '表单在审核完成前会保持锁定。' : 'The form stays locked while the Maabar team reviews your submission.')
                        : verificationProgress.firstIncompleteStep === 1
                          ? (isAr ? 'حفظ الخطوة الأولى يحدّث ملف الشركة فقط، ثم يفتح لك الخطوة الثانية داخل نفس المسار.' : lang === 'zh' ? '保存第 1 步只会更新公司资料，然后会在同一路径中打开第 2 步。' : 'Saving step 1 only updates the company profile, then opens step 2 inside the same flow.')
                          : verificationProgress.firstIncompleteStep === 2
                            ? (isAr ? 'لن يتم إرسال أي شيء بعد. الإرسال الحقيقي يحصل فقط من شاشة المراجعة النهائية.' : lang === 'zh' ? '现在还不会提交。只有在最终确认页点击最后按钮时才会正式送审。' : 'Nothing is submitted yet. The real submission only happens from the final review screen.')
                            : (isAr ? 'راجع الملخص النهائي ثم اضغط زر الإرسال الأخير مرة واحدة.' : lang === 'zh' ? '请先查看最终摘要，再点击最后的提交按钮。' : 'Open the summary screen, check everything, then use the one final submit button.')}
                    </p>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button onClick={openVerificationFlow} className="btn-dark-sm" style={{ fontSize: 11, minHeight: 36 }}>
                        {supplierState.isUnderReviewStage ? (isAr ? 'عرض حالة التحقق' : lang === 'zh' ? '查看认证状态' : 'View verification status') : t.verificationCtaAction}
                      </button>
                      {!supplierState.isUnderReviewStage && (
                        <button onClick={() => setActiveTab('settings')} className="btn-outline" style={{ fontSize: 11, minHeight: 36 }}>
                          {t.onboardingGoSettings}
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={{ padding: '22px 24px', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-xl)', marginBottom: 24 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8, ...arFont }}>
                      {isAr ? 'يمكنك البدء الآن — التحقق ليس شرطاً' : lang === 'zh' ? '您现在就可以开始 — 无需先认证' : 'You can start now — verification isn’t required'}
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: 14, ...arFont }}>
                      {isAr ? 'قدّم عروضك، راسل المشترين، وأضِف منتجاتك من الآن. التحقق مطلوب فقط لاستلام المدفوعات.' : lang === 'zh' ? '现在即可报价、与买家沟通并添加产品。仅在收款时才需要完成认证。' : 'Send offers, message buyers and add products right away. Verification is only needed to receive payouts.'}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                      {[
                        isAr ? 'الطلبات' : lang === 'zh' ? '需求' : 'Requests',
                        isAr ? 'المنتجات' : lang === 'zh' ? '产品' : 'Products',
                        isAr ? 'العروض' : lang === 'zh' ? '报价' : 'Offers',
                        isAr ? 'الرسائل' : lang === 'zh' ? '消息' : 'Messages',
                        isAr ? 'العينات' : lang === 'zh' ? '样品' : 'Samples',
                      ].map((item) => (
                        <span key={item} style={{ padding: '6px 10px', borderRadius: 'var(--radius-chip)', border: '1px solid rgba(45,122,79,0.2)', background: 'rgba(45,122,79,0.06)', color: 'var(--green)', fontSize: 11, letterSpacing: 0.3 }}>
                          {item} · {isAr ? 'متاح' : lang === 'zh' ? '已开放' : 'Available'}
                        </span>
                      ))}
                    </div>
                    <button onClick={() => setActiveTab('requests')} className="btn-dark-sm" style={{ fontSize: 11, minHeight: 36 }}>
                      {isAr ? 'ابدأ العمل الآن — تصفّح الطلبات' : lang === 'zh' ? '立即开始 — 浏览需求' : 'Start working now — browse requests'}
                    </button>
                  </div>

                  <button onClick={() => nav('/')} style={{ background: 'none', border: 'none', color: 'var(--text-disabled)', fontSize: 11, cursor: 'pointer', letterSpacing: 2, textTransform: 'uppercase', padding: 0, transition: 'color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-disabled)'}>
                    {t.backHome}
                  </button>
                </>
              ) : (
                <>
              {/* ── Supplier identity card ──
                  Cover photo flush to top; padding/overflow overridden so the
                  cover image stays clipped to the card's rounded corners. */}
              <div className="supplier-identity-card" style={{ padding: 0, overflow: 'hidden' }}>
                {(() => {
                  const company    = settings.company_name || profile?.company_name || name || '';
                  const firstLetter = company.trim()[0]?.toUpperCase() || '·';
                  // Cover photo is now driven exclusively by profiles.cover_photo_url
                  // (migration 20260506000001). When unset, fall back to plain
                  // cream so the supplier has full control over what shows here.
                  const cover      = settings.cover_photo_url || profile?.cover_photo_url || null;
                  const specialty  = getSpecialtyLabel(settings.speciality || profile?.speciality, lang) || '';
                  const yearEst    = settings.year_established || profile?.year_established || '';
                  const cityLine   = [settings.city, settings.country].filter(Boolean).join(' · ');
                  const estLabel   = isAr ? 'تأسست' : lang === 'zh' ? '成立于' : 'Est.';
                  return (
                    <>
                      {/* Cover photo — image when set, else plain cream fill */}
                      <div style={{
                        width: '100%', height: 160, background: 'var(--bg-page)',
                        overflow: 'hidden',
                      }}>
                        {cover && (
                          <img src={cover} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        )}
                      </div>

                      {/* Body */}
                      <div style={{ padding: '0 24px 20px' }}>
                        {/* Avatar overlapping cover bottom */}
                        <div style={{
                          width: 64, height: 64, borderRadius: 'var(--radius-full)',
                          border: '3px solid var(--surface-raised)', background: 'var(--surface-raised)',
                          marginTop: -32, marginBottom: 14, overflow: 'hidden',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {settings.avatar_url
                            ? <img src={settings.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <span style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)' }}>{firstLetter}</span>}
                        </div>

                        {/* Company name */}
                        <h2 style={{ fontSize: isAr ? 22 : 24, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8, ...arFont, letterSpacing: isAr ? 0 : -0.3 }}>
                          {company || '—'}
                        </h2>

                        {/* Verified pill */}
                        {supplierState.isApprovedStage && (
                          <div style={{ marginBottom: 8 }}>
                            <span style={{ fontSize: 11, color: 'var(--green)', background: 'rgba(45,122,79,0.10)', border: '1px solid rgba(45,122,79,0.22)', borderRadius: 'var(--radius-chip)', padding: '3px 10px', fontWeight: 600, ...arFont, display: 'inline-block' }}>
                              {isAr ? '✓ مورد موثّق' : lang === 'zh' ? '✓ 认证供应商' : '✓ Verified'}
                            </span>
                          </div>
                        )}

                        {/* Specialty */}
                        {specialty && (
                          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, ...arFont }}>
                            {specialty}
                          </p>
                        )}

                        {/* Est. year */}
                        {yearEst && (
                          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, ...arFont }}>
                            {estLabel} {yearEst}
                          </p>
                        )}

                        {/* City · Country */}
                        {cityLine && (
                          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, ...arFont }}>
                            {cityLine}
                          </p>
                        )}

                        {/* MS-ID pill */}
                        {supplierMaabarId && (
                          <div style={{ marginBottom: 12 }}>
                            <span style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-hero)', border: '1px solid var(--border)', borderRadius: 'var(--radius-chip)', padding: '3px 10px', fontFamily: 'var(--font-sans)', letterSpacing: 0.5 }}>
                              {supplierMaabarId}
                            </span>
                          </div>
                        )}

                        {/* Stats row — Phase 7 redesign:
                            Drop Offers tile (zero on a fresh dashboard reads
                            negative). Drop Rating tile when null/0. Add Years
                            experience. No "✓ Responsive" tile: it was derived
                            from status, never from response data (removed
                            2026-10-10). Mirrors mobile SupplierHomeScreen. */}
                        {(() => {
                          const yearsExpRaw = Number(profile?.years_experience);
                          const yrsSuffix   = isAr ? 'سنة' : lang === 'zh' ? '年' : 'yrs';
                          const tiles = [
                            { label: isAr ? 'المنتجات' : lang === 'zh' ? '产品' : 'Products', value: stats.products ?? '—' },
                            Number.isFinite(yearsExpRaw) && yearsExpRaw > 0
                              ? { label: isAr ? 'الخبرة' : lang === 'zh' ? '经验' : 'Experience', value: `${yearsExpRaw} ${yrsSuffix}` }
                              : null,
                            profile?.rating > 0
                              ? { label: isAr ? 'التقييم' : lang === 'zh' ? '评分' : 'Rating', value: `${profile.rating}` }
                              : null,
                          ].filter(Boolean);
                          return (
                            <div style={{ display: 'flex', gap: 16, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                              {tiles.map(({ label, value }) => (
                                <div key={label} style={{ flex: 1, textAlign: 'center' }}>
                                  <p style={{ fontSize: 20, fontWeight: 300, color: 'var(--text-primary)', lineHeight: 1.1 }}>{value}</p>
                                  <p style={{ fontSize: 10, color: 'var(--text-disabled)', marginTop: 2, letterSpacing: 0.5, textTransform: lang === 'zh' ? 'none' : 'uppercase', ...arFont }}>{label}</p>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </>
                  );
                })()}
              </div>

              {needsVerification && (
                <div style={{ marginBottom: 32, padding: '20px 24px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8, ...arFont }}>
                    {t.verificationCtaTitle}
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--text-disabled)', marginBottom: 16, ...arFont, lineHeight: 1.7 }}>
                    {t.verificationCtaBody}
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {/* The stage-1 bar. openVerificationFlow opens the
                        eight-field PAYOUT file and is kept only where payout
                        is the subject — the payout lock banner, and the
                        in-flow step buttons. */}
                    <button onClick={() => nav('/supplier/verify')} className="btn-dark-sm" style={{ fontSize: 11, minHeight: 34 }}>{t.verificationCtaAction}</button>
                  </div>
                </div>
              )}
              {showUploadProductsBanner && (
                <div style={{ marginBottom: 32, padding: '20px 24px', background: 'rgba(160,136,80,0.06)', border: '1px solid rgba(160,136,80,0.22)', borderRadius: 'var(--radius-lg)' }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8, ...arFont }}>
                    {t.uploadProductsBannerTitle}
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--text-disabled)', marginBottom: 16, ...arFont, lineHeight: 1.7 }}>
                    {t.uploadProductsBannerBody}
                  </p>
                  <button onClick={() => setActiveTab('add-product')} className="btn-dark-sm" style={{ fontSize: 11, minHeight: 34 }}>{t.uploadProductsBannerAction}</button>
                </div>
              )}
              {needsPayoutSetup && (
                <div style={{ marginBottom: 32, padding: '20px 24px', background: 'rgba(45,122,79,0.06)', border: '1px solid rgba(45,122,79,0.18)', borderRadius: 'var(--radius-lg)' }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8, ...arFont }}>
                    {t.payoutCtaTitle}
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--text-disabled)', marginBottom: 16, ...arFont, lineHeight: 1.7 }}>
                    {t.payoutCtaBody}
                  </p>
                  <button onClick={() => setActiveTab('payout')} className="btn-dark-sm" style={{ fontSize: 11, minHeight: 34 }}>{t.payoutCtaAction}</button>
                </div>
              )}
              {pendingCount > 0 && (
                <div style={{ marginBottom: 40 }}>
                  <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 14, fontWeight: 500 }}>
                    {t.needsAttention} ({pendingCount})
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {closedOffers.map((o, i) => (
                      <div key={`closed-${i}`}
                        onClick={() => { setActiveTab('offers'); setTimeout(() => document.getElementById(`offer-${o.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150); }}
                        style={{ background: 'rgba(45,122,79,0.06)', border: '1px solid rgba(45,122,79,0.22)', borderRadius: 'var(--radius-lg)', padding: '14px 20px', cursor: 'pointer', transition: 'opacity 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '0.78'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--green)', marginBottom: 3, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? 'عرضك قُبل — أكّد جاهزيتك' : lang === 'zh' ? '报价已通过 — 请确认就绪' : 'Offer accepted — confirm readiness'}
                        </p>
                        <p style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {getTitle(o.requests)} · <span style={{ fontVariantNumeric: 'lining-nums' }}>{getOfferEstimatedTotal(o, o.requests).toFixed(2)} USD</span>
                        </p>
                      </div>
                    ))}
                    {paidOffers.map((o, i) => (
                      <div key={`paid-${i}`}
                        onClick={() => { setActiveTab('offers'); setTimeout(() => document.getElementById(`offer-${o.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150); }}
                        style={{ background: 'rgba(160,136,80,0.06)', border: '1px solid rgba(160,136,80,0.22)', borderRadius: 'var(--radius-lg)', padding: '14px 20px', cursor: 'pointer', transition: 'opacity 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '0.78'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--warn)', marginBottom: 3, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? 'الدفعة الأولى وصلت — ابدأ الإنتاج' : lang === 'zh' ? '首付款已到账 — 开始生产' : 'First payment received — start production'}
                        </p>
                        <p style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {getTitle(o.requests)} · <span style={{ fontVariantNumeric: 'lining-nums' }}>{getOfferEstimatedTotal(o, o.requests).toFixed(2)} USD</span>
                        </p>
                      </div>
                    ))}
                    {rejectedOffers.map((o, i) => (
                      <div key={`rej-${i}`} style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <p style={{ fontSize: 13, color: 'var(--red)', ...arFont }}>{t.offerRejected}: {getTitle(o.requests)}</p>
                        <button onClick={() => dismissRejected(o.id)} className="btn-outline" style={{ padding: '5px 12px', fontSize: 10, minHeight: 30 }}>{isAr ? 'تجاهل' : lang === 'zh' ? '忽略' : 'Dismiss'}</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 40 }}>
                <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 14, fontWeight: 500 }}>{t.stats}</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                  <StatCard label={t.offersCount}   value={stats.offers}   onClick={() => setActiveTab('offers')} />
                  <StatCard label={t.productsCount} value={stats.products} onClick={() => setActiveTab('my-products')} />
                  <StatCard label={t.messagesCount} value={stats.messages} onClick={() => nav('/inbox')} highlight={stats.messages > 0} />
                </div>
                {/* Extended Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 10 }}>
                  <StatCard
                    label={isAr ? 'إجمالي المبيعات' : lang === 'zh' ? '总销售额 (SAR)' : 'Total Sales (SAR)'}
                    value={stats.totalSales ? `${stats.totalSales.toLocaleString()} ` : '—'}
                    onClick={() => {}}
                  />
                  <StatCard
                    label={isAr ? 'نسبة قبول العروض' : lang === 'zh' ? '报价接受率' : 'Offer Accept Rate'}
                    value={stats.offers > 0 ? `${Math.round((stats.acceptedOffers || 0) / stats.offers * 100)}%` : '—'}
                    onClick={() => setActiveTab('offers')}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 40 }}>
                <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 14, fontWeight: 500 }}>{t.quickActions}</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                  <QuickAction title={t.myProducts}     onClick={() => setActiveTab('my-products')} isAr={isAr} />
                  <QuickAction title={t.addNewProduct}  onClick={() => setActiveTab('add-product')} isAr={isAr} />
                </div>
              </div>

              <button onClick={() => nav('/')} style={{ background: 'none', border: 'none', color: 'var(--text-disabled)', fontSize: 11, cursor: 'pointer', letterSpacing: 2, textTransform: 'uppercase', padding: 0, transition: 'color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-disabled)'}>
                {t.backHome}
              </button>
                </>
              ))}
            </div>
          )}

          {/* ── REQUESTS ── */}
          {/* Unified Requests tab (decision #1): status-classified action feed. */}
          {!isRestrictedSupplierTab && activeTab === 'requests' && (
            <div style={section}>
              {ownerFactory && ownerFacInvites > 0 && factoryBanner(bannerLabels.invites, ownerFacInvites, 'messages')}
              <SupplierRequestsPanel sb={sb} supplierId={user.id} lang={lang}
                // The empty-state checklist and the inline prompt both route
                // here. 'settings' is where speciality lives — no categories
                // section was created for one field (owner, 2026-09-19).
                onGoTab={(tab) => setActiveTab(tab)}
                onAction={(it) => {
                  // A message item opens THE conversation, not a list and not a
                  // tab. It used to call setActiveTab('messages'); with that tab
                  // gone the id fell through to the unknown-tab fallback below,
                  // which lands an application-stage supplier on verification —
                  // so "reply to the message" took them to their own paperwork.
                  //
                  // The row already knows who wrote it: supplierActionItems
                  // groups unread inbound by sender and carries it as buyerId,
                  // and those rows are direct messages (.eq('receiver_id', me)),
                  // so /chat/<peer> is the conversation.
                  if (it.kind === 'message') {
                    const peer = it.buyerId || it.sourceId;
                    if (peer) { nav(`/chat/${peer}`); return; }
                    nav('/inbox');   // no peer on the row — the list is still better than a wrong tab
                    return;
                  }
                  // An RFQ needing a response opens THAT request, with its
                  // offer form — there is no browse screen to send them to.
                  if (it.kind === 'rfq' && it.bucket === 'needs_response' && it.requestId) {
                    setDetailRequestId(it.requestId);
                    setActiveTab('request-detail');
                    return;
                  }
                  setActiveTab(
                    it.kind === 'direct' ? 'direct-orders'
                      : it.kind === 'sample' ? 'samples'
                        : it.kind === 'inquiry' ? 'product-inquiries'
                          : 'offers'
                  );
                }} />
            </div>
          )}

          {/* Browse the open RFQ pool + submit offers (reached from the unified feed). */}
          {!isRestrictedSupplierTab && activeTab === 'request-detail' && (
            <div style={section}>
              <BackBtn onClick={() => setActiveTab('requests')} label={t.back} />
              <h2 style={{ fontSize: isAr ? 28 : 34, fontWeight: 300, marginBottom: 24, color: 'var(--text-primary)', ...arFont, letterSpacing: isAr ? 0 : -0.5 }}>
                {isAr ? 'الطلب' : lang === 'zh' ? '该需求' : 'The request'}
              </h2>

              {loadingRequests && [1, 2, 3].map(i => <SkeletonCard key={i} />)}
              {!loadingRequests && requests.length === 0 && <div style={{ textAlign: 'center', padding: '60px 0' }}><p style={{ color: 'var(--text-disabled)', fontSize: 14, ...arFont }}>{isAr ? 'لا توجد طلبات' : lang === 'zh' ? '暂无需求' : 'No requests'}</p></div>}

              {!loadingRequests && requests.map(r => (
                <div key={r.id} style={{ marginBottom: offerForms[r.id] ? 0 : 10 }}>
                  <div style={{ border: '1px solid var(--border-subtle)', padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20, background: 'var(--bg-subtle)', transition: 'all 0.15s', borderRadius: offerForms[r.id] ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-muted)'; e.currentTarget.style.background = 'var(--bg-muted)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.background = 'var(--bg-subtle)'; }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                        <span className={`status-badge ${r.status === 'offers_received' ? 'status-pending' : 'status-open'}`}>
                          {r.status === 'offers_received'
                            ? (isAr ? 'وصلته عروض' : lang === 'zh' ? '已有报价' : 'Offers received')
                            : (isAr ? 'مفتوح' : lang === 'zh' ? '开放中' : 'Open')}
                        </span>
                        {r.category && r.category !== 'other' && <span style={{ fontSize: 10, padding: '2px 8px', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-pill)', color: 'var(--text-disabled)' }}>{cats.find(c => c.val === r.category)?.label || r.category}</span>}
                        <span style={{ color: 'var(--text-disabled)', fontSize: 11 }}>{fmtDate(r.created_at)}</span>
                      </div>
                      <h3 style={{ fontSize: 15, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)', ...arFont }}>{getTitle(r)}</h3>
                      <div style={{ display: 'flex', gap: 16, color: 'var(--text-disabled)', fontSize: 12, flexWrap: 'wrap' }}>
                        <span>{r.profiles?.full_name || r.profiles?.company_name || (isAr ? 'تاجر' : lang === 'zh' ? '采购商' : 'Trader')}</span>
                        <span>{r.quantity || '—'}</span>
                        {(() => {
                          const desc = lang === 'zh'
                            ? (r.description_zh || r.description_en || r.description_ar || r.description)
                            : lang === 'en'
                              ? (r.description_en || r.description_ar || r.description)
                              : (r.description_ar || r.description);
                          return desc ? <span>{desc.substring(0, 55)}…</span> : null;
                        })()}
                      </div>
                    </div>
                    <button className="btn-outline" onClick={() => setSelectedRequest(r)} style={{ minHeight: 38, whiteSpace: 'nowrap' }}>
  {isAr ? 'التفاصيل' : lang === 'zh' ? '详情' : 'Details'}
</button>
                    {pendingOfferIds.has(r.id) ? (
                      // Same button, same size, disabled — content becomes a spinner.
                      // The label is kept invisible so the width never changes (no
                      // layout jump); the spinner is centered over it.
                      <button className="btn-dark-sm" disabled aria-label={isAr ? 'جاري إرسال العرض' : lang === 'zh' ? '正在发送报价' : 'Sending offer'} style={{ minHeight: 38, whiteSpace: 'nowrap', position: 'relative', cursor: 'default' }}>
                        <span style={{ visibility: 'hidden' }}>{isAr ? 'قدم عرضك' : lang === 'zh' ? '提交报价' : 'Submit Quote'}</span>
                        <span className="mb-spinner" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)' }} />
                      </button>
                    ) : (
                      <button className="btn-dark-sm" onClick={() => toggleOfferForm(r.id)} style={{ minHeight: 38, whiteSpace: 'nowrap' }}>
                        {offerForms[r.id] ? (isAr ? 'إغلاق' : lang === 'zh' ? '关闭' : 'Close') : (isAr ? 'قدم عرضك' : lang === 'zh' ? '提交报价' : 'Submit Quote')}
                      </button>
                    )}
                  </div>

                  {offerForms[r.id] && (
                    <div style={{ background: 'var(--bg-muted)', border: '1px solid var(--border-subtle)', borderTop: 'none', padding: '18px 22px', marginBottom: 10, borderRadius: '0 0 var(--radius-lg) var(--radius-lg)' }}>
                      {/* ── Variant line items (Phase 4) ── */}
                      {r.lineItems?.length > 0 && (
                        <div style={{ marginBottom: 16, padding: '14px 16px', borderRadius: 'var(--radius-lg)', background: 'var(--bg-subtle)', border: '1px solid var(--border-muted)' }}>
                          <p style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 10 }}>
                            {isAr ? 'المنتجات المطلوبة' : lang === 'zh' ? '询价明细' : 'Requested Line Items'}
                          </p>
                          {r.lineItems.map((li, idx) => (
                            <div key={li.id || idx} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: idx < r.lineItems.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                              <p style={{ flex: 1, fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{li.product_variants?.sku || li.variant_id?.slice(0, 8) || '—'}</p>
                              <p style={{ fontSize: 12, color: 'var(--text-secondary)', minWidth: 60, textAlign: 'center' }}>{isAr ? `${li.quantity} قطعة` : `×${li.quantity}`}</p>
                              <p style={{ fontSize: 12, color: 'var(--text-primary)', direction: 'ltr', minWidth: 80, textAlign: 'right' }}>{formatPriceWithConversion({
                                amount: Number(li.unit_price_usd),
                                sourceCurrency: 'USD',
                                displayCurrency: viewerCurrency,
                                rates: exchangeRates,
                                lang,
                              })}/u</p>
                              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', direction: 'ltr', minWidth: 80, textAlign: 'right' }}>{formatPriceWithConversion({
                                amount: li.quantity * li.unit_price_usd,
                                sourceCurrency: 'USD',
                                displayCurrency: viewerCurrency,
                                rates: exchangeRates,
                                lang,
                              })}</p>
                            </div>
                          ))}
                          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8 }}>
                            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', direction: 'ltr' }}>
                              {isAr ? 'الإجمالي: ' : lang === 'zh' ? '总计：' : 'Total: '}{formatPriceWithConversion({
                                amount: r.lineItems.reduce((s, li) => s + li.quantity * li.unit_price_usd, 0),
                                sourceCurrency: 'USD',
                                displayCurrency: viewerCurrency,
                                rates: exchangeRates,
                                lang,
                              })}
                            </p>
                          </div>
                        </div>
                      )}

                      {(r.budget_per_unit || r.payment_plan || r.sample_requirement) && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                          {r.budget_per_unit && (
                            <span style={{ fontSize: 10, padding: '4px 8px', borderRadius: 'var(--radius-pill)', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', color: 'var(--text-disabled)' }}>
                              {`${termsCopy.targetPrice}: `}
                              {formatPriceWithConversion({
                                amount: parseFloat(r.budget_per_unit),
                                sourceCurrency: r.budget_currency || 'SAR',
                                displayCurrency: viewerCurrency,
                                rates: exchangeRates,
                                lang,
                              })}
                            </span>
                          )}
                          {termValue(r, 'paymentPlan') && <span style={{ fontSize: 10, padding: '4px 8px', borderRadius: 'var(--radius-pill)', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', color: 'var(--text-disabled)' }}>{`${termsCopy.paymentTerms}: ${termValue(r, 'paymentPlan')}`}</span>}
                          {termValue(r, 'sample') && <span style={{ fontSize: 10, padding: '4px 8px', borderRadius: 'var(--radius-pill)', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', color: 'var(--text-disabled)' }}>{`${termsCopy.sample}: ${termValue(r, 'sample')}`}</span>}
                        </div>
                      )}
                      {/* THE SHARED QUOTE CORE — the same component and the
                          same words the public RFQ form renders. A supplier
                          must not be able to price two ways depending on
                          which door they came through. */}
                      {askCapability && (
                        <DeliveryCapability
                          lang={lang}
                          value={deliveryTerms}
                          onChange={setDeliveryTerms}
                          t={{ title: qt.capTitle, hint: qt.capHint }}
                          styles={{ label: { display: 'block', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 6, ...arFont } }}
                        />
                      )}

                      <QuoteFields
                        t={qt}
                        idPrefix={`off-${r.id}`}
                        allowedBases={allowedBasesFor(deliveryTerms)}
                        styles={{
                          label: { display: 'block', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 6, ...arFont },
                          input: { width: '100%', padding: '11px 13px', borderRadius: 'var(--radius-control)', border: '1px solid var(--border-subtle)', background: 'var(--bg-base)', color: 'var(--text-primary)', fontSize: 15, boxSizing: 'border-box' },
                          hint: { margin: '4px 0 0', fontSize: 12, color: 'var(--text-disabled)', lineHeight: 1.6, ...arFont },
                        }}
                        askSample={SAMPLE_ASKED.includes(String(r.sample_requirement || ''))}
                        sampleRequirement={r.sample_requirement || ''}
                        deliveryAsk={deliveryAskText(r.delivery_preference, lang)}
                        basisWarning={deliveryShortfallText(offers[r.id]?.priceBasis, r.delivery_preference, lang)}
                        isAr={isAr}
                        cnyPerUsd={exchangeRates?.CNY}
                        values={{
                          price: offers[r.id]?.price ?? '',
                          currency: offers[r.id]?.currency ?? 'USD',
                          moq: offers[r.id]?.moq ?? '',
                          leadTime: offers[r.id]?.leadTime ?? '',
                          origin: offers[r.id]?.origin ?? '',
                          note: offers[r.id]?.note ?? '',
                          priceBasis: offers[r.id]?.priceBasis ?? '',
                          validDays: offers[r.id]?.validDays ?? 30,
                          sampleAvailable: offers[r.id]?.sampleAvailable ?? false,
                          samplePrice: offers[r.id]?.samplePrice ?? '',
                          sampleDays: offers[r.id]?.sampleDays ?? '',
                        }}
                        onChange={(k, v) => setOffers(prev => ({ ...prev, [r.id]: { ...prev[r.id], [k]: v } }))}
                      />

                      {offers[r.id]?.price && (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                          gap: 8,
                          marginBottom: 14,
                        }}>
                          <div style={{ padding: '10px 12px', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                            <p style={{ fontSize: 10, color: 'var(--text-disabled)', marginBottom: 4 }}>{isAr ? 'إجمالي المنتجات' : lang === 'zh' ? '产品合计' : 'Products Total'}</p>
                            <p style={{ fontSize: 14, color: 'var(--text-primary)', direction: 'ltr' }}>
                              {formatCurrencyAmount(getOfferProductSubtotal({ price: offers[r.id]?.price }, r), offers[r.id]?.currency || viewerCurrency, lang, { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          {/* (Removed 2026-09-21) The "Shipping" tile. It was fed
                              the LITERAL { shipping_cost: null }, so it printed
                              0.00 on every quote no matter what the supplier
                              typed — a number that looked like a figure they had
                              entered. shipping_cost has not been collected since
                              20260919105244; price_basis is what says who carries
                              freight, and it is right above this box. */}
                          <div style={{ padding: '10px 12px', background: 'var(--bg-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
                            <p style={{ fontSize: 10, color: 'var(--text-disabled)', marginBottom: 4 }}>{isAr ? 'الإجمالي التقديري' : lang === 'zh' ? '预计总额' : 'Estimated Total'}</p>
                            <p style={{ fontSize: 14, color: 'var(--text-primary)', direction: 'ltr' }}>
                              {formatCurrencyAmount(getOfferEstimatedTotal({ price: offers[r.id]?.price }, r), offers[r.id]?.currency || viewerCurrency, lang, { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      )}
                      {/* Said BEFORE the quote is sent (owner, 2026-10-11): an
                          unverified supplier's quote is saved but held from the
                          trader until the account is approved. */}
                      {!supplierQuotesReachTraders(profile?.status, quotesReachTraders) && (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap', padding: '10px 12px', marginBottom: 12, background: 'rgba(196,138,46,0.08)', border: '1px solid rgba(196,138,46,0.28)', borderRadius: 'var(--radius-md)' }}>
                          <p style={{ flex: '1 1 240px', margin: 0, fontSize: 12, lineHeight: 1.7, color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                            {heldQuoteCopy('beforeSubmit', lang)}
                          </p>
                          <button type="button" className="btn-outline" onClick={openVerificationFlow} style={{ minHeight: 32, fontSize: 12, whiteSpace: 'nowrap' }}>
                            {heldQuoteCopy('cta', lang)}
                          </button>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button className="btn-dark-sm" onClick={() => submitOffer(r.id, r.buyer_id)} disabled={submittingOfferId === r.id} style={{ minHeight: 40, opacity: submittingOfferId === r.id ? 0.7 : 1 }}>{submittingOfferId === r.id ? (isAr ? 'جاري الإرسال...' : lang === 'zh' ? '发送中...' : 'Sending...') : (isAr ? 'إرسال العرض' : lang === 'zh' ? '发送报价' : 'Send Offer')}</button>
                        <button className="btn-outline" onClick={() => toggleOfferForm(r.id)} style={{ minHeight: 40 }}>{t.cancel}</button>
                        <span style={{ fontSize: 11, color: 'var(--text-disabled)', display: 'flex', alignItems: 'center', gap: 4, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? 'يراه التاجر فقط — سعرك سري' : lang === 'zh' ? '仅买家可见 — 您的报价保密' : 'Only the buyer sees this — your price is private'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── DIRECT PURCHASE ORDERS (Step 3) ── */}
          {!isRestrictedSupplierTab && activeTab === 'direct-orders' && (
            <div style={section}>
              <BackBtn onClick={() => setActiveTab('overview')} label={t.back} />
              <h2 style={{ fontSize: isAr ? 28 : 34, fontWeight: 300, marginBottom: 8, color: 'var(--text-primary)', ...arFont, letterSpacing: isAr ? 0 : -0.5 }}>
                {isAr ? 'طلبات الشراء المباشر' : lang === 'zh' ? '直接采购订单' : 'Direct Purchase Orders'}
              </h2>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24, ...arFont, lineHeight: 1.7 }}>
                {isAr
                  ? 'طلبات شراء مباشرة من تجار اختاروا منتجاتك. عليك التأكيد أو الرفض خلال 24 ساعة، وإلا تم إلغاء الطلب تلقائياً.'
                  : lang === 'zh'
                    ? '买家直接下单的采购订单。请在 24 小时内确认或拒绝，否则订单将自动取消。'
                    : 'Direct purchase orders from buyers who picked your products. Confirm or reject within 24 hours — orders are auto-cancelled after that.'}
              </p>

              {loadingDirectOrders && [1, 2].map(i => <SkeletonCard key={i} />)}
              {!loadingDirectOrders && directOrders.length === 0 && (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <p style={{ color: 'var(--text-disabled)', fontSize: 14, ...arFont }}>
                    {isAr ? 'لا توجد طلبات شراء مباشرة حالياً' : lang === 'zh' ? '暂无直接采购订单' : 'No pending direct orders'}
                  </p>
                </div>
              )}

              {!loadingDirectOrders && directOrders.map(r => {
                const cd = formatDirectCountdown(r.created_at, directOrdersNowMs);
                const productName = r.product
                  ? (lang === 'zh' ? (r.product.name_zh || r.product.name_en || r.product.name_ar) : lang === 'en' ? (r.product.name_en || r.product.name_ar || r.product.name_zh) : (r.product.name_ar || r.product.name_en || r.product.name_zh))
                  : getTitle(r);
                const buyerName = r.profiles?.company_name || r.profiles?.full_name || (isAr ? 'تاجر' : lang === 'zh' ? '采购商' : 'Trader');
                const acting = directOrderActioning[r.id];
                const countdownColor = cd.expired ? 'var(--red)' : cd.hours < 4 ? 'var(--warn)' : 'var(--green)';
                const countdownLabel = cd.expired
                  ? (isAr ? 'انتهت المهلة' : lang === 'zh' ? '已超时' : 'Expired')
                  : (isAr ? `متبقّي ${cd.hours} ساعة ${cd.mins} دقيقة` : lang === 'zh' ? `剩余 ${cd.hours} 小时 ${cd.mins} 分钟` : `${cd.hours}h ${cd.mins}m left`);

                return (
                  <div key={r.id} style={{ marginBottom: 12, border: '1px solid var(--border-subtle)', background: 'var(--bg-subtle)', borderRadius: 'var(--radius-lg)', padding: '18px 22px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
                      <div style={{ flex: 1, minWidth: 220 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 'var(--radius-pill)', background: 'rgba(45,122,79,0.08)', border: '1px solid rgba(45,122,79,0.2)', color: 'var(--green)', letterSpacing: 0.4 }}>
                            {isAr ? 'شراء مباشر' : lang === 'zh' ? '直接采购' : 'Direct Purchase'}
                          </span>
                          <span style={{ color: 'var(--text-disabled)', fontSize: 11 }}>{fmtDate(r.created_at)}</span>
                        </div>
                        <h3 style={{ fontSize: 15, fontWeight: 500, marginBottom: 8, color: 'var(--text-primary)', ...arFont }}>{productName}</h3>
                        <div style={{ display: 'flex', gap: 16, color: 'var(--text-secondary)', fontSize: 12, flexWrap: 'wrap', ...arFont }}>
                          <span>{isAr ? 'التاجر:' : lang === 'zh' ? '买家：' : 'Buyer:'} {buyerName}</span>
                          <span>{isAr ? 'الكمية:' : lang === 'zh' ? '数量：' : 'Qty:'} {r.quantity || '—'}</span>
                          {(() => {
                            const productPrice = deriveProductPriceFrom(r.product);
                            if (!productPrice) return null;
                            return (
                              <span style={{ direction: 'ltr' }}>
                                {productPrice.toFixed(2)} {r.product?.currency || 'USD'} / unit
                              </span>
                            );
                          })()}
                        </div>
                        {r.description && (
                          <p style={{ marginTop: 10, padding: '10px 12px', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', fontSize: 12, color: 'var(--text-secondary)', ...arFont, lineHeight: 1.6 }}>
                            {r.description}
                          </p>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, minWidth: 130 }}>
                        <span style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-disabled)' }}>
                          {isAr ? 'مهلة 24 ساعة' : lang === 'zh' ? '24 小时窗口' : '24h Window'}
                        </span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: countdownColor, fontFamily: 'var(--font-en)' }}>
                          {countdownLabel}
                        </span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button
                        className="btn-dark-sm"
                        onClick={() => confirmDirectOrder(r)}
                        disabled={Boolean(acting) || cd.expired}
                        style={{ minHeight: 38, opacity: cd.expired ? 0.5 : 1 }}>
                        {acting === 'confirming' ? '...' : isAr ? 'تأكيد الطلب' : lang === 'zh' ? '确认订单' : 'Confirm Order'}
                      </button>
                      <button
                        onClick={() => rejectDirectOrder(r)}
                        disabled={Boolean(acting) || cd.expired}
                        style={{ background: 'none', border: '1px solid rgba(224,92,92,0.3)', color: 'var(--red)', padding: '8px 16px', fontSize: 12, cursor: (acting || cd.expired) ? 'not-allowed' : 'pointer', borderRadius: 'var(--radius-md)', minHeight: 38, opacity: cd.expired ? 0.5 : 1, ...arFont }}>
                        {acting === 'rejecting' ? '...' : isAr ? 'رفض الطلب' : lang === 'zh' ? '拒绝订单' : 'Reject Order'}
                      </button>
                      {r.buyer_id && (
                        <button className="btn-outline" onClick={() => nav(`/chat/${r.buyer_id}`)} style={{ minHeight: 38, ...arFont }}>
                          {isAr ? 'تواصل مع التاجر' : lang === 'zh' ? '联系买家' : 'Chat with Buyer'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* ── Confirmed — awaiting buyer payment (read-only) ─────── */}
              <div style={{ marginTop: 36, paddingTop: 28, borderTop: '1px solid var(--border-subtle)' }}>
                <h3 style={{ fontSize: isAr ? 18 : 20, fontWeight: 500, marginBottom: 8, color: 'var(--text-primary)', ...arFont, letterSpacing: isAr ? 0 : -0.2 }}>
                  {isAr ? 'بانتظار دفع التاجر' : lang === 'zh' ? '等待买家付款' : 'Awaiting Buyer Payment'}
                </h3>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 18, ...arFont, lineHeight: 1.7 }}>
                  {isAr
                    ? 'أكدت الطلب — بانتظار إكمال التاجر للدفع. سينتقل إلى «مدفوع» لتجهيز الشحن.'
                    : lang === 'zh'
                      ? '您已确认订单 — 等待买家付款。付款后将移至"已付款"以便您备货发货。'
                      : 'You confirmed the order — waiting for the buyer to pay. It then moves to "Paid" for you to prepare shipping.'}
                </p>

                {loadingConfirmedDirectOrders && [1, 2].map(i => <SkeletonCard key={`confirmed-skel-${i}`} />)}
                {!loadingConfirmedDirectOrders && confirmedDirectOrders.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '36px 0' }}>
                    <p style={{ color: 'var(--text-disabled)', fontSize: 13, ...arFont }}>
                      {isAr ? 'لا توجد طلبات مؤكدة بانتظار الدفع' : lang === 'zh' ? '暂无等待付款的已确认订单' : 'No confirmed orders awaiting payment'}
                    </p>
                  </div>
                )}

                {!loadingConfirmedDirectOrders && confirmedDirectOrders.map(r => {
                  const productName = r.product
                    ? (lang === 'zh' ? (r.product.name_zh || r.product.name_en || r.product.name_ar) : lang === 'en' ? (r.product.name_en || r.product.name_ar || r.product.name_zh) : (r.product.name_ar || r.product.name_en || r.product.name_zh))
                    : getTitle(r);
                  const buyerName = r.profiles?.company_name || r.profiles?.full_name || (isAr ? 'تاجر' : lang === 'zh' ? '采购商' : 'Trader');
                  const productPrice = deriveProductPriceFrom(r.product);
                  return (
                    <div key={`confirmed-${r.id}`} style={{ marginBottom: 12, border: '1px solid rgba(45,122,79,0.22)', background: 'rgba(45,122,79,0.03)', borderRadius: 'var(--radius-lg)', padding: '18px 22px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 'var(--radius-pill)', background: 'rgba(45,122,79,0.08)', border: '1px solid rgba(45,122,79,0.2)', color: 'var(--green)', letterSpacing: 0.4 }}>
                          {isAr ? 'مؤكد — بانتظار الدفع' : lang === 'zh' ? '已确认 — 等待付款' : 'Confirmed — Awaiting Payment'}
                        </span>
                        <span style={{ color: 'var(--text-disabled)', fontSize: 11 }}>{fmtDate(r.updated_at || r.created_at)}</span>
                      </div>
                      <h3 style={{ fontSize: 15, fontWeight: 500, marginBottom: 8, color: 'var(--text-primary)', ...arFont }}>{productName}</h3>
                      <div style={{ display: 'flex', gap: 16, color: 'var(--text-secondary)', fontSize: 12, flexWrap: 'wrap', ...arFont }}>
                        <span>{isAr ? 'التاجر:' : lang === 'zh' ? '买家：' : 'Buyer:'} {buyerName}</span>
                        <span>{isAr ? 'الكمية:' : lang === 'zh' ? '数量：' : 'Qty:'} {r.quantity || '—'}</span>
                        {productPrice && (
                          <span style={{ direction: 'ltr' }}>{productPrice.toFixed(2)} {r.product?.currency || 'USD'} / unit</span>
                        )}
                      </div>
                      {r.buyer_id && (
                        <button className="btn-outline" onClick={() => nav(`/chat/${r.buyer_id}`)} style={{ marginTop: 12, minHeight: 34, ...arFont }}>
                          {isAr ? 'تواصل مع التاجر' : lang === 'zh' ? '联系买家' : 'Chat with Buyer'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* ── Paid orders awaiting tracking (Step 5) ─────────────── */}
              <div style={{ marginTop: 36, paddingTop: 28, borderTop: '1px solid var(--border-subtle)' }}>
                <h3 style={{ fontSize: isAr ? 18 : 20, fontWeight: 500, marginBottom: 8, color: 'var(--text-primary)', ...arFont, letterSpacing: isAr ? 0 : -0.2 }}>
                  {isAr ? 'طلبات مدفوعة — أضف رقم التتبع' : lang === 'zh' ? '已付款订单 — 添加物流单号' : 'Paid Orders — Add Tracking'}
                </h3>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 18, ...arFont, lineHeight: 1.7 }}>
                  {isAr
                    ? 'أكمل التاجر الدفع — جهّز الشحنة وارفع رقم التتبع لتظهر للتاجر داخل مَعبر.'
                    : lang === 'zh'
                      ? '买家已付款 — 请准备发货并上传物流单号，以便买家在 Maabar 内查看。'
                      : 'The buyer has paid — prepare the shipment and upload the tracking number so the buyer can see it in Maabar.'}
                </p>

                {loadingPaidDirectOrders && [1, 2].map(i => <SkeletonCard key={`paid-skel-${i}`} />)}
                {!loadingPaidDirectOrders && paidDirectOrders.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '36px 0' }}>
                    <p style={{ color: 'var(--text-disabled)', fontSize: 13, ...arFont }}>
                      {isAr ? 'لا توجد طلبات مدفوعة في انتظار الشحن' : lang === 'zh' ? '暂无等待发货的已付款订单' : 'No paid orders awaiting tracking'}
                    </p>
                  </div>
                )}

                {!loadingPaidDirectOrders && paidDirectOrders.map(r => {
                  const product = r.product || {};
                  const productName = lang === 'zh'
                    ? (product.name_zh || product.name_en || product.name_ar)
                    : lang === 'en'
                      ? (product.name_en || product.name_ar || product.name_zh)
                      : (product.name_ar || product.name_en || product.name_zh);
                  const buyerName = r.profiles?.company_name || r.profiles?.full_name || (isAr ? 'تاجر' : lang === 'zh' ? '采购商' : 'Trader');
                  const acting = directShippingActioning[r.id];
                  const payment = r.payment || null;
                  const paymentDate = payment?.created_at || r.updated_at;
                  const paymentAmount = payment ? Number(payment.amount || 0) : 0;
                  const paymentCurrency = payment?.currency || product.currency || 'USD';
                  const trackingValue = trackingInputs[r.id] || '';

                  return (
                    <div key={`paid-${r.id}`} style={{ marginBottom: 12, border: '1px solid rgba(45,122,79,0.25)', background: 'rgba(45,122,79,0.04)', borderRadius: 'var(--radius-lg)', padding: '18px 22px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
                        <div style={{ flex: 1, minWidth: 220 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 'var(--radius-pill)', background: 'rgba(45,122,79,0.10)', border: '1px solid rgba(45,122,79,0.28)', color: 'var(--green)', letterSpacing: 0.4, fontWeight: 600 }}>
                              {isAr ? 'مدفوع — جاهز للشحن' : lang === 'zh' ? '已付款 — 准备发货' : 'Paid — Ready to Ship'}
                            </span>
                          </div>
                          <h4 style={{ fontSize: 15, fontWeight: 500, marginBottom: 8, color: 'var(--text-primary)', ...arFont }}>{productName || (isAr ? 'منتج' : 'Product')}</h4>
                          <div style={{ display: 'flex', gap: 14, color: 'var(--text-secondary)', fontSize: 12, flexWrap: 'wrap', ...arFont }}>
                            <span>{isAr ? 'التاجر:' : lang === 'zh' ? '买家：' : 'Buyer:'} {buyerName}</span>
                            <span>{isAr ? 'الكمية:' : lang === 'zh' ? '数量：' : 'Qty:'} {r.quantity || '—'}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, minWidth: 150 }}>
                          <span style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-disabled)' }}>
                            {isAr ? 'تاريخ الدفع' : lang === 'zh' ? '付款日期' : 'Payment Date'}
                          </span>
                          <span style={{ fontSize: 12, color: 'var(--text-primary)', ...arFont }}>{fmtDate(paymentDate)}</span>
                          {paymentAmount > 0 && (
                            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--green)', direction: 'ltr', marginTop: 4 }}>
                              {paymentAmount.toFixed(2)} {paymentCurrency}
                            </span>
                          )}
                        </div>
                      </div>

                      <div style={{ marginBottom: 10 }}>
                        <button
                          onClick={() => setInvoiceOrder({ requestId: r.id, order: { request_ref: r.request_ref, buyer_name: buyerName, supplier_name: profile?.company_name || profile?.full_name || '', product_name: productName, quantity: Number(r.quantity) || 1, unit_price: deriveProductPriceFrom(product) ?? 0, currency: product.currency || 'SAR' } })}
                          style={{ padding: '8px 14px', fontSize: 12, fontWeight: 600, background: 'none', border: '1px solid #0C6B5A', color: '#0C6B5A', borderRadius: 'var(--radius-md)', cursor: 'pointer' }}>
                          {isAr ? 'إصدار الفاتورة' : lang === 'zh' ? '开具发票' : 'Issue Invoice'}
                        </button>
                      </div>

                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
                        <select
                          value={shippingCompany}
                          onChange={e => setShippingCompany(e.target.value)}
                          style={{ padding: '8px 12px', fontSize: 12, border: '1px solid var(--border-subtle)', background: 'var(--bg-subtle)', color: 'var(--text-secondary)', borderRadius: 'var(--radius-md)', cursor: 'pointer', outline: 'none', minHeight: 38 }}>
                          {['DHL','FedEx','Aramex','UPS','SMSA', isAr ? 'أخرى' : lang === 'zh' ? '其他' : 'Other'].map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                        <input
                          className="form-input"
                          style={{ flex: 1, minWidth: 160 }}
                          placeholder={t.trackingNum}
                          value={trackingValue}
                          onChange={e => setTrackingInputs(prev => ({ ...prev, [r.id]: e.target.value }))}
                          dir="ltr"
                        />
                        <button
                          className="btn-dark-sm"
                          onClick={() => submitDirectTracking(r)}
                          disabled={Boolean(acting) || !trackingValue.trim()}
                          style={{ minHeight: 38 }}>
                          {acting === 'shipping' ? '...' : isAr ? 'إرسال رقم التتبع' : lang === 'zh' ? '提交物流单号' : 'Send Tracking'}
                        </button>
                      </div>

                      {r.buyer_id && (
                        <button className="btn-outline" onClick={() => nav(`/chat/${r.buyer_id}`)} style={{ minHeight: 36, fontSize: 11, padding: '7px 14px', ...arFont }}>
                          {isAr ? 'تواصل مع التاجر' : lang === 'zh' ? '联系买家' : 'Chat with Buyer'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* ── Active shipments (read-only) ──────────────────────── */}
              <div style={{ marginTop: 36, paddingTop: 28, borderTop: '1px solid var(--border-subtle)' }}>
                <h3 style={{ fontSize: isAr ? 18 : 20, fontWeight: 500, marginBottom: 8, color: 'var(--text-primary)', ...arFont, letterSpacing: isAr ? 0 : -0.2 }}>
                  {isAr ? 'الشحنات النشطة' : lang === 'zh' ? '运输中的订单' : 'Active Shipments'}
                </h3>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 18, ...arFont, lineHeight: 1.7 }}>
                  {isAr
                    ? 'الطلبات التي شحنتها وفي انتظار تأكيد التاجر بالاستلام.'
                    : lang === 'zh'
                      ? '已发货的订单，等待买家确认收货。'
                      : 'Orders you have shipped — awaiting the buyer to confirm receipt.'}
                </p>

                {loadingDirectHistory && [1, 2].map(i => <SkeletonCard key={`shipping-skel-${i}`} />)}
                {!loadingDirectHistory && shippingDirectOrders.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '36px 0' }}>
                    <p style={{ color: 'var(--text-disabled)', fontSize: 13, ...arFont }}>
                      {isAr ? 'لا توجد شحنات نشطة' : lang === 'zh' ? '暂无运输中的订单' : 'No active shipments'}
                    </p>
                  </div>
                )}

                {!loadingDirectHistory && shippingDirectOrders.map(r => {
                  const product = r.product || {};
                  const productName = lang === 'zh'
                    ? (product.name_zh || product.name_en || product.name_ar)
                    : lang === 'en'
                      ? (product.name_en || product.name_ar || product.name_zh)
                      : (product.name_ar || product.name_en || product.name_zh);
                  const buyerName = r.profiles?.company_name || r.profiles?.full_name || (isAr ? 'تاجر' : lang === 'zh' ? '采购商' : 'Trader');
                  const carrier = r.shipping_company || 'Other';
                  const trackingNumber = r.tracking_number || '';
                  const trackUrl = trackingNumber ? getTrackingUrl(carrier, trackingNumber) : null;
                  const isArrived = r.status === 'arrived';
                  const statusLabel = isArrived
                    ? (isAr ? 'وصلت — التاجر يفحص' : lang === 'zh' ? '已到货 — 买家正在验收' : 'Arrived — Buyer Inspecting')
                    : (isAr ? 'في الطريق' : lang === 'zh' ? '运输中' : 'In Transit');
                  const statusColor = isArrived ? 'var(--green)' : 'var(--warn)';
                  const statusBg = isArrived ? 'rgba(45,122,79,0.05)' : 'rgba(160,136,80,0.05)';
                  const statusBorder = isArrived ? 'rgba(45,122,79,0.30)' : 'rgba(160,136,80,0.30)';

                  return (
                    <div key={`shipping-${r.id}`} style={{ marginBottom: 12, border: `1px solid ${statusBorder}`, background: statusBg, borderRadius: 'var(--radius-lg)', padding: '18px 22px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
                        <div style={{ flex: 1, minWidth: 220 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 'var(--radius-pill)', background: 'var(--bg-raised)', border: `1px solid ${statusBorder}`, color: statusColor, letterSpacing: 0.4, fontWeight: 600, ...arFont }}>
                              {statusLabel}
                            </span>
                            <span style={{ color: 'var(--text-disabled)', fontSize: 11 }}>{fmtDate(r.created_at)}</span>
                          </div>
                          <h4 style={{ fontSize: 15, fontWeight: 500, marginBottom: 8, color: 'var(--text-primary)', ...arFont }}>{productName || (isAr ? 'منتج' : 'Product')}</h4>
                          <div style={{ display: 'flex', gap: 14, color: 'var(--text-secondary)', fontSize: 12, flexWrap: 'wrap', ...arFont }}>
                            <span>{isAr ? 'التاجر:' : lang === 'zh' ? '买家：' : 'Buyer:'} {buyerName}</span>
                            <span>{isAr ? 'الكمية:' : lang === 'zh' ? '数量：' : 'Qty:'} {r.quantity || '—'}</span>
                          </div>
                        </div>
                      </div>

                      {trackingNumber && (
                        <div style={{ padding: '10px 14px', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', ...arFont }}>
                          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                            {isAr ? 'شركة الشحن:' : lang === 'zh' ? '承运商：' : 'Carrier:'} <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{carrier}</strong>
                          </span>
                          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                            {isAr ? 'رقم التتبع:' : lang === 'zh' ? '物流单号：' : 'Tracking #:'} <strong style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-en)', fontWeight: 600, direction: 'ltr', display: 'inline-block' }}>{trackingNumber}</strong>
                          </span>
                          {trackUrl && (
                            <a href={trackUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--green)', textDecoration: 'none', borderBottom: '1px dashed var(--green)', paddingBottom: 1 }}>
                              {isAr ? 'تتبع الشحنة ↗' : lang === 'zh' ? '查看跟踪 ↗' : 'Track Shipment ↗'}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* ── Completed orders (read-only) ──────────────────────── */}
              <div style={{ marginTop: 36, paddingTop: 28, borderTop: '1px solid var(--border-subtle)' }}>
                <h3 style={{ fontSize: isAr ? 18 : 20, fontWeight: 500, marginBottom: 8, color: 'var(--text-primary)', ...arFont, letterSpacing: isAr ? 0 : -0.2 }}>
                  {isAr ? 'الطلبات المكتملة' : lang === 'zh' ? '已完成订单' : 'Completed Orders'}
                </h3>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 18, ...arFont, lineHeight: 1.7 }}>
                  {isAr
                    ? 'الطلبات التي أكد التاجر استلامها — تم تحرير المبالغ.'
                    : lang === 'zh'
                      ? '买家已确认收货的订单 — 款项已放给您。'
                      : 'Orders the buyer has confirmed delivered — payouts have been released.'}
                </p>

                {loadingDirectHistory && [1].map(i => <SkeletonCard key={`done-skel-${i}`} />)}
                {!loadingDirectHistory && completedDirectOrders.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '36px 0' }}>
                    <p style={{ color: 'var(--text-disabled)', fontSize: 13, ...arFont }}>
                      {isAr ? 'لا توجد طلبات مكتملة بعد' : lang === 'zh' ? '暂无已完成订单' : 'No completed orders yet'}
                    </p>
                  </div>
                )}

                {!loadingDirectHistory && completedDirectOrders.map(r => {
                  const product = r.product || {};
                  const productName = lang === 'zh'
                    ? (product.name_zh || product.name_en || product.name_ar)
                    : lang === 'en'
                      ? (product.name_en || product.name_ar || product.name_zh)
                      : (product.name_ar || product.name_en || product.name_zh);
                  const buyerName = r.profiles?.company_name || r.profiles?.full_name || (isAr ? 'تاجر' : lang === 'zh' ? '采购商' : 'Trader');
                  const payment = r.payment || null;
                  const paymentAmount = payment ? Number(payment.amount || 0) : 0;
                  const paymentCurrency = product.currency || 'USD';
                  const deliveryDate = r.updated_at || payment?.created_at || r.created_at;

                  return (
                    <div key={`done-${r.id}`} style={{ marginBottom: 12, border: '1px solid rgba(45,122,79,0.20)', background: 'rgba(45,122,79,0.03)', borderRadius: 'var(--radius-lg)', padding: '16px 20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 220 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 'var(--radius-pill)', background: 'rgba(45,122,79,0.10)', border: '1px solid rgba(45,122,79,0.28)', color: 'var(--green)', letterSpacing: 0.4, fontWeight: 600, ...arFont }}>
                              ✓ {isAr ? 'مكتمل' : lang === 'zh' ? '已完成' : 'Completed'}
                            </span>
                          </div>
                          <h4 style={{ fontSize: 14, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)', ...arFont }}>{productName || (isAr ? 'منتج' : 'Product')}</h4>
                          <div style={{ display: 'flex', gap: 14, color: 'var(--text-secondary)', fontSize: 12, flexWrap: 'wrap', ...arFont }}>
                            <span>{isAr ? 'التاجر:' : lang === 'zh' ? '买家：' : 'Buyer:'} {buyerName}</span>
                            <span>{isAr ? 'الكمية:' : lang === 'zh' ? '数量：' : 'Qty:'} {r.quantity || '—'}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, minWidth: 140 }}>
                          <span style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-disabled)' }}>
                            {isAr ? 'تاريخ التسليم' : lang === 'zh' ? '交付日期' : 'Delivered'}
                          </span>
                          <span style={{ fontSize: 12, color: 'var(--text-primary)', ...arFont }}>{fmtDate(deliveryDate)}</span>
                          {paymentAmount > 0 && (
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)', direction: 'ltr', marginTop: 4 }}>
                              {paymentAmount.toFixed(2)} {paymentCurrency}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── MY PRODUCTS ── */}
          {!isRestrictedSupplierTab && activeTab === 'my-products' && (
            <div style={section}>
              <BackBtn onClick={() => setActiveTab('overview')} label={t.back} />
              {ownerFactory && (
                <div style={{ marginBottom: 28 }}>
                  <h3 style={{ fontSize: isAr ? 20 : 22, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px', ...arFont }}>
                    {isAr ? `منتجات جهّزناها لك (${ownerCatalog?.length ?? ownerFacProducts})` : lang === 'zh' ? `我们为您准备的产品 (${ownerCatalog?.length ?? ownerFacProducts})` : `Products we prepared for you (${ownerCatalog?.length ?? ownerFacProducts})`}
                  </h3>
                  <p style={{ fontSize: 12.5, color: 'var(--text-tertiary)', margin: '0 0 14px', ...arFont }}>
                    {isAr ? 'راجع الأسعار — المنتج الذي تضبط سعره يُعلَّم «مؤكّد».' : lang === 'zh' ? '查看价格 — 您设定价格的产品将标记为"已确认"。' : 'Review prices — a product you price is marked "Confirmed".'}
                  </p>
                  {ownerCatalog === null
                    ? <p style={{ color: 'var(--text-disabled)', fontSize: 13, ...arFont }}>…</p>
                    : ownerCatalog.length === 0 ? null
                      : <FactoryCatalogQuickEdit products={ownerCatalog} setProducts={setOwnerCatalog} lang={lang} />}
                  <div style={{ height: 1, background: 'var(--border-subtle)', margin: '28px 0 4px' }} />
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 32 }}>
                <h2 style={{ fontSize: isAr ? 28 : 34, fontWeight: 300, color: 'var(--text-primary)', ...arFont, letterSpacing: isAr ? 0 : -0.5 }}>{t.myProductsTitle}</h2>
                <button className="btn-dark-sm" onClick={() => setActiveTab('add-product')} style={{ fontSize: 11, minHeight: 36 }}>{isAr ? '+ إضافة' : lang === 'zh' ? '+ 添加' : '+ Add'}</button>
              </div>

              {loadingProducts && [1, 2, 3].map(i => <SkeletonCard key={i} />)}
              {!loadingProducts && myProducts.length === 0 && (
                <div style={{ textAlign: 'center', padding: '80px 0', borderTop: '1px solid var(--border-subtle)' }}>
                  <p style={{ color: 'var(--text-disabled)', fontSize: 14, marginBottom: 24, ...arFont }}>{t.noProducts}</p>
                  <button className="btn-dark-sm" onClick={() => setActiveTab('add-product')} style={{ minHeight: 40 }}>{t.addNewProduct}</button>
                </div>
              )}

              {!loadingProducts && myProducts.map((p, idx) => (
                <div key={p.id}>
                  {editingProduct?.id === p.id ? (
                    <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '24px 0', animation: 'fadeIn 0.3s ease' }}>
                      {editProductComposerStep === 'preview' ? (
                        <ProductPreviewPanel key={editingProduct.id} product={normalizeProductDraftMedia(editingProduct)} onPublish={updateProduct} onBack={() => setEditProductComposerStep('edit')} t={t} isAr={isAr} saving={saving} lang={lang} certificationsCount={(editingProduct.certifications || []).length} />
                      ) : (
                        <ProductForm key={editingProduct.id} isEdit certificationsCount={(editingProduct.certifications || []).length} data={editingProduct} setData={setEditingProduct} onSave={updateProduct} onSaveDraft={() => updateProduct(true)} onPreview={openEditProductPreview} showPreviewAction onCancel={() => { setEditingProduct(null); setEditProductComposerStep('edit'); }} imgRef={editImageRef} vidRef={editVideoRef} onImgChange={e => handleImageUpload(e, true)} onVidChange={e => handleVideoUpload(e, true)} onRemoveImage={index => removeImageAt(index, true)} onRemoveVideo={() => removeVideo(true)} uploadingImage={uploadingImage} uploadingVideo={uploadingVideo} t={t} isAr={isAr} saving={saving} usdRate={usdRate} categories={cats} lang={lang} variantData={editVariantData} setVariantData={setEditVariantData} />
                      )}
                      {/* The edit form's own message line: validation errors,
                          "prices still loading" and a failed price save used
                          to be set here but rendered only on the Add tab, so
                          an edit that failed its check looked like a dead
                          button (2026-10-04). */}
                      {productSaveMsg && (
                        <p data-testid="edit-product-msg" style={{ marginTop: 12, fontSize: 13, color: 'var(--red)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {productSaveMsg}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '16px 0', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', animation: `fadeIn 0.35s ease ${idx * 0.04}s both` }}>
                      <div style={{ width: 60, height: 60, borderRadius: 'var(--radius-lg)', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                        {getPrimaryProductImage(p) ? <img src={getPrimaryProductImage(p)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 20, opacity: 0.25 }}>◻</span>}
                      </div>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                          <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', ...arFont }}>{lang === 'zh' ? p.name_zh || p.name_en : lang === 'ar' ? p.name_ar || p.name_en : p.name_en || p.name_ar}</p>
                          {getProductGalleryImages(p).length > 1 && <span style={{ fontSize: 9, padding: '2px 7px', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-panel)', color: 'var(--text-disabled)', letterSpacing: 0.5 }}>{lang === 'zh' ? `${getProductGalleryImages(p).length} 图` : `${getProductGalleryImages(p).length} IMG`}</span>}
                          {p.video_url && <span style={{ fontSize: 9, padding: '2px 7px', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-panel)', color: 'var(--text-disabled)', letterSpacing: 0.5 }}>{lang === 'zh' ? '视频' : 'VIDEO'}</span>}
                          {p.sample_available && <span style={{ fontSize: 9, padding: '2px 7px', background: 'rgba(45,122,79,0.1)', border: '1px solid rgba(45,122,79,0.2)', borderRadius: 'var(--radius-panel)', color: 'var(--green)', letterSpacing: 0.5 }}>{isAr ? 'عينة' : lang === 'zh' ? '样品' : 'SAMPLE'}</span>}
                          {p.category && p.category !== 'other' && <span style={{ fontSize: 9, padding: '2px 7px', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-panel)', color: 'var(--text-disabled)', letterSpacing: 0.5 }}>{cats.find(c => c.val === p.category)?.label || p.category}</span>}
                        </div>
                        {deriveProductPriceFrom(p) != null ? (
                          <p style={{ fontSize: 12, color: 'var(--text-disabled)' }}>{deriveProductPriceFrom(p)} {p.currency || 'USD'} · {isAr ? 'MOQ' : lang === 'zh' ? '最小起订量' : 'MOQ'}: {p.moq}</p>
                        ) : (
                          // No price (owner, 2026-10-04): existing products are not
                          // blocked, but the supplier is asked for one, here.
                          <div data-testid="no-price-prompt" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 'var(--radius-pill)', border: '1px solid rgba(176,141,46,0.4)', color: '#8A6D1E', background: 'rgba(176,141,46,0.12)', ...arFont }}>{t.noPriceBadge}</span>
                            <button onClick={() => startEditProduct(p)} className="btn-dark-sm" style={{ padding: '4px 12px', fontSize: 11, minHeight: 28, ...arFont }}>{t.addYourPrice}</button>
                            <span style={{ fontSize: 11, color: 'var(--text-disabled)', ...arFont }}>{t.noPriceHint}</span>
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {p.is_draft ? (
                          <span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 'var(--radius-pill)', border: '1px solid rgba(176,141,46,0.4)', color: '#8A6D1E', background: 'rgba(176,141,46,0.12)' }}>{isAr ? 'مسودة' : lang === 'zh' ? '草稿' : 'Draft'}</span>
                        ) : (
                          <>
                            <span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 'var(--radius-pill)', border: '1px solid', borderColor: p.is_active ? 'rgba(45,122,79,0.3)' : 'var(--border-subtle)', color: p.is_active ? 'var(--green)' : 'var(--text-disabled)', background: p.is_active ? 'rgba(45,122,79,0.08)' : 'transparent' }}>{p.is_active ? t.active : t.inactive}</span>
                            <button onClick={() => toggleProductActive(p)} className="btn-outline" style={{ padding: '5px 10px', fontSize: 10, minHeight: 28 }}>{t.toggleActive}</button>
                          </>
                        )}
                        <button onClick={() => startEditProduct(p)} className="btn-outline" style={{ padding: '5px 10px', fontSize: 10, minHeight: 28 }}>{t.edit}</button>
                        <button onClick={() => deleteProduct(p)} style={{ background: 'none', border: '1px solid rgba(224,92,92,0.3)', color: 'var(--red)', padding: '5px 10px', fontSize: 10, cursor: 'pointer', borderRadius: 'var(--radius-md)', minHeight: 28 }}>{t.delete}</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── MY OFFERS ── */}
          {!isRestrictedSupplierTab && activeTab === 'offers' && (
            <div style={section}>
              <BackBtn onClick={() => setActiveTab('overview')} label={t.back} />
              <h2 style={{ fontSize: isAr ? 28 : 34, fontWeight: 300, marginBottom: 32, color: 'var(--text-primary)', ...arFont, letterSpacing: isAr ? 0 : -0.5 }}>{t.myOffers}</h2>

              {loadingOffers && [1, 2, 3].map(i => <SkeletonCard key={i} />)}
              {!loadingOffers && myOffers.length === 0 && (
                <div style={{ textAlign: 'center', padding: '80px 0', borderTop: '1px solid var(--border-subtle)' }}>
                  <p style={{ fontSize: 14, color: 'var(--text-disabled)', marginBottom: 24, ...arFont }}>{t.noOffers}</p>
                  <button className="btn-dark-sm" onClick={() => setActiveTab('requests')} style={{ minHeight: 40 }}>{t.browseReqs}</button>
                </div>
              )}

              {!loadingOffers && myOffers.map((o, idx) => {
                const reqStatus  = o.requests?.status || '';
                const isAccepted = o.status === 'accepted';
                const isPending  = o.status === 'pending';
                const step       = isAccepted ? (SUPPLIER_STEP_MAP[reqStatus] ?? 0) : 0;
                const isComplete = completedPayments.has(o.request_id);
                // "Trader" when the name is withheld (2026-09-28) — the profiles
                // embed is RLS-null for a supplier, so this is the usual case.
                const buyerName  = o.requests?.profiles?.company_name || o.requests?.profiles?.full_name || traderLabel(lang);
                const category   = o.requests?.category;
                const total      = getOfferEstimatedTotal(o, o.requests);
                const isShipping = ['shipping', 'arrived', 'delivered'].includes(reqStatus);
                return (
                  <div key={o.id} id={`offer-${o.id}`} style={{ borderTop: '1px solid var(--border-subtle)', padding: '24px 0', animation: `fadeIn 0.35s ease ${idx * 0.04}s both` }}>

                    {/* ── Section 1: category tag + time, then title ── */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5, gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {category && (
                          <span style={{ fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border-subtle)', color: 'var(--text-disabled)' }}>
                            {category}
                          </span>
                        )}
                        {o.created_at && (
                          <span style={{ fontSize: 11, color: 'var(--text-disabled)' }}>{relativeTime(o.created_at)}</span>
                        )}
                      </div>
                      {!isAccepted && (
                        <span style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', padding: '3px 10px', borderRadius: 'var(--radius-pill)', border: '1px solid', borderColor: o.status === 'rejected' ? 'rgba(224,92,92,0.3)' : 'var(--border-subtle)', color: o.status === 'rejected' ? 'var(--red)' : 'var(--text-disabled)' }}>
                          {OFFER_STATUS[lang]?.[o.status] || o.status}
                        </span>
                      )}

                      {/* Saved but hidden from the trader until this supplier
                          is approved (2026-10-11). Approval releases it
                          automatically (reveal_quotes_on_supplier_approval). */}
                      {isQuoteHeldForVerification(o, o.requests) && (
                        <span style={{ fontSize: 10, letterSpacing: 1.2, padding: '3px 10px', borderRadius: 'var(--radius-pill)', border: '1px solid rgba(196,138,46,0.35)', color: '#9A6B1F', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {heldQuoteCopy('chip', lang)}
                        </span>
                      )}

                      {/* THE REQUEST was cancelled by the trader — a different
                          fact from the offer's own status, which is untouched.
                          Without this the supplier's quote reads 'pending'
                          forever and nothing explains why it went quiet.

                          The quote itself is deliberately NOT deleted or
                          rewritten: since migration 20260920120000 a trader can
                          no longer destroy a quoted request, only close it, so
                          the supplier keeps the record of the work they did. */}
                      {reqStatus === 'cancelled' && (
                        <span style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', padding: '3px 10px', borderRadius: 'var(--radius-pill)', border: '1px solid rgba(224,92,92,0.3)', color: 'var(--red)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? 'أُلغي الطلب' : lang === 'zh' ? '需求已取消' : 'Request cancelled'}
                        </span>
                      )}
                    </div>
                    <h3 style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 10, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', lineHeight: 1.4 }}>{getTitle(o.requests)}</h3>
                    {isQuoteHeldForVerification(o, o.requests) && (
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {heldQuoteCopy('held', lang)}
                        </span>
                        <button type="button" className="btn-outline" onClick={openVerificationFlow} style={{ minHeight: 30, fontSize: 12 }}>
                          {heldQuoteCopy('cta', lang)}
                        </button>
                      </div>
                    )}

                    {/* ── Section 2: buyer name + verified badge (accepted only) ── */}
                    {isAccepted && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{buyerName}</span>
                        <span style={{ fontSize: 10, color: 'var(--green)', background: 'rgba(45,122,79,0.08)', border: '1px solid rgba(45,122,79,0.22)', borderRadius: 'var(--radius-chip)', padding: '2px 8px', fontWeight: 600, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? 'موثّق' : lang === 'zh' ? '认证' : 'Verified'}
                        </span>
                      </div>
                    )}

                    {/* ── 6-step status timeline (accepted only) ── */}
                    {isAccepted && <SupplierStatusTimeline step={step} />}

                    {/* ── Payment pills (accepted only) ── */}
                    {isAccepted && <SupplierPaymentPills requestStatus={reqStatus} paymentPlan={o.requests?.payment_plan} />}

                    {/* ── Price grid ── */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 14 }}>
                      <div style={{ padding: '10px 14px', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                        <p style={{ fontSize: 10, color: 'var(--text-disabled)', marginBottom: 4 }}>{isAr ? 'سعر الوحدة' : lang === 'zh' ? '单价' : 'Unit Price'}</p>
                        <p style={{ fontSize: 18, fontWeight: 300, color: 'var(--text-primary)', lineHeight: 1.1, fontVariantNumeric: 'lining-nums' }}>
                          {parseFloat(o.price || 0).toFixed(2)} <span style={{ fontSize: 11, color: 'var(--text-disabled)' }}>{normalizeDisplayCurrency(o.currency || viewerCurrency)}</span>
                        </p>
                        {hasOfferShippingCost(o) && (
                          <p style={{ fontSize: 10, color: 'var(--text-disabled)', marginTop: 3 }}>
                            {isAr ? 'شحن: ' : lang === 'zh' ? '运费：' : 'Ship: '}<span style={{ fontVariantNumeric: 'lining-nums' }}>{formatPriceWithConversion({
                              amount: getOfferShippingCost(o),
                              sourceCurrency: o.currency || viewerCurrency,
                              displayCurrency: viewerCurrency,
                              rates: exchangeRates,
                              lang,
                            })}</span>
                          </p>
                        )}
                      </div>
                      {isAccepted && o.requests?.quantity && (
                        <div style={{ padding: '10px 14px', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                          <p style={{ fontSize: 10, color: 'var(--text-disabled)', marginBottom: 4 }}>{isAr ? 'الكمية' : lang === 'zh' ? '数量' : 'Quantity'}</p>
                          <p style={{ fontSize: 18, fontWeight: 300, color: 'var(--text-primary)', lineHeight: 1.1, fontVariantNumeric: 'lining-nums' }}>{o.requests.quantity}</p>
                        </div>
                      )}
                      <div style={{ padding: '10px 14px', background: 'var(--bg-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
                        <p style={{ fontSize: 10, color: 'var(--text-disabled)', marginBottom: 4 }}>{isAr ? 'الإجمالي التقديري' : lang === 'zh' ? '预计总额' : 'Est. Total'}</p>
                        <p style={{ fontSize: 18, fontWeight: 300, color: 'var(--text-primary)', lineHeight: 1.1, fontVariantNumeric: 'lining-nums' }}>
                          {formatPriceWithConversion({
                            amount: total,
                            sourceCurrency: o.currency || viewerCurrency,
                            displayCurrency: viewerCurrency,
                            rates: exchangeRates,
                            lang,
                          })}
                        </p>
                        {o.moq && <p style={{ fontSize: 10, color: 'var(--text-disabled)', marginTop: 3 }}>MOQ {o.moq} · {o.delivery_days}{isAr ? ' يوم' : lang === 'zh' ? ' 天' : 'd'}{o.origin ? ` · ${o.origin}` : ''}</p>}
                      </div>
                    </div>

                    {/* ── Section 5: payment plan (accepted only) ── */}
                    {isAccepted && o.requests?.payment_plan && (
                      <div style={{ marginBottom: 14, padding: '10px 14px', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                        <p style={{ fontSize: 10, color: 'var(--text-disabled)', marginBottom: 3 }}>{isAr ? 'خطة الدفع' : lang === 'zh' ? '付款计划' : 'Payment Plan'}</p>
                        <p style={{ fontSize: 12, color: 'var(--text-primary)', fontVariantNumeric: 'lining-nums', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {/* The shared wording (supplierRequestTerms): a full plan
                              reads "100% upfront", not "100% + 0% on shipping",
                              and a stored legacy value still reads correctly. */}
                          {termValue(o.requests, 'paymentPlan')}
                        </p>
                      </div>
                    )}

                    {/* ── Section 6: shipment details (shipping / arrived / delivered only) ── */}
                    {isAccepted && isShipping && o.requests?.tracking_number && (
                      <div style={{ marginBottom: 14, padding: '12px 16px', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)' }}>
                        <p style={{ fontSize: 10, color: 'var(--text-disabled)', marginBottom: 8, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                          {isAr ? 'تفاصيل الشحنة' : lang === 'zh' ? '物流信息' : 'Shipment'}
                        </p>
                        {o.requests?.shipping_company && (
                          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                            {isAr ? 'شركة الشحن: ' : lang === 'zh' ? '承运商：' : 'Carrier: '}{o.requests.shipping_company}
                          </p>
                        )}
                        <p style={{ fontSize: 12, color: 'var(--text-primary)', fontFamily: 'var(--font-sans)', marginBottom: 4 }}>
                          <span style={{ color: 'var(--text-disabled)' }}>{isAr ? 'رقم التتبع: ' : lang === 'zh' ? '追踪号：' : 'Tracking: '}</span>
                          {o.requests.tracking_number}
                        </p>
                        {(() => {
                          const etaDate = o.requests?.estimated_delivery
                            ? new Date(o.requests.estimated_delivery)
                            : o.delivery_days && o.updated_at
                              ? (() => { const d = new Date(o.updated_at); d.setDate(d.getDate() + Number(o.delivery_days)); return d; })()
                              : null;
                          return etaDate ? (
                            <p style={{ fontSize: 11, color: 'var(--text-disabled)', fontVariantNumeric: 'lining-nums' }}>
                              {isAr ? 'التسليم المتوقع: ' : lang === 'zh' ? '预计到达：' : 'ETA: '}
                              {etaDate.toLocaleDateString(isAr ? 'ar-SA-u-nu-latn' : lang === 'zh' ? 'zh-CN' : 'en-US')}
                            </p>
                          ) : null;
                        })()}
                      </div>
                    )}

                    {/* ── Action area ── */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

                      {/* Pending offer: edit / cancel / delete.

                          ⚠️ EDIT AND CANCEL OFFER DISAPPEAR WHEN THE REQUEST
                          ITSELF IS CANCELLED. Neither means anything once the
                          request is over: nobody will read an edited price, and
                          there is nothing left to withdraw from. Same condition
                          as the badge above, deliberately, so the card cannot
                          say "request cancelled" while offering to amend it.

                          DELETE STAYS (owner, 2026-09-20). It is the supplier's
                          own row and they may want to tidy their list -- unlike
                          the trader's delete, which destroyed someone else's
                          work. Removing a record of your own quote harms
                          nobody. */}
                      {isPending && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {reqStatus !== 'cancelled' && (
                          <button
                            onClick={() => {
                              setEditOfferModal(o);
                              // The SHARED quote-core shape, same keys the
                              // create form uses. shippingCost/shippingMethod
                              // are gone and origin is NOT defaulted to
                              // 'China' — opening the editor must not invent
                              // a compliance value the supplier never chose.
                              setEditOfferForm({
                                price: String(o.price ?? ''),
                                // The offer's OWN currency: an older SAR/CNY
                                // offer keeps it, shown locked by QuoteFields.
                                currency: o.currency || 'USD',
                                moq: o.moq || '',
                                leadTime: String(o.delivery_days ?? ''),
                                origin: o.origin || '',
                                note: o.note || '',
                                priceBasis: o.price_basis || '',
                                validDays: o.valid_days || 30,
                                sampleAvailable: o.sample_available ?? false,
                                samplePrice: o.sample_price === null || o.sample_price === undefined ? '' : String(o.sample_price),
                                sampleDays: o.sample_days === null || o.sample_days === undefined ? '' : String(o.sample_days),
                              });
                            }}
                            className="btn-outline"
                            style={{ padding: '5px 12px', fontSize: 11, minHeight: 32 }}>
                            {t.edit}
                          </button>
                          )}
                          {reqStatus !== 'cancelled' && (
                          <button onClick={() => cancelOffer(o)} style={{ background: 'none', border: '1px solid rgba(224,92,92,0.3)', color: 'var(--red)', padding: '5px 12px', fontSize: 11, cursor: 'pointer', borderRadius: 'var(--radius-md)', minHeight: 32 }}>
                            {isAr ? 'إلغاء العرض' : lang === 'zh' ? '取消报价' : 'Cancel Offer'}
                          </button>
                          )}
                          <button onClick={() => deleteOffer(o)} style={{ background: 'none', border: '1px solid rgba(224,92,92,0.3)', color: 'var(--red)', padding: '5px 12px', fontSize: 11, cursor: 'pointer', borderRadius: 'var(--radius-md)', minHeight: 32 }}>
                            {t.delete}
                          </button>
                        </div>
                      )}

                      {/* Section 7a: accepted (closed) — supplier confirms readiness */}
                      {isAccepted && reqStatus === 'closed' && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            className="btn-primary"
                            style={{ minHeight: 40, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', fontSize: 13 }}
                            onClick={async () => {
                              await sb.from('requests').update({ status: 'supplier_confirmed' }).eq('id', o.request_id);
                              await sb.from('notifications').insert({
                                user_id: o.requests.buyer_id,
                                type: 'supplier_confirmed',
                                title_ar: 'المورد جاهز — يمكنك الآن إتمام الدفع',
                                title_en: 'Supplier is ready — you can now complete payment',
                                title_zh: '供应商已准备好 — 您现在可以付款',
                                ref_id: o.request_id,
                                is_read: false,
                              });
                              loadMyOffers();
                            }}>
                            {isAr ? 'جاهز للمتابعة — أبلغ التاجر' : lang === 'zh' ? '已就绪 — 通知买家' : 'Ready — notify buyer'}
                          </button>
                          {o.requests?.buyer_id && (
                            <button className="btn-outline" onClick={() => nav(`/chat/${o.requests.buyer_id}`)} style={{ minHeight: 40, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                              {t.contactTrader}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Section 7a: supplier_confirmed — waiting for buyer first payment */}
                      {isAccepted && reqStatus === 'supplier_confirmed' && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <p style={{ fontSize: 12, color: 'var(--text-disabled)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', flex: 1 }}>
                            {isAr ? 'في انتظار دفع التاجر' : lang === 'zh' ? '等待买家付款' : 'Awaiting buyer payment'}
                          </p>
                          {o.requests?.buyer_id && (
                            <button className="btn-outline" onClick={() => nav(`/chat/${o.requests.buyer_id}`)} style={{ minHeight: 38, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                              {t.contactTrader}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Section 7b: paid. CHANGED 2026-09-28 (Phase 1): tracking —
                          which ships the order — only when it is PAID IN FULL.
                          After a deposit only, the supplier marks it ready to
                          ship; that is when the 70% balance falls due (settled
                          decision, PROJECT_STATE.md). Mirrors the app. */}
                      {isAccepted && reqStatus === 'paid' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {isFullyPaidOffer(o) ? (
                          <div style={{ padding: '14px 16px', background: 'rgba(45,122,79,0.05)', border: '1px solid rgba(45,122,79,0.20)', borderRadius: 'var(--radius-lg)' }}>
                            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--green)', marginBottom: 10, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                              {isAr ? 'مدفوع بالكامل — أضف رقم التتبع' : lang === 'zh' ? '已全额付款 — 添加追踪号' : 'Paid in full — add tracking number'}
                            </p>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <select
                                value={shippingCompany}
                                onChange={e => setShippingCompany(e.target.value)}
                                style={{ padding: '8px 12px', fontSize: 12, border: '1px solid var(--border-subtle)', background: 'var(--bg-subtle)', color: 'var(--text-secondary)', borderRadius: 'var(--radius-md)', cursor: 'pointer', outline: 'none', minHeight: 38 }}>
                                {['DHL','FedEx','Aramex','UPS','SMSA', isAr ? 'أخرى' : lang === 'zh' ? '其他' : 'Other'].map(c => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                              </select>
                              <input className="form-input" style={{ flex: 1, minWidth: 120 }} placeholder={t.trackingNum} value={trackingInputs[o.request_id] || ''} onChange={e => setTrackingInputs(prev => ({ ...prev, [o.request_id]: e.target.value }))} dir="ltr" />
                              <button className="btn-dark-sm" onClick={() => submitTracking(o.request_id, o.requests?.buyer_id, o.delivery_days, reqStatus)} style={{ minHeight: 38 }}>{t.send}</button>
                            </div>
                          </div>
                          ) : (
                          <div style={{ padding: '14px 16px', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)' }}>
                            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.7, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                              {isAr ? 'استلمت الدفعة الأولى. عند جهوز البضاعة للشحن، أبلغ التاجر ليدفع الدفعة الثانية.' : lang === 'zh' ? '已收到首付款。货物备好后，通知买家支付尾款。' : 'Deposit received. When the goods are ready to ship, tell the trader so they pay the balance.'}
                            </p>
                            <button className="btn-dark-sm" onClick={() => markReadyToShip(o)} style={{ minHeight: 38, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                              {isAr ? 'البضاعة جاهزة للشحن' : lang === 'zh' ? '货物已备好发货' : 'Goods ready to ship'}
                            </button>
                          </div>
                          )}
                          {o.requests?.buyer_id && (
                            <button className="btn-outline" onClick={() => nav(`/chat/${o.requests.buyer_id}`)} style={{ minHeight: 38, alignSelf: 'flex-start', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                              {t.contactTrader}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Section 7b: ready_to_ship, NOT paid in full — the balance
                          is due; nothing to ship yet (2026-09-28). */}
                      {isAccepted && reqStatus === 'ready_to_ship' && !isFullyPaidOffer(o) && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <p style={{ fontSize: 12, color: 'var(--warn)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', flex: 1 }}>
                            {isAr ? 'في انتظار دفعة التاجر الثانية' : lang === 'zh' ? '等待买家支付尾款' : 'Awaiting second payment from buyer'}
                          </p>
                          {o.requests?.buyer_id && (
                            <button className="btn-outline" onClick={() => nav(`/chat/${o.requests.buyer_id}`)} style={{ minHeight: 38, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                              {t.contactTrader}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Section 7b: tracking, once PAID IN FULL — ready_to_ship
                          after a 100% payment, or shipping (the balance was paid
                          and telr-verify moved it there) without tracking yet. */}
                      {isAccepted && !o.requests?.tracking_number && (
                        (reqStatus === 'ready_to_ship' && isFullyPaidOffer(o)) || reqStatus === 'shipping'
                      ) && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <select
                            value={shippingCompany}
                            onChange={e => setShippingCompany(e.target.value)}
                            style={{ padding: '8px 12px', fontSize: 12, border: '1px solid var(--border-subtle)', background: 'var(--bg-subtle)', color: 'var(--text-secondary)', borderRadius: 'var(--radius-md)', cursor: 'pointer', outline: 'none', minHeight: 38 }}>
                            {['DHL','FedEx','Aramex','UPS','SMSA', isAr ? 'أخرى' : lang === 'zh' ? '其他' : 'Other'].map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                          <input className="form-input" style={{ flex: 1, minWidth: 120 }} placeholder={t.trackingNum} value={trackingInputs[o.request_id] || ''} onChange={e => setTrackingInputs(prev => ({ ...prev, [o.request_id]: e.target.value }))} dir="ltr" />
                          <button className="btn-dark-sm" onClick={() => submitTracking(o.request_id, o.requests?.buyer_id, o.delivery_days, reqStatus)} style={{ minHeight: 38 }}>{t.send}</button>
                        </div>
                      )}

                      {/* Section 7c: shipping WITH tracking — shipped. It used to say
                          "awaiting second payment", which is backwards: an offer
                          order reaches shipping only once the balance is paid. */}
                      {isAccepted && reqStatus === 'shipping' && o.requests?.tracking_number && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <p style={{ fontSize: 12, color: 'var(--green)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', flex: 1 }}>
                            {isAr ? 'تم الشحن — مدفوع بالكامل' : lang === 'zh' ? '已发货 — 已全额付款' : 'Shipped — paid in full'}
                          </p>
                          {o.requests?.buyer_id && (
                            <button className="btn-outline" onClick={() => nav(`/chat/${o.requests.buyer_id}`)} style={{ minHeight: 38, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                              {t.contactTrader}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Section 7d: arrived / delivered — order complete, no button */}
                      {isAccepted && ['arrived', 'delivered'].includes(reqStatus) && (
                        <p style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? '✓ الطلب مكتمل — تم تحويل كامل المبلغ' : lang === 'zh' ? '✓ 订单已完成 — 全款已转账' : '✓ Order complete — full amount transferred'}
                        </p>
                      )}

                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* MESSAGES: no tab here. /inbox is the one list — it covers factory
              conversations too, which this tab never could. */}

          {/* ── ADD PRODUCT ── */}
          {/* ── SAMPLES ── */}
          {!isRestrictedSupplierTab && activeTab === 'samples' && (
            <div style={section}>
              <BackBtn onClick={() => setActiveTab('overview')} label={t.back} />
              <h2 style={{ fontSize: isAr ? 28 : 34, fontWeight: 300, marginBottom: 32, color: 'var(--text-primary)', ...arFont, letterSpacing: isAr ? 0 : -0.5 }}>
                {isAr ? 'طلبات العينات' : lang === 'zh' ? '样品请求' : 'Sample Requests'}
              </h2>
              {samples.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '80px 0', borderTop: '1px solid var(--border-subtle)' }}>
                  <p style={{ color: 'var(--text-disabled)', fontSize: 14, ...arFont }}>{isAr ? 'لا توجد طلبات عينات بعد' : lang === 'zh' ? '暂无样品申请' : 'No sample requests yet'}</p>
                </div>
              ) : samples.map((s, idx) => (
                <div key={s.id} style={{ borderTop: '1px solid var(--border-subtle)', padding: '20px 0', animation: `fadeIn 0.35s ease ${idx*0.04}s both` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6, ...arFont }}>
                        {lang === 'zh' ? s.products?.name_zh : lang === 'ar' ? s.products?.name_ar : s.products?.name_en}
                      </p>
                      <p style={{ fontSize: 12, color: 'var(--text-disabled)', marginBottom: 4 }}>
                        {s.profiles?.company_name || s.profiles?.full_name || traderLabel(lang)} · {isAr ? `الكمية: ${s.quantity}` : lang === 'zh' ? `数量：${s.quantity}` : `Qty: ${s.quantity}`}
                      </p>
                      <p style={{ fontSize: 12, color: 'var(--text-disabled)' }}>
                        {s.sample_price} SAR {isAr ? '+ شحن' : lang === 'zh' ? '+ 运费' : '+ shipping'} {s.shipping_price} SAR
                      </p>
                      {s.notes && <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, ...arFont }}>{s.notes}</p>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 'var(--radius-pill)', border: '1px solid', borderColor: s.status==='approved' ? 'rgba(45,122,79,0.3)' : s.status==='rejected' ? 'rgba(224,92,92,0.3)' : 'var(--border-subtle)', color: s.status==='approved' ? 'var(--green)' : s.status==='rejected' ? 'var(--red)' : 'var(--text-disabled)' }}>
                        {s.status === 'approved' ? (isAr ? 'مقبول' : lang === 'zh' ? '已通过' : 'Approved') : s.status === 'rejected' ? (isAr ? 'مرفوض' : lang === 'zh' ? '已拒绝' : 'Rejected') : (isAr ? 'قيد المراجعة' : lang === 'zh' ? '审核中' : 'Pending')}
                      </span>
                      {s.status === 'pending' && <>
                        <button onClick={() => updateSampleStatus(s.id, 'approved')} className="btn-dark-sm" style={{ fontSize: 11, minHeight: 28, padding: '4px 12px' }}>{isAr ? 'قبول' : lang === 'zh' ? '通过' : 'Approve'}</button>
                        <button onClick={() => updateSampleStatus(s.id, 'rejected')} style={{ background: 'none', border: '1px solid rgba(224,92,92,0.3)', color: 'var(--red)', padding: '4px 12px', fontSize: 11, cursor: 'pointer', borderRadius: 'var(--radius-md)', minHeight: 28 }}>{isAr ? 'رفض' : lang === 'zh' ? '拒绝' : 'Reject'}</button>
                      </>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── PRODUCT INQUIRIES ── */}
          {!isRestrictedSupplierTab && activeTab === 'product-inquiries' && (
            <div style={section}>
              <BackBtn onClick={() => setActiveTab('overview')} label={t.back} />
              <h2 style={{ fontSize: isAr ? 28 : 34, fontWeight: 300, marginBottom: 14, color: 'var(--text-primary)', ...arFont, letterSpacing: isAr ? 0 : -0.5 }}>
                {isAr ? 'استفسارات المنتجات' : lang === 'zh' ? '产品咨询' : 'Product Inquiries'}
              </h2>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: 28, maxWidth: 760, ...arFont }}>
                {isAr
                  ? 'هذه الاستفسارات تأتي مباشرة من صفحات المنتجات الجاهزة. ردك هنا يُحفظ داخل النظام ويُرسل أيضًا إلى بريد المشتري.'
                  : lang === 'zh'
                    ? '这些咨询直接来自现货产品页面。您在这里的回复会保存到系统内，并同步发送到买家邮箱。'
                    : 'These inquiries come directly from ready-product pages. Your reply here is saved inside the system and also emailed to the buyer.'}
              </p>

              {productInquiries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '80px 0', borderTop: '1px solid var(--border-subtle)' }}>
                  <p style={{ color: 'var(--text-disabled)', fontSize: 14, ...arFont }}>{isAr ? 'لا توجد استفسارات منتجات بعد' : lang === 'zh' ? '暂时没有产品咨询' : 'No product inquiries yet'}</p>
                </div>
              ) : productInquiries.map((inquiry, idx) => {
                const buyerName = inquiry.profiles?.company_name || inquiry.profiles?.full_name || (isAr ? 'تاجر' : lang === 'zh' ? '买家' : 'Buyer');
                const productName = getProductInquiryProductName(inquiry.products, lang);
                const statusLabel = getProductInquiryStatusLabel(inquiry.status, lang, 'supplier');
                const replies = inquiry.product_inquiry_replies || [];
                const lastReply = replies[replies.length - 1] || null;
                return (
                  <div key={inquiry.id} style={{ borderTop: '1px solid var(--border-subtle)', padding: '22px 0', animation: `fadeIn 0.35s ease ${idx * 0.04}s both` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 12 }}>
                      <div>
                        <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6, ...arFont }}>{productName}</p>
                        <p style={{ fontSize: 12, color: 'var(--text-disabled)', marginBottom: 4 }}>{buyerName}</p>
                        <p style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.8, margin: 0, ...arFont }}>
                          {translatedInquiries[inquiry.id] || inquiry.question_text}
                        </p>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: isAr ? 'flex-start' : 'flex-end', gap: 6 }}>
                        <span style={{ fontSize: 10, padding: '4px 10px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border-subtle)', color: inquiry.status === 'answered' ? 'var(--green)' : 'var(--text-secondary)' }}>
                          {statusLabel}
                        </span>
                        <p style={{ fontSize: 11, color: 'var(--text-disabled)', margin: 0 }}>{new Date(inquiry.updated_at || inquiry.created_at).toLocaleDateString(isAr ? 'ar-SA-u-nu-latn' : lang === 'zh' ? 'zh-CN' : 'en-US')}</p>
                      </div>
                    </div>

                    {lastReply && (
                      <div style={{ marginBottom: 14, padding: '12px 14px', borderRadius: 'var(--radius-lg)', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)' }}>
                        <p style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 6 }}>
                          {isAr ? 'آخر رد محفوظ' : lang === 'zh' ? '最近回复' : 'Latest saved reply'}
                        </p>
                        <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8, margin: 0, ...arFont }}>{lastReply.message}</p>
                      </div>
                    )}

                    <div className="form-group" style={{ marginBottom: 12 }}>
                      <label className={`form-label${isAr ? ' ar' : ''}`}>{isAr ? 'رد المورد' : lang === 'zh' ? '供应商回复' : 'Supplier Reply'}</label>
                      <textarea
                        className="form-input"
                        rows={3}
                        style={{ resize: 'vertical' }}
                        value={inquiryReplies[inquiry.id] || ''}
                        onChange={(event) => setInquiryReplies((current) => ({ ...current, [inquiry.id]: event.target.value }))}
                        placeholder={isAr ? 'اكتب الرد الذي سيظهر للمشتري داخل مَعبر ويصل إلى بريده' : lang === 'zh' ? '输入将保存到系统并同步发送到买家邮箱的回复' : 'Write the reply that will be saved in-system and emailed to the buyer'}
                      />
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button className="btn-dark-sm" onClick={() => submitInquiryReply(inquiry)} disabled={replyingInquiryId === inquiry.id}>
                        {replyingInquiryId === inquiry.id ? '...' : isAr ? 'إرسال الرد ←' : lang === 'zh' ? '发送回复 →' : 'Send Reply →'}
                      </button>
                      {inquiry.buyer_id && (
                        <button className="btn-outline" onClick={() => nav(`/chat/${inquiry.buyer_id}`)}>
                          {isAr ? 'فتح محادثة التاجر' : lang === 'zh' ? '打开买家聊天' : 'Open Buyer Chat'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── REVIEWS ── */}
          {!isRestrictedSupplierTab && activeTab === 'reviews' && (
            <div style={section}>
              <BackBtn onClick={() => setActiveTab('overview')} label={t.back} />
              <h2 style={{ fontSize: isAr ? 28 : 34, fontWeight: 300, marginBottom: 32, color: 'var(--text-primary)', ...arFont, letterSpacing: isAr ? 0 : -0.5 }}>
                {isAr ? 'تقييماتي' : lang === 'zh' ? '我的评价' : 'My Reviews'}
              </h2>
              {myReviews.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '80px 0', borderTop: '1px solid var(--border-subtle)' }}>
                  <p style={{ color: 'var(--text-disabled)', fontSize: 14, ...arFont }}>{isAr ? 'لا توجد تقييمات بعد' : lang === 'zh' ? '暂时没有评价' : 'No reviews yet'}</p>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 32 }}>
                    <p style={{ fontSize: 48, fontWeight: 300, color: 'var(--text-primary)', lineHeight: 1 }}>
                      {(myReviews.reduce((s,r) => s+r.rating, 0) / myReviews.length).toFixed(1)}
                    </p>
                    <div>
                      <div style={{ display: 'flex', gap: 2, marginBottom: 4 }}>
                        {[1,2,3,4,5].map(s => <span key={s} style={{ fontSize: 16, color: s <= Math.round(myReviews.reduce((sum,r)=>sum+r.rating,0)/myReviews.length) ? 'var(--warn)' : 'var(--border-default)' }}>★</span>)}
                      </div>
                      <p style={{ fontSize: 11, color: 'var(--text-disabled)', letterSpacing: 1 }}>{myReviews.length} {isAr ? 'تقييم' : lang === 'zh' ? '条评价' : 'reviews'}</p>
                    </div>
                  </div>
                  {myReviews.map((r, idx) => (
                    <div key={r.id} style={{ borderTop: '1px solid var(--border-subtle)', padding: '18px 0', animation: `fadeIn 0.35s ease ${idx*0.04}s both` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        {/* Anonymous by design — the same label on every review screen. */}
                        <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{reviewerLabel(lang)}</p>
                        <div style={{ display: 'flex', gap: 1 }}>
                          {[1,2,3,4,5].map(s => <span key={s} style={{ fontSize: 12, color: s <= r.rating ? 'var(--warn)' : 'var(--border-default)' }}>★</span>)}
                        </div>
                      </div>
                      {r.comment && <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, ...arFont }}>{r.comment}</p>}
                      <p style={{ fontSize: 11, color: 'var(--text-disabled)', marginTop: 6 }}>{new Date(r.created_at).toLocaleDateString(isAr ? 'ar-SA-u-nu-latn' : lang === 'zh' ? 'zh-CN' : 'en-US')}</p>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {!isRestrictedSupplierTab && activeTab === 'add-product' && (
            <div style={section}>
              <BackBtn onClick={() => setActiveTab('overview')} label={t.back} />
              <h2 style={{ fontSize: isAr ? 28 : 34, fontWeight: 300, marginBottom: 32, color: 'var(--text-primary)', ...arFont, letterSpacing: isAr ? 0 : -0.5 }}>{t.addProductTitle}</h2>
              {productComposerStep === 'preview' ? (
                <ProductPreviewPanel product={normalizeProductDraftMedia(product)} onPublish={addProduct} onBack={() => setProductComposerStep('edit')} t={t} isAr={isAr} saving={saving} lang={lang} certificationsCount={(product.certifications || []).length} />
              ) : (
                <ProductForm data={product} setData={setProduct} onSave={addProduct} onSaveDraft={() => addProduct(true)} onPreview={openProductPreview} showPreviewAction imgRef={imageRef} vidRef={videoRef} onImgChange={e => handleImageUpload(e, false)} onVidChange={e => handleVideoUpload(e, false)} onRemoveImage={index => removeImageAt(index, false)} onRemoveVideo={() => removeVideo(false)} onCancel={() => setActiveTab('overview')} uploadingImage={uploadingImage} uploadingVideo={uploadingVideo} t={t} isAr={isAr} saving={saving} usdRate={usdRate} categories={cats} lang={lang} variantData={variantData} setVariantData={setVariantData} certificationsCount={(product.certifications || []).length} />
              )}
              {productSaveMsg && (
                <p style={{ marginTop: 12, fontSize: 13, color: ([t.productSavedWithFallback, 'تم إضافة المنتج بنجاح', '产品添加成功', 'Product added successfully'].includes(productSaveMsg) ? (productSaveMsg === t.productSavedWithFallback ? 'var(--warn)' : 'var(--green)') : 'var(--red)'), fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                  {productSaveMsg}
                </p>
              )}
            </div>
          )}

          {/* ── VERIFICATION ── */}
         {/* ── VERIFICATION ── */}
          {activeTab === 'verification' && (
            <div style={{ ...section, background: 'var(--bg-page)', minHeight: '100vh' }}>
              <style>{VF_CSS}</style>

              {/* Progress bar */}
              <VfProgressBar
                step={verificationStep}
                status={showVfSuccess ? 'success' : isVerificationLocked ? 'review' : 'form'}
              />

              <div style={{ maxWidth: 580, margin: '0 auto', padding: '44px 24px 80px' }}>

                {/* ── UNDER REVIEW ── */}
                {isVerificationLocked && !showVfSuccess && (
                  <div>
                    <div className="vf-fu" style={{ marginBottom: 36 }}>
                      <p style={{ fontSize: 11, color: VF_C.amber, fontFamily: 'var(--font-ar)', fontWeight: 400, marginBottom: 12 }}>
                        VERIFICATION_UNDER_REVIEW
                      </p>
                      <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 38, fontWeight: 300, color: VF_C.ink, letterSpacing: -0.8, lineHeight: 1.2, marginBottom: 14 }}>
                        {isAr ? 'التحقق الآن' : lang === 'zh' ? '认证正在' : 'Verification'}
                        <br />
                        <em style={{ fontStyle: 'italic', color: VF_C.ink60 }}>
                          {isAr ? 'تحت المراجعة' : lang === 'zh' ? '审核中' : 'Under Review'}
                        </em>
                      </h1>
                      <p style={{ fontSize: 15, lineHeight: 1.9, color: VF_C.ink60, fontWeight: 300, fontFamily: 'var(--font-ar)' }}>
                        {isAr
                          ? 'تم استلام التحقق بنجاح. لن يظهر النموذج القابل للتعديل مرة أخرى أثناء المراجعة.'
                          : lang === 'zh'
                            ? '认证资料已成功提交。审核期间表单将保持锁定。'
                            : 'Your verification was received. The form stays locked during review.'}
                      </p>
                    </div>

                    <div className="vf-fu" style={{ marginBottom: 32, animationDelay: '0.08s' }}>
                      <VfStepBadges currentState="review" isAr={isAr} lang={lang} />
                    </div>

                    {/* Submitted data */}
                    <div className="vf-fu" style={{ marginBottom: 28, animationDelay: '0.14s' }}>
                      <p style={{ fontSize: 11, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 400, marginBottom: 10 }}>
                        {isAr ? 'البيانات المُرسلة' : lang === 'zh' ? '已提交资料' : 'Submitted data'}
                      </p>
                      <div style={{ background: VF_C.white, border: `1px solid ${VF_C.ink10}`, borderRadius: 'var(--radius-panel)', overflow: 'hidden' }}>
                        {[
                          [isAr ? 'اسم الشركة' : lang === 'zh' ? '公司名称' : 'Company', settings.company_name || '—'],
                          [isAr ? 'المدينة / الدولة' : lang === 'zh' ? '城市 / 国家' : 'City / Country', `${settings.city || '—'} · ${settings.country || '—'}`],
                          [isAr ? 'التخصص' : lang === 'zh' ? '专业' : 'Specialty', getSpecialtyLabel(settings.speciality, lang) || '—'],
                          ['WeChat', settings.wechat || '—'],
                          [isAr ? 'رابط المتجر' : lang === 'zh' ? '店铺链接' : 'Trade link', settings.trade_link || '—'],
                          [isAr ? 'رقم التسجيل' : lang === 'zh' ? '注册号' : 'Reg. number', verification.reg_number || '—'],
                          [isAr ? 'سنوات الخبرة' : lang === 'zh' ? '从业年限' : 'Experience', verification.years_experience ? `${verification.years_experience} ${isAr ? 'سنوات' : lang === 'zh' ? '年' : 'yrs'}` : '—'],
                          [isAr ? 'رخصة الأعمال' : lang === 'zh' ? '营业执照' : 'License', verification.license_photo ? (isAr ? 'مرفوعة ✓' : lang === 'zh' ? '已上传 ✓' : 'Uploaded ✓') : '—'],
                          [isAr ? 'صور المصنع' : lang === 'zh' ? '工厂图片' : 'Factory photos', verificationImages.length > 0 ? `${verificationImages.length} ${isAr ? 'صور ✓' : lang === 'zh' ? '张 ✓' : 'photos ✓'}` : '—'],
                          ...(verificationVideos.length > 0 ? [[isAr ? 'فيديو المصنع' : lang === 'zh' ? '工厂视频' : 'Factory video', `${verificationVideos.length} ${isAr ? 'فيديو ✓' : lang === 'zh' ? '个 ✓' : 'video ✓'}`]] : []),
                        ].map(([k, v]) => (
                          <div key={k} className="vf-info-row">
                            <span style={{ fontSize: 13, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 300, flexShrink: 0 }}>{k}</span>
                            <span style={{ fontSize: 13, color: VF_C.ink, fontFamily: 'var(--font-ar)', fontWeight: 400 }}>{v}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Locked features */}
                    <div className="vf-fu" style={{ marginBottom: 28, animationDelay: '0.2s' }}>
                      <p style={{ fontSize: 11, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 400, marginBottom: 10 }}>
                        {isAr ? 'مقفل حتى الاعتماد' : lang === 'zh' ? '通过前保持锁定' : 'Locked until approval'}
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {[
                          isAr ? 'إضافة المنتجات' : lang === 'zh' ? '添加产品' : 'Add products',
                          isAr ? 'طلبات التجار'   : lang === 'zh' ? '采购需求'  : 'Trader requests',
                          isAr ? 'تقديم العروض'   : lang === 'zh' ? '提交报价'  : 'Submit offers',
                          isAr ? 'الرسائل'         : lang === 'zh' ? '消息'      : 'Messages',
                        ].map(f => (
                          <span key={f} style={{ padding: '7px 14px', borderRadius: 'var(--radius-chip)', border: `1px solid ${VF_C.ink10}`, background: VF_C.paper, fontSize: 13, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 300 }}>
                            {f} · {isAr ? 'مقفل' : lang === 'zh' ? '锁定' : 'Locked'}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* What's next */}
                    <div className="vf-fu" style={{ marginBottom: 24, animationDelay: '0.26s' }}>
                      <div style={{ background: VF_C.white, border: `1px solid ${VF_C.ink10}`, borderRadius: 'var(--radius-panel)', overflow: 'hidden' }}>
                        {[
                          { title: isAr ? 'راقب بريدك الإلكتروني' : lang === 'zh' ? '关注邮箱通知' : 'Watch your email',
                            body:  isAr ? 'ستصلك رسالة فور اعتماد حسابك من فريق مَعبر' : lang === 'zh' ? '账户通过后系统会自动发邮件通知您' : 'You will get an email as soon as your account is approved' },
                          { title: isAr ? 'المدة المتوقعة' : lang === 'zh' ? '预计时间' : 'Expected time',
                            body:  isAr ? 'عادةً خلال 72 ساعة من وقت الإرسال' : lang === 'zh' ? '通常在提交后 72 小时内完成' : 'Usually within 72 hours of submission' },
                          { title: isAr ? 'هل تحتاج مساعدة؟' : lang === 'zh' ? '需要帮助？' : 'Need help?',
                            body:  isAr ? 'تواصل معنا على support@maabar.io' : lang === 'zh' ? '联系我们：support@maabar.io' : 'Contact us at support@maabar.io' },
                        ].map(({ title, body }, i) => (
                          <div key={i} className="vf-next-item" style={{ borderBottom: i < 2 ? `1px solid ${VF_C.ink05}` : 'none' }}>
                            <div style={{ width: 6, height: 6, borderRadius: 'var(--radius-full)', background: VF_C.ink10, marginTop: 7, flexShrink: 0 }} />
                            <div>
                              <p style={{ fontSize: 14, color: VF_C.ink, fontFamily: 'var(--font-ar)', fontWeight: 500, marginBottom: 3 }}>{title}</p>
                              <p style={{ fontSize: 13, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 300, lineHeight: 1.6 }}>{body}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Amber note */}
                    <div className="vf-fu" style={{ animationDelay: '0.32s', padding: '14px 18px', borderRadius: 'var(--radius-panel)', background: VF_C.amberBg, border: `1px solid ${VF_C.amberBr}` }}>
                      <p style={{ fontSize: 13, color: VF_C.amber, fontFamily: 'var(--font-ar)', fontWeight: 400, lineHeight: 1.7 }}>
                        {isAr
                          ? 'يتم حفظ ملفاتك داخل تخزين خاص ولا تُعرض بروابط عامة. بياناتك في أمان.'
                          : lang === 'zh'
                            ? '您的文件存储在私有空间，不会通过公开链接暴露。数据安全有保障。'
                            : 'Your files are stored in private storage and never exposed through public URLs.'}
                      </p>
                    </div>
                  </div>
                )}

                {/* ── SUCCESS SCREEN ── */}
                {showVfSuccess && (
                  <div className="vf-fi" style={{ textAlign: 'center', paddingTop: 8 }}>
                    <div style={{ width: 68, height: 68, borderRadius: 'var(--radius-full)', background: VF_C.sageBg, border: `1.5px solid ${VF_C.sageBr}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', animation: 'vf-ringPulse 1.2s ease 0.4s' }}>
                      <VfChk size={28} color={VF_C.sage} />
                    </div>
                    <p style={{ fontSize: 11, color: VF_C.sage, fontFamily: 'var(--font-ar)', fontWeight: 400, marginBottom: 12 }}>
                      {isAr ? 'تم الإرسال بنجاح' : lang === 'zh' ? '提交成功' : 'Submitted successfully'}
                    </p>
                    <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 40, fontWeight: 300, color: VF_C.ink, letterSpacing: -1, lineHeight: 1.15, marginBottom: 16 }}>
                      {isAr ? 'طلبك في أيدي' : lang === 'zh' ? '您的申请已交到' : 'Your request is in the hands of'}
                      <br />
                      <em style={{ fontStyle: 'italic', color: VF_C.ink60 }}>
                        {isAr ? 'فريق مَعبر' : lang === 'zh' ? 'Maabar 团队' : 'the Maabar team'}
                      </em>
                    </h1>
                    <p style={{ fontSize: 15, lineHeight: 1.9, color: VF_C.ink60, fontWeight: 300, maxWidth: 340, margin: '0 auto 36px', fontFamily: 'var(--font-ar)' }}>
                      {isAr
                        ? 'سنراجع طلبك ونتواصل معك خلال 72 ساعة.'
                        : lang === 'zh'
                          ? '我们会在 72 小时内审核并与您联系。'
                          : 'We will review your request and contact you within 72 hours.'}
                    </p>
                    <div style={{ marginBottom: 36 }}>
                      <VfStepBadges currentState="success" isAr={isAr} lang={lang} />
                    </div>
                    <div style={{ background: VF_C.white, border: `1px solid ${VF_C.ink10}`, borderRadius: 'var(--radius-panel)', overflow: 'hidden', padding: '0 20px', textAlign: isAr ? 'right' : 'left', marginBottom: 32 }}>
                      {[
                        { title: isAr ? 'راقب بريدك الإلكتروني' : lang === 'zh' ? '关注邮箱' : 'Watch your email',
                          body:  isAr ? 'ستصلك رسالة فور اعتماد حسابك' : lang === 'zh' ? '账户通过后会收到邮件' : 'You will be notified when approved' },
                        { title: isAr ? 'جهّز منتجاتك' : lang === 'zh' ? '准备您的产品' : 'Prepare your products',
                          body:  isAr ? 'يمكنك تجهيز قائمة منتجاتك وإضافتها فور الموافقة' : lang === 'zh' ? '可以提前准备产品列表，通过后立即发布' : 'You can prepare your product list and add them upon approval' },
                        { title: isAr ? 'تواصل معنا' : lang === 'zh' ? '联系我们' : 'Contact us',
                          body:  'support@maabar.io' },
                      ].map(({ title, body }, i) => (
                        <div key={i} style={{ display: 'flex', gap: 14, padding: '16px 0', borderBottom: i < 2 ? `1px solid ${VF_C.ink05}` : 'none', alignItems: 'flex-start' }}>
                          <div style={{ width: 6, height: 6, borderRadius: 'var(--radius-full)', background: VF_C.ink10, marginTop: 7, flexShrink: 0 }} />
                          <div>
                            <p style={{ fontSize: 14, color: VF_C.ink, fontFamily: 'var(--font-ar)', fontWeight: 500, marginBottom: 3 }}>{title}</p>
                            <p style={{ fontSize: 13, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 300, lineHeight: 1.6 }}>{body}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      className="vf-btn-ink"
                      onClick={() => setShowVfSuccess(false)}>
                      {isAr ? 'عرض حالة الطلب ←' : lang === 'zh' ? '查看申请状态 →' : 'View request status →'}
                    </button>
                  </div>
                )}

                {/* ── ACTIVE FORM ── */}
                {!isVerificationLocked && !showVfSuccess && (
                  <div>
                    <VfDotStepper step={verificationStep} />
                    <div className="vf-fu" style={{ marginBottom: 36 }}>
                      <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 38, fontWeight: 300, color: VF_C.ink, letterSpacing: -0.8, lineHeight: 1.2, marginBottom: 6 }}>
                        {verificationStep === 1
                          ? (isAr ? 'بيانات الشركة'      : lang === 'zh' ? '公司资料' : 'Company Profile')
                          : verificationStep === 2
                            ? (isAr ? 'ملفات التحقق'      : lang === 'zh' ? '认证文件' : 'Verification Files')
                            : (isAr ? 'المراجعة النهائية' : lang === 'zh' ? '最终确认' : 'Final Review')}
                      </h1>
                      <p style={{ fontSize: 13, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 300 }}>
                        {verificationStep === 1
                          ? (isAr ? 'معلومات شركتك — تُحفظ مباشرة في حسابك' : lang === 'zh' ? '公司信息将直接保存到您的账户' : 'Company info — saved directly to your account')
                          : verificationStep === 2
                            ? (isAr ? 'المستندات المطلوبة للتحقق من موثوقيتك' : lang === 'zh' ? '验证可信度所需的文件' : 'Documents required to verify your credibility')
                            : (isAr ? 'راجع كل شيء — لن يُرسل الطلب إلا عند تأكيدك' : lang === 'zh' ? '请确认所有内容后再提交' : 'Review everything — request only submits on confirmation')}
                      </p>
                    </div>

                    {/* Draft saved note */}
                    {verificationDraftSavedLabel && (
                      <div style={{ marginBottom: 18, padding: '10px 16px', borderRadius: 'var(--radius-panel)', border: `1px solid ${VF_C.ink10}`, background: VF_C.paper }}>
                        <p style={{ fontSize: 12, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 300 }}>
                          {isAr ? `يتم حفظ المسودة محلياً. آخر حفظ: ${verificationDraftSavedLabel}` : lang === 'zh' ? `草稿已自动保存。最近保存：${verificationDraftSavedLabel}` : `Draft saved locally. Last saved: ${verificationDraftSavedLabel}`}
                        </p>
                      </div>
                    )}

                    {/* Error / success messages */}
                    {verificationMsg && (
                      <div className="vf-fi" style={{ marginBottom: 20, padding: '12px 16px', borderRadius: 'var(--radius-panel)', border: `1px solid ${verificationSaved ? VF_C.sageBr : 'rgba(224,92,92,0.2)'}`, background: verificationSaved ? VF_C.sageBg : 'rgba(224,92,92,0.07)' }}>
                        <p style={{ fontSize: 13, color: verificationSaved ? VF_C.sage : 'var(--red)', fontFamily: 'var(--font-ar)', fontWeight: 400, lineHeight: 1.7 }}>{verificationMsg}</p>
                      </div>
                    )}

                    {/* ── STEP 1 ── */}
                    {verificationStep === 1 && (
                      <div>
                        <VfField label={isAr ? 'اسم الشركة *' : lang === 'zh' ? '公司名称 *' : 'Company Name *'} delay={0} invalid={isSettingsFieldMissing('company_name')}>
                          <input className="vf-input" value={settings.company_name || ''} onChange={e => setSettings(prev => ({ ...prev, company_name: e.target.value }))} />
                        </VfField>
                        <div style={{ height: 20 }} />
                        <VfG2>
                          <VfField label={isAr ? 'المدينة *' : lang === 'zh' ? '城市 *' : 'City *'} delay={0.05} invalid={isSettingsFieldMissing('city')}>
                            <input className="vf-input" value={settings.city || ''} onChange={e => setSettings(prev => ({ ...prev, city: e.target.value }))} />
                          </VfField>
                          <VfField label={isAr ? 'الدولة *' : lang === 'zh' ? '国家 *' : 'Country *'} delay={0.1} invalid={isSettingsFieldMissing('country')}>
                            <input className="vf-input" value={settings.country || ''} onChange={e => setSettings(prev => ({ ...prev, country: e.target.value }))} />
                          </VfField>
                        </VfG2>
                        <div style={{ height: 20 }} />
                        <VfG2>
                          <VfField label={isAr ? 'التخصص *' : lang === 'zh' ? '专业 *' : 'Specialty *'} delay={0.15}>
                            <select className="vf-select" value={settings.speciality || ''} onChange={e => setSettings(prev => ({ ...prev, speciality: e.target.value }))}>
                              <option value="">—</option>
                              {(CATEGORIES[lang] || CATEGORIES.en).filter(c => c.val !== 'all').map(c => (
                                <option key={c.val} value={c.val}>{c.label}</option>
                              ))}
                            </select>
                          </VfField>
                          <VfField label={isAr ? 'نوع النشاط' : lang === 'zh' ? '企业类型' : 'Business Type'} delay={0.2}>
                            <input className="vf-input" value={settings.business_type || ''} onChange={e => setSettings(prev => ({ ...prev, business_type: e.target.value }))} placeholder={isAr ? 'مصنّع / تاجر جملة' : lang === 'zh' ? '制造商 / 批发商' : 'Manufacturer / Wholesaler'} />
                          </VfField>
                        </VfG2>
                        <VfSep label={isAr ? 'التواصل' : lang === 'zh' ? '联系方式' : 'Contact'} />
                        <VfG2>
                          <VfField label="WeChat *" delay={0.25}>
                            <input className="vf-input" value={settings.wechat || ''} onChange={e => setSettings(prev => ({ ...prev, wechat: e.target.value }))} dir="ltr" />
                          </VfField>
                          <VfField label={isAr ? 'واتساب' : 'WhatsApp'} delay={0.3}>
                            <input className="vf-input" value={settings.whatsapp || ''} onChange={e => setSettings(prev => ({ ...prev, whatsapp: e.target.value }))} dir="ltr" placeholder="+86..." />
                          </VfField>
                        </VfG2>
                        <div style={{ height: 20 }} />
                        <VfField label={isAr ? 'رابط المتجر / الملف التجاري *' : lang === 'zh' ? '店铺链接 *' : 'Trade Profile Link *'} delay={0.35} invalid={isSettingsFieldMissing('trade_link')}>
                          <input className="vf-input" value={settings.trade_link || ''} onChange={e => setSettings(prev => ({ ...prev, trade_link: e.target.value }))} dir="ltr" placeholder="https://alibaba.com/..." />
                        </VfField>
                        <div style={{ height: 20 }} />
                        <VfField label={isAr ? 'موقع الشركة' : lang === 'zh' ? '公司官网' : 'Company Website'} delay={0.4}>
                          <input className="vf-input" value={settings.company_website || ''} onChange={e => setSettings(prev => ({ ...prev, company_website: e.target.value }))} dir="ltr" placeholder="https://..." />
                        </VfField>
                        <div style={{ height: 20 }} />
                        <VfField label={isAr ? 'وصف الشركة' : lang === 'zh' ? '公司介绍' : 'Company Description'} delay={0.45}>
                          <textarea className="vf-input" rows={3} style={{ resize: 'none', lineHeight: 1.7 }}
                            value={settings.company_description || ''}
                            onChange={e => setSettings(prev => ({ ...prev, company_description: e.target.value }))}
                            placeholder={isAr ? 'نبذة — ما تنتجه، خبرتك، ميزتك التنافسية' : lang === 'zh' ? '简介 — 产品、经验、竞争优势' : 'Brief — products, experience, competitive advantage'}
                          />
                        </VfField>
                        <div className="vf-fu" style={{ animationDelay: '0.5s', marginTop: 36, display: 'grid', gap: 10 }}>
                          {settingsError && <VfSaveError message={settingsError} />}
                          <button className="vf-btn-ink" disabled={savingSettings}
                            onClick={() => saveSettings({ nextVerificationStep: 2, navigateToVerification: true })}>
                            {savingSettings
                              ? (isAr ? 'جاري الحفظ...' : lang === 'zh' ? '保存中...' : 'Saving...')
                              : (isAr ? 'حفظ والانتقال للخطوة 2 ←' : lang === 'zh' ? '保存并进入第 2 步 →' : 'Save and continue to step 2 →')}
                          </button>
                          <button className="vf-btn-ghost" disabled={savingSettings} onClick={() => saveSettings()}>
                            {isAr ? 'حفظ فقط' : lang === 'zh' ? '仅保存' : 'Save only'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* ── STEP 2 ── */}
                    {verificationStep === 2 && (
                      <div>
                        <VfG2>
                          <VfField label={isAr ? 'رقم تسجيل الشركة *' : lang === 'zh' ? '公司注册号 *' : 'Company Reg. Number *'} delay={0}>
                            <input className="vf-input" value={verification.reg_number || ''} onChange={e => setVerification(prev => ({ ...prev, reg_number: e.target.value }))} dir="ltr" />
                          </VfField>
                          <VfField label={isAr ? 'سنوات الخبرة *' : lang === 'zh' ? '从业年限 *' : 'Years of Experience *'} delay={0.05}>
                            <input className="vf-input" type="number" min="0" value={verification.years_experience || ''} onChange={e => setVerification(prev => ({ ...prev, years_experience: e.target.value }))} dir="ltr" />
                          </VfField>
                        </VfG2>
                        <div style={{ height: 20 }} />
                        <VfField label={isAr ? 'عدد الموظفين' : lang === 'zh' ? '员工人数' : 'Number of Employees'} delay={0.1}>
                          <input className="vf-input" type="number" min="0" value={verification.num_employees || ''} onChange={e => setVerification(prev => ({ ...prev, num_employees: e.target.value }))} dir="ltr" placeholder={isAr ? 'اختياري' : lang === 'zh' ? '可选' : 'Optional'} />
                        </VfField>

                        <VfSep label={isAr ? 'المستندات' : lang === 'zh' ? '文件' : 'Documents'} />

                        {/* License */}
                        <div className="vf-fu" style={{ animationDelay: '0.2s', marginBottom: 12 }}>
                          <p style={{ fontSize: 12, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 400, marginBottom: 8 }}>
                            {isAr ? 'رخصة الأعمال أو هوية المنشأة *' : lang === 'zh' ? '营业执照或企业证明 *' : 'Business License or Company ID *'}
                          </p>
                          {verification.license_photo ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderRadius: 'var(--radius-panel)', background: VF_C.sageBg, border: `1px solid ${VF_C.sageBr}` }}>
                              <VfChk size={13} />
                              <p style={{ fontSize: 13, color: VF_C.sage, fontFamily: 'var(--font-ar)', flex: 1, fontWeight: 400 }}>
                                {isAr ? 'رخصة الأعمال — مرفوعة' : lang === 'zh' ? '营业执照 — 已上传' : 'License — uploaded'}
                              </p>
                              <button onClick={() => openVerificationDoc(verification.license_photo)} style={{ background: 'none', border: 'none', color: VF_C.ink30, fontSize: 12, fontFamily: 'var(--font-ar)', cursor: 'pointer' }}>
                                {isAr ? 'عرض' : lang === 'zh' ? '查看' : 'View'}
                              </button>
                              <label style={{ color: VF_C.ink30, fontSize: 12, fontFamily: 'var(--font-ar)', cursor: 'pointer' }}>
                                {isAr ? 'استبدال' : lang === 'zh' ? '更换' : 'Replace'}
                                <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={async e => { await uploadVerificationDoc(e.target.files?.[0], 'license'); e.target.value = ''; }} />
                              </label>
                            </div>
                          ) : (
                            <label style={{ display: 'block', border: `1px solid ${VF_C.ink10}`, borderRadius: 'var(--radius-panel)', padding: '26px 20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.25s', background: VF_C.paper }}>
                              {uploadingVerificationDoc.license
                                ? <p style={{ fontSize: 13, color: VF_C.ink30, fontFamily: 'var(--font-ar)' }}>...</p>
                                : <>
                                    <p style={{ fontSize: 14, color: VF_C.ink60, fontFamily: 'var(--font-ar)', fontWeight: 400, marginBottom: 4 }}>
                                      {isAr ? 'رفع الملف' : lang === 'zh' ? '上传文件' : 'Upload file'}
                                    </p>
                                    <p style={{ fontSize: 12, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 300 }}>
                                      PDF {isAr ? 'أو صورة' : lang === 'zh' ? '或图片' : 'or image'} · 10MB
                                    </p>
                                  </>
                              }
                              <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={async e => { await uploadVerificationDoc(e.target.files?.[0], 'license'); e.target.value = ''; }} />
                            </label>
                          )}
                        </div>

                        {/* Legal Representative ID — required */}
                        <div className="vf-fu" style={{ animationDelay: '0.22s', marginBottom: 12 }}>
                          <p style={{ fontSize: 12, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 400, marginBottom: 8 }}>
                            {t.legalRepIdLabel}<span style={{ color: 'var(--error, var(--red))', marginInlineStart: 4 }}>*</span>
                          </p>
                          {verification.legal_rep_id_photo ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderRadius: 'var(--radius-panel)', background: VF_C.sageBg, border: `1px solid ${VF_C.sageBr}` }}>
                              <VfChk size={13} />
                              <p style={{ fontSize: 13, color: VF_C.sage, fontFamily: 'var(--font-ar)', flex: 1, fontWeight: 400 }}>
                                {isAr ? 'هوية الممثل القانوني — مرفوعة' : lang === 'zh' ? '法定代表人身份证 — 已上传' : 'Legal Rep. ID — uploaded'}
                              </p>
                              <button onClick={() => openVerificationDoc(verification.legal_rep_id_photo)} style={{ background: 'none', border: 'none', color: VF_C.ink30, fontSize: 12, fontFamily: 'var(--font-ar)', cursor: 'pointer' }}>
                                {isAr ? 'عرض' : lang === 'zh' ? '查看' : 'View'}
                              </button>
                              <label style={{ color: VF_C.ink30, fontSize: 12, fontFamily: 'var(--font-ar)', cursor: 'pointer' }}>
                                {isAr ? 'استبدال' : lang === 'zh' ? '更换' : 'Replace'}
                                <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={async e => { await uploadVerificationDoc(e.target.files?.[0], 'legal_rep_id'); e.target.value = ''; }} />
                              </label>
                            </div>
                          ) : (
                            <label style={{ display: 'block', border: `1px solid ${VF_C.ink10}`, borderRadius: 'var(--radius-panel)', padding: '26px 20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.25s', background: VF_C.paper }}>
                              {uploadingVerificationDoc.legal_rep_id
                                ? <p style={{ fontSize: 13, color: VF_C.ink30, fontFamily: 'var(--font-ar)' }}>...</p>
                                : <>
                                    <p style={{ fontSize: 14, color: VF_C.ink60, fontFamily: 'var(--font-ar)', fontWeight: 400, marginBottom: 4 }}>
                                      {isAr ? 'رفع الملف' : lang === 'zh' ? '上传文件' : 'Upload file'}
                                    </p>
                                    <p style={{ fontSize: 12, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 300 }}>
                                      PDF {isAr ? 'أو صورة' : lang === 'zh' ? '或图片' : 'or image'} · 10MB
                                    </p>
                                  </>
                              }
                              <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={async e => { await uploadVerificationDoc(e.target.files?.[0], 'legal_rep_id'); e.target.value = ''; }} />
                            </label>
                          )}
                        </div>

                        {/* Factory/Office Address Proof — required */}
                        <div className="vf-fu" style={{ animationDelay: '0.24s', marginBottom: 12 }}>
                          <p style={{ fontSize: 12, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 400, marginBottom: 8 }}>
                            {t.addressProofLabel}<span style={{ color: 'var(--error, var(--red))', marginInlineStart: 4 }}>*</span>
                          </p>
                          {verification.address_proof_photo ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderRadius: 'var(--radius-panel)', background: VF_C.sageBg, border: `1px solid ${VF_C.sageBr}` }}>
                              <VfChk size={13} />
                              <p style={{ fontSize: 13, color: VF_C.sage, fontFamily: 'var(--font-ar)', flex: 1, fontWeight: 400 }}>
                                {isAr ? 'إثبات عنوان المصنع — مرفوع' : lang === 'zh' ? '工厂/办公室地址证明 — 已上传' : 'Address proof — uploaded'}
                              </p>
                              <button onClick={() => openVerificationDoc(verification.address_proof_photo)} style={{ background: 'none', border: 'none', color: VF_C.ink30, fontSize: 12, fontFamily: 'var(--font-ar)', cursor: 'pointer' }}>
                                {isAr ? 'عرض' : lang === 'zh' ? '查看' : 'View'}
                              </button>
                              <label style={{ color: VF_C.ink30, fontSize: 12, fontFamily: 'var(--font-ar)', cursor: 'pointer' }}>
                                {isAr ? 'استبدال' : lang === 'zh' ? '更换' : 'Replace'}
                                <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={async e => { await uploadVerificationDoc(e.target.files?.[0], 'address_proof'); e.target.value = ''; }} />
                              </label>
                            </div>
                          ) : (
                            <label style={{ display: 'block', border: `1px solid ${VF_C.ink10}`, borderRadius: 'var(--radius-panel)', padding: '26px 20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.25s', background: VF_C.paper }}>
                              {uploadingVerificationDoc.address_proof
                                ? <p style={{ fontSize: 13, color: VF_C.ink30, fontFamily: 'var(--font-ar)' }}>...</p>
                                : <>
                                    <p style={{ fontSize: 14, color: VF_C.ink60, fontFamily: 'var(--font-ar)', fontWeight: 400, marginBottom: 4 }}>
                                      {isAr ? 'رفع الملف' : lang === 'zh' ? '上传文件' : 'Upload file'}
                                    </p>
                                    <p style={{ fontSize: 12, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 300 }}>
                                      PDF {isAr ? 'أو صورة' : lang === 'zh' ? '或图片' : 'or image'} · 10MB
                                    </p>
                                  </>
                              }
                              <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={async e => { await uploadVerificationDoc(e.target.files?.[0], 'address_proof'); e.target.value = ''; }} />
                            </label>
                          )}
                        </div>

                        {/* Factory images */}
                        <div className="vf-fu" style={{ animationDelay: '0.25s', marginBottom: 12 }}>
                          <p style={{ fontSize: 12, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 400, marginBottom: 8 }}>
                            {isAr ? 'صور المصنع أو المستودع *' : lang === 'zh' ? '工厂或仓库照片 *' : 'Factory or Warehouse Photos *'}
                            <span style={{ fontSize: 11, color: VF_C.ink30, fontWeight: 300, marginRight: 6 }}>{verificationImages.length}/{VERIFICATION_IMAGE_LIMIT}</span>
                          </p>
                          {verificationImages.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {verificationImages.map((item, i) => (
                                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 'var(--radius-panel)', background: VF_C.sageBg, border: `1px solid ${VF_C.sageBr}` }}>
                                  <VfChk size={13} />
                                  <p style={{ fontSize: 13, color: VF_C.sage, fontFamily: 'var(--font-ar)', flex: 1, fontWeight: 400 }}>
                                    {isAr ? `صورة ${i + 1}` : lang === 'zh' ? `图片 ${i + 1}` : `Image ${i + 1}`}
                                  </p>
                                  <button onClick={() => openVerificationDoc(item)} style={{ background: 'none', border: 'none', color: VF_C.ink30, fontSize: 12, fontFamily: 'var(--font-ar)', cursor: 'pointer' }}>
                                    {isAr ? 'عرض' : lang === 'zh' ? '查看' : 'View'}
                                  </button>
                                  <button onClick={() => removeVerificationMedia('image', item)} style={{ background: 'none', border: 'none', color: VF_C.ink30, fontSize: 12, fontFamily: 'var(--font-ar)', cursor: 'pointer' }}>
                                    {isAr ? 'حذف' : lang === 'zh' ? '删除' : 'Remove'}
                                  </button>
                                </div>
                              ))}
                              {verificationImages.length < VERIFICATION_IMAGE_LIMIT && (
                                <label style={{ display: 'block', border: `1px solid ${VF_C.ink10}`, borderRadius: 'var(--radius-panel)', padding: '14px 20px', textAlign: 'center', cursor: 'pointer', background: VF_C.paper }}>
                                  <p style={{ fontSize: 13, color: VF_C.ink60, fontFamily: 'var(--font-ar)', fontWeight: 400 }}>
                                    + {isAr ? 'إضافة صورة' : lang === 'zh' ? '添加图片' : 'Add image'}
                                  </p>
                                  <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={async e => { await uploadVerificationMedia(e.target.files, 'image'); e.target.value = ''; }} />
                                </label>
                              )}
                            </div>
                          ) : (
                            <label style={{ display: 'block', border: `1px solid ${VF_C.ink10}`, borderRadius: 'var(--radius-panel)', padding: '26px 20px', textAlign: 'center', cursor: 'pointer', background: VF_C.paper }}>
                              {uploadingVerificationDoc.images
                                ? <>
                                    <p style={{ fontSize: 13, color: VF_C.ink60, fontFamily: 'var(--font-ar)', fontWeight: 400, marginBottom: 8 }}>
                                      {isAr ? `جاري رفع الصور… ${verificationUploadPct.images ?? 0}%` : lang === 'zh' ? `正在上传图片… ${verificationUploadPct.images ?? 0}%` : `Uploading photos… ${verificationUploadPct.images ?? 0}%`}
                                    </p>
                                    <div style={{ height: 6, borderRadius: 999, background: VF_C.ink10, overflow: 'hidden', maxWidth: 240, margin: '0 auto' }}>
                                      <div style={{ width: `${verificationUploadPct.images ?? 0}%`, height: '100%', background: VF_C.sage, borderRadius: 999, transition: 'width 0.2s ease' }} />
                                    </div>
                                  </>
                                : <>
                                    <p style={{ fontSize: 14, color: VF_C.ink60, fontFamily: 'var(--font-ar)', fontWeight: 400, marginBottom: 4 }}>
                                      {isAr ? 'صور المصنع أو المستودع' : lang === 'zh' ? '工厂或仓库照片' : 'Factory or warehouse photos'}
                                    </p>
                                    <p style={{ fontSize: 12, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 300 }}>
                                      {isAr ? 'حتى 5 صور · JPG أو PNG' : lang === 'zh' ? '最多 5 张 · JPG 或 PNG' : 'Up to 5 photos · JPG or PNG'}
                                    </p>
                                  </>
                              }
                              <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={async e => { await uploadVerificationMedia(e.target.files, 'image'); e.target.value = ''; }} />
                            </label>
                          )}
                        </div>

                        {/* Factory video — required */}
                        <div className="vf-fu" style={{ animationDelay: '0.3s', marginBottom: 12 }}>
                          <p style={{ fontSize: 12, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 400, marginBottom: 8 }}>
                            {isAr ? 'فيديو المصنع' : lang === 'zh' ? '工厂视频' : 'Factory Video'}
                            <span style={{ color: 'var(--error, var(--red))', marginRight: 4 }}>*</span>
                          </p>
                          {verificationVideos.length > 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderRadius: 'var(--radius-panel)', background: VF_C.sageBg, border: `1px solid ${VF_C.sageBr}` }}>
                              <svg width={14} height={14} viewBox="0 0 24 24" fill="none"><polygon points="5 3 19 12 5 21 5 3" fill={VF_C.sage} /></svg>
                              <p style={{ fontSize: 13, color: VF_C.sage, fontFamily: 'var(--font-ar)', flex: 1, fontWeight: 400 }}>
                                {isAr ? 'فيديو المصنع — مرفوع' : lang === 'zh' ? '工厂视频 — 已上传' : 'Factory video — uploaded'}
                              </p>
                              <button onClick={() => removeVerificationMedia('video', verificationVideos[0])} style={{ background: 'none', border: 'none', color: VF_C.ink30, fontSize: 12, fontFamily: 'var(--font-ar)', cursor: 'pointer' }}>
                                {isAr ? 'حذف' : lang === 'zh' ? '删除' : 'Remove'}
                              </button>
                            </div>
                          ) : (
                            <label style={{ display: 'block', padding: '14px 18px', borderRadius: 'var(--radius-panel)', border: `1px dashed ${VF_C.ink10}`, background: VF_C.paper, cursor: uploadingVerificationDoc.videos ? 'default' : 'pointer' }}>
                              {uploadingVerificationDoc.videos ? (
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <p style={{ fontSize: 13, color: VF_C.ink60, fontFamily: 'var(--font-ar)', fontWeight: 400, margin: 0 }}>
                                      {isAr ? 'جاري رفع الفيديو…' : lang === 'zh' ? '正在上传视频…' : 'Uploading video…'}
                                    </p>
                                    <p style={{ fontSize: 13, color: VF_C.ink60, fontFamily: 'var(--font-ar)', fontWeight: 600, margin: 0 }} dir="ltr">
                                      {verificationUploadPct.videos ?? 0}%
                                    </p>
                                  </div>
                                  <div style={{ height: 6, borderRadius: 999, background: VF_C.ink10, overflow: 'hidden' }}>
                                    <div style={{ width: `${verificationUploadPct.videos ?? 0}%`, height: '100%', background: VF_C.sage, borderRadius: 999, transition: 'width 0.2s ease' }} />
                                  </div>
                                  <p style={{ fontSize: 11, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 300, marginTop: 8, marginBottom: 0 }}>
                                    {isAr ? 'لا تغلق الصفحة حتى يكتمل الرفع.' : lang === 'zh' ? '上传完成前请勿关闭页面。' : 'Keep this page open until the upload completes.'}
                                  </p>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                  <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-control)', background: VF_C.ink05, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <svg width={14} height={14} viewBox="0 0 24 24" fill="none"><polygon points="5 3 19 12 5 21 5 3" fill={VF_C.ink30} /></svg>
                                  </div>
                                  <div>
                                    <p style={{ fontSize: 13, color: VF_C.ink60, fontFamily: 'var(--font-ar)', fontWeight: 400 }}>
                                      {isAr ? 'رفع فيديو' : lang === 'zh' ? '上传视频' : 'Upload video'}
                                    </p>
                                    <p style={{ fontSize: 11, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 300, marginTop: 2 }}>
                                      {isAr ? 'جولة في المصنع · MP4 · حتى 200MB' : lang === 'zh' ? '工厂参观视频 · MP4 · 最大 200MB' : 'Factory tour · MP4 · Max 200MB'}
                                    </p>
                                  </div>
                                </div>
                              )}
                              <input type="file" accept="video/*" disabled={uploadingVerificationDoc.videos} style={{ display: 'none' }} onChange={async e => { await uploadVerificationMedia(e.target.files, 'video'); e.target.value = ''; }} />
                            </label>
                          )}
                        </div>

                        {/* Privacy note — visible below all upload fields */}
                        <div className="vf-fu" style={{ animationDelay: '0.34s', marginTop: 18, marginBottom: 6, padding: '12px 16px', borderRadius: 'var(--radius-panel)', background: VF_C.paper, border: `1px solid ${VF_C.ink10}` }}>
                          <p style={{ fontSize: 12, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 300, lineHeight: 1.7, margin: 0 }}>
                            {t.verificationPrivacyNote}
                          </p>
                        </div>

                        <div className="vf-fu" style={{ animationDelay: '0.36s', marginTop: 36, display: 'grid', gap: 10 }}>
                          <button className="vf-btn-ink" onClick={() => {
                            const isStepReady = Boolean(String(verification.reg_number || '').trim())
                              && Boolean(String(verification.years_experience || '').trim())
                              && Boolean(String(verification.license_photo || '').trim())
                              && Boolean(String(verification.legal_rep_id_photo || '').trim())
                              && Boolean(String(verification.address_proof_photo || '').trim())
                              && verificationImages.length > 0;
                            if (!isStepReady) { setVerificationSaved(false); setVerificationMsg(t.verificationMissing); return; }
                            setVerificationMsg('');
                            setVerificationStep(3);
                          }}>
                            {isAr ? 'التالي: المراجعة النهائية ←' : lang === 'zh' ? '下一步：最终确认 →' : 'Next: Final review →'}
                          </button>
                          <button className="vf-btn-ghost" onClick={() => setVerificationStep(1)}>
                            {isAr ? 'رجوع لبيانات الشركة' : lang === 'zh' ? '返回公司资料' : 'Back to company profile'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* ── STEP 3 ── */}
                    {verificationStep === 3 && (
                      <div>
                        <div className="vf-fu" style={{ animationDelay: '0s', background: VF_C.white, border: `1px solid ${VF_C.ink10}`, borderRadius: 'var(--radius-panel)', overflow: 'hidden', marginBottom: 24 }}>
                          {[
                            [isAr ? 'اسم الشركة'       : lang === 'zh' ? '公司名称'   : 'Company',       settings.company_name || '—'],
                            [isAr ? 'المدينة / الدولة'  : lang === 'zh' ? '城市 / 国家' : 'City / Country', `${settings.city || '—'} · ${settings.country || '—'}`],
                            [isAr ? 'التخصص'            : lang === 'zh' ? '专业'       : 'Specialty',      getSpecialtyLabel(settings.speciality, lang) || '—'],
                            ['WeChat',                                                                         settings.wechat || '—'],
                            [isAr ? 'رابط المتجر'       : lang === 'zh' ? '店铺链接'   : 'Trade link',     settings.trade_link || '—'],
                            [isAr ? 'رقم التسجيل'       : lang === 'zh' ? '注册号'     : 'Reg. number',    verification.reg_number || '—'],
                            [isAr ? 'سنوات الخبرة'      : lang === 'zh' ? '从业年限'   : 'Experience',     verification.years_experience || '—'],
                            [isAr ? 'رخصة الأعمال'      : lang === 'zh' ? '营业执照'   : 'License',        verification.license_photo ? (isAr ? 'مرفوعة ✓' : lang === 'zh' ? '已上传 ✓' : 'Uploaded ✓') : '—'],
                            [t.legalRepIdLabel,                                                              verification.legal_rep_id_photo ? (isAr ? 'مرفوعة ✓' : lang === 'zh' ? '已上传 ✓' : 'Uploaded ✓') : '—'],
                            [t.addressProofLabel,                                                            verification.address_proof_photo ? (isAr ? 'مرفوع ✓' : lang === 'zh' ? '已上传 ✓' : 'Uploaded ✓') : '—'],
                            [isAr ? 'صور المصنع'         : lang === 'zh' ? '工厂图片'   : 'Factory photos', `${verificationImages.length} ${isAr ? 'صور' : lang === 'zh' ? '张' : 'photos'}`],
                            ...(verificationVideos.length > 0 ? [[isAr ? 'فيديو المصنع' : lang === 'zh' ? '工厂视频' : 'Factory video', isAr ? 'مرفوع ✓' : lang === 'zh' ? '已上传 ✓' : 'Uploaded ✓']] : []),
                          ].map(([k, v], i, arr) => (
                            <div key={k} className="vf-review-row" style={{ borderBottom: i < arr.length - 1 ? `1px solid ${VF_C.ink05}` : 'none' }}>
                              <span style={{ fontSize: 13, color: VF_C.ink30, fontFamily: 'var(--font-ar)', fontWeight: 300, flexShrink: 0 }}>{k}</span>
                              <span style={{ fontSize: 13, color: VF_C.ink, fontFamily: 'var(--font-ar)', fontWeight: 400 }}>{v}</span>
                            </div>
                          ))}
                        </div>
                        <div className="vf-fu" style={{ animationDelay: '0.1s', display: 'grid', gap: 10 }}>
                          <button className="vf-btn-ink" disabled={savingVerification} onClick={async () => { await saveVerification(); if (verificationSaved) setShowVfSuccess(true); }}>
                            {savingVerification
                              ? (isAr ? 'جاري الإرسال...' : lang === 'zh' ? '提交中...' : 'Submitting...')
                              : (isAr ? 'إرسال طلب التحقق ←' : lang === 'zh' ? '提交认证申请 →' : 'Submit verification request →')}
                          </button>
                          <button className="vf-btn-ghost" onClick={() => setVerificationStep(2)}>
                            {isAr ? 'رجوع للتعديل' : lang === 'zh' ? '返回修改' : 'Back to edit'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          )}
          {/* ── PAYOUT ── */}
          {!isRestrictedSupplierTab && activeTab === 'payout' && (
            <div style={section}>
              <BackBtn onClick={() => setActiveTab('overview')} label={t.back} />
              <h2 style={{ fontSize: isAr ? 28 : 34, fontWeight: 300, marginBottom: 14, color: 'var(--text-primary)', ...arFont, letterSpacing: isAr ? 0 : -0.5 }}>{t.payoutTitle}</h2>
              <p style={{ maxWidth: 720, fontSize: 13, color: 'var(--text-disabled)', lineHeight: 1.9, marginBottom: 24, ...arFont }}>{t.payoutIntro}</p>

              {!supplierState.isApprovedStage ? (
                <div style={{ maxWidth: 720, padding: '18px 20px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-muted)', background: 'var(--bg-subtle)' }}>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8, ...arFont }}>{t.payoutLocked}</p>
                </div>
              ) : (
                <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border-muted)', padding: '24px 28px', borderRadius: 'var(--radius-xl)', maxWidth: 720 }}>
                  <p style={{ fontSize: 12, color: 'var(--text-disabled)', marginBottom: 18, ...arFont }}>{t.payoutNote}</p>
                  <div style={{ marginBottom: 18 }}>
                    <SaveFeedbackCard feedback={payoutFeedback} isAr={isAr} />
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label className={`form-label${isAr ? ' ar' : ''}`}>{t.beneficiaryName}</label>
                      <input className="form-input" value={payout.payout_beneficiary_name} onChange={e => setPayout(prev => ({ ...prev, payout_beneficiary_name: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className={`form-label${isAr ? ' ar' : ''}`}>{t.bankName}</label>
                      <input className="form-input" value={payout.bank_name} onChange={e => setPayout(prev => ({ ...prev, bank_name: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className={`form-label${isAr ? ' ar' : ''}`}>{t.bankAccountNumber}</label>
                      <input className="form-input" value={payout.payout_account_number} onChange={e => setPayout(prev => ({ ...prev, payout_account_number: e.target.value }))} dir="ltr" />
                    </div>
                    <div className="form-group">
                      <label className={`form-label${isAr ? ' ar' : ''}`}>{t.swiftCode}</label>
                      <input className="form-input" value={payout.swift_code} onChange={e => setPayout(prev => ({ ...prev, swift_code: e.target.value }))} dir="ltr" />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label className={`form-label${isAr ? ' ar' : ''}`}>{t.bankAddress}</label>
                      <input className="form-input" value={payout.payout_bank_address} onChange={e => setPayout(prev => ({ ...prev, payout_bank_address: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className={`form-label${isAr ? ' ar' : ''}`}>{t.preferredCurrency}</label>
                      <select className="form-input" value={payout.preferred_display_currency || 'USD'} onChange={e => setPayout(prev => ({ ...prev, preferred_display_currency: e.target.value }))}>
                        {DISPLAY_CURRENCIES.map(code => <option key={code} value={code}>{code}</option>)}
                      </select>
                    </div>
                  </div>

                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 14, marginBottom: 4, lineHeight: 1.7, ...arFont }}>
                    {t.payoutReassurance}
                  </p>

                  <button onClick={savePayout} disabled={savingPayout} className="btn-primary" style={{ padding: '12px 32px', fontSize: 13, alignSelf: 'flex-start', minHeight: 46, marginTop: 10 }}>
                    {payoutButtonLabel}
                  </button>
                  <div style={{ marginTop: 12, minHeight: 22 }}>
                    {savingPayout ? (
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', ...arFont }}>
                        {isAr ? 'جاري حفظ بيانات الدفعات...' : lang === 'zh' ? '正在保存收款信息…' : 'Saving payout details...'}
                      </p>
                    ) : payoutError ? (
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--red)', ...arFont }}>
                        {payoutError}
                      </p>
                    ) : hasSavedPayoutRecord && !isPayoutDirty ? (
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--green)', ...arFont }}>
                        {isAr
                          ? `تم حفظ بيانات الدفعات بنجاح${resolvedPayoutSavedAt ? ` · آخر حفظ ${formatDraftSavedAt(resolvedPayoutSavedAt, lang)}` : ''}`
                          : lang === 'zh'
                            ? `收款资料已成功保存${resolvedPayoutSavedAt ? ` · 最后保存时间：${formatDraftSavedAt(resolvedPayoutSavedAt, lang)}` : ''}`
                            : `Payout details saved successfully${resolvedPayoutSavedAt ? ` · Last saved ${formatDraftSavedAt(resolvedPayoutSavedAt, lang)}` : ''}`}
                      </p>
                    ) : isPayoutDirty ? (
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', ...arFont }}>
                        {isAr ? 'لديك تعديلات غير محفوظة على بيانات الدفعات.' : lang === 'zh' ? '您有尚未保存的收款修改。' : 'You have unsaved payout changes.'}
                      </p>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── WALLET & REFERRALS ── */}
          {SHOW_SUPPLIER_REFERRALS && !isRestrictedSupplierTab && activeTab === 'wallet' && (() => {
            const code = profile.maabar_supplier_id || '';
            const shareLink = code ? `https://maabar.io/login/supplier?ref=${code}&mode=signup` : '';
            const pendingSum = referralRewards.filter(r => r.state === 'pending').reduce((a, r) => a + Number(r.amount || 0), 0);
            const withdrawableSum = referralRewards.filter(r => r.state === 'withdrawable').reduce((a, r) => a + Number(r.amount || 0), 0);
            const verifiedCount = referrals.filter(r => r.referred_verified_at).length;
            const rewardLabel = (rt) => rt === 'referred_verified'
              ? (isAr ? 'إحالة أكملت التوثيق' : lang === 'zh' ? '推荐已通过认证' : 'Referral verified')
              : (isAr ? 'أول منتج + إحالة موثّقة' : lang === 'zh' ? '首个产品 + 已认证推荐' : 'First product + verified referral');
            const cardBox = { background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 18 };
            const dir = isAr ? 'rtl' : 'ltr';
            // navigator.clipboard is undefined outside a secure context (http on a
            // LAN IP), so fall back to a hidden textarea + execCommand.
            const copyText = async (text, key) => {
              if (!text) return;
              try {
                if (navigator.clipboard?.writeText) {
                  await navigator.clipboard.writeText(text);
                } else {
                  const ta = document.createElement('textarea');
                  ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
                  document.body.appendChild(ta); ta.focus(); ta.select();
                  document.execCommand('copy'); document.body.removeChild(ta);
                }
                setCopiedKey(key); setTimeout(() => setCopiedKey(''), 1500);
              } catch (e) { /* noop */ }
            };
            const copyLabel = (key, base) => copiedKey === key
              ? (isAr ? 'تم ✓' : lang === 'zh' ? '已复制 ✓' : 'Copied ✓') : base;
            return (
              <div style={{ ...section, maxWidth: 900, direction: dir }}>
                <h2 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                  {isAr ? 'المحفظة والإحالات' : lang === 'zh' ? '钱包与推荐' : 'Wallet & Referrals'}
                </h2>
                <div style={{ background: 'linear-gradient(135deg, #FCF8F0, #F7EDD6)', border: '1px solid rgba(176,141,46,.45)', borderRadius: 14, padding: '16px 18px', margin: '0 0 18px' }}>
                  <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#8A6D1E', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {isAr ? '🎁 اربح حتى $60 عن كل مورد تدعوه' : lang === 'zh' ? '🎁 每邀请一位供应商，最多赚取 $60' : '🎁 Earn up to $60 for every supplier you invite'}
                  </p>
                  <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {isAr ? '‏$30 عند إتمام توثيق المورد المُحال · ‏$30 إضافية عند نشرك منتجاً' : lang === 'zh' ? '推荐供应商通过认证得 $30 · 您发布产品再得 $30' : '$30 when your referral gets verified · $30 more once you publish a product'}
                  </p>
                </div>

                {/* Referral journey → celebration (replaces the old pending/withdrawable cards) */}
                {(() => {
                  const inviteDone = verifiedCount >= 1;
                  const productDone = (stats.products || 0) >= 1;
                  const earned = pendingSum + withdrawableSum;
                  const step = (done, label, reward) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0' }}>
                      <span style={{ width: 22, height: 22, borderRadius: '50%', display: 'grid', placeItems: 'center', flex: '0 0 auto', background: done ? '#8A6D1E' : 'rgba(0,0,0,0.06)', color: done ? '#fff' : 'var(--text-disabled)', fontSize: 12, fontWeight: 700 }}>{done ? '✓' : ''}</span>
                      <span style={{ flex: 1, fontSize: 14, color: done ? 'var(--text-primary)' : 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{label}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#8A6D1E' }}>{reward}</span>
                    </div>
                  );
                  if (inviteDone && productDone && earned > 0) {
                    return (
                      <div style={{ background: 'linear-gradient(135deg, #FCF8F0, #F7EDD6)', border: '1px solid rgba(176,141,46,.5)', borderRadius: 16, padding: '22px 20px', marginBottom: 16, textAlign: 'center' }}>
                        <p style={{ margin: 0, fontSize: 28 }}>🎉</p>
                        <p style={{ margin: '6px 0 0', fontSize: 19, fontWeight: 700, color: '#8A6D1E', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? `تهانينا! كسبت $${earned}` : lang === 'zh' ? `恭喜！您赚取了 $${earned}` : `Congrats! You've earned $${earned}`}
                        </p>
                        <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                          {isAr ? 'يتحوّل لك مع أول عملية بيع في معبر.' : lang === 'zh' ? '将在您于 Maabar 的首笔销售时转入您的账户。' : 'It transfers to you with your first sale on Maabar.'}
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div style={{ ...cardBox, marginBottom: 16 }}>
                      <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                        {isAr ? 'رحلتك نحو $60' : lang === 'zh' ? '赚取 $60 的旅程' : 'Your journey to $60'}
                      </p>
                      {step(inviteDone, isAr ? 'ادعُ مورداً يُكمل توثيقه' : lang === 'zh' ? '邀请一位供应商完成认证' : 'Invite a supplier who gets verified', '+$30')}
                      {step(productDone, isAr ? 'انشر أول منتج لك' : lang === 'zh' ? '发布您的第一个产品' : 'Publish your first product', '+$30')}
                    </div>
                  );
                })()}

                {/* Code + share */}
                {code ? (
                  <div style={{ ...cardBox, marginBottom: 16 }}>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 6px' }}>{isAr ? 'كودك للإحالة' : lang === 'zh' ? '您的推荐码' : 'Your referral code'}</p>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                      <code style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1, color: 'var(--text-primary)', direction: 'ltr' }}>{code}</code>
                      <button onClick={() => copyText(code, 'code')} className="btn-outline" style={{ padding: '6px 12px', fontSize: 12 }}>{copyLabel('code', isAr ? 'نسخ' : lang === 'zh' ? '复制' : 'Copy')}</button>
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 6px' }}>{isAr ? 'رابط الدعوة' : lang === 'zh' ? '邀请链接' : 'Invite link'}</p>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input readOnly value={shareLink} onFocus={(e) => e.target.select()} style={{ flex: 1, minWidth: 220, direction: 'ltr', fontSize: 12, padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 10, background: 'var(--bg-base)', color: 'var(--text-secondary)' }} />
                      <button onClick={() => copyText(shareLink, 'link')} className="btn-primary" style={{ padding: '8px 14px', fontSize: 12 }}>{copyLabel('link', isAr ? 'نسخ الرابط' : lang === 'zh' ? '复制链接' : 'Copy link')}</button>
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '12px 0 0' }}>
                      {isAr ? `إحالاتك: ${referrals.length} / 10 · موثّقة: ${verifiedCount}` : lang === 'zh' ? `推荐：${referrals.length} / 10 · 已认证：${verifiedCount}` : `Referrals: ${referrals.length} / 10 · Verified: ${verifiedCount}`}
                    </p>
                  </div>
                ) : (
                  <div style={{ ...cardBox, marginBottom: 16 }}>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                      {isAr ? 'كودك للإحالة يظهر بعد اعتماد توثيقك كمورد موثّق.' : lang === 'zh' ? '您的推荐码将在账户通过认证后显示。' : 'Your referral code appears once your account is verified.'}
                    </p>
                  </div>
                )}

                {/* Rewards ledger */}
                <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 10px' }}>{isAr ? 'سجل المكافآت' : lang === 'zh' ? '奖励记录' : 'Rewards ledger'}</h3>
                {referralRewards.length === 0 ? (
                  <p style={{ fontSize: 13, color: 'var(--text-disabled)' }}>{isAr ? 'لا مكافآت بعد — ابدأ بمشاركة كودك.' : lang === 'zh' ? '暂无奖励 — 分享您的推荐码开始吧。' : 'No rewards yet — share your code to start.'}</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {referralRewards.map((r) => (
                      <div key={r.id} style={{ ...cardBox, padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 2px' }}>{rewardLabel(r.reward_type)}</p>
                          <p style={{ fontSize: 11, color: 'var(--text-disabled)', margin: 0, direction: 'ltr', textAlign: isAr ? 'right' : 'left' }}>${Number(r.amount)} · {new Date(r.earned_at).toLocaleDateString(isAr ? 'ar-SA-u-nu-latn' : lang === 'zh' ? 'zh-CN' : 'en-US')}</p>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 999, background: r.state === 'withdrawable' ? 'rgba(45,122,79,0.10)' : 'rgba(176,141,46,0.12)', color: r.state === 'withdrawable' ? 'var(--green)' : '#8A6D1E' }}>
                          {r.state === 'withdrawable' ? (isAr ? 'قابل للسحب' : lang === 'zh' ? '可提现' : 'Withdrawable') : (isAr ? 'قيد التفعيل' : lang === 'zh' ? '待激活' : 'Pending')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── SETTINGS ── */}
          {activeTab === 'settings' && (
            <div style={{ ...section, background: 'var(--bg-page)', paddingBottom: 60 }}>
              <BackBtn onClick={() => setActiveTab('overview')} label={t.back} />
              <h2 style={{ fontSize: isAr ? 26 : 32, fontWeight: 300, marginBottom: 24, color: 'var(--text-primary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', letterSpacing: isAr ? 0 : -0.5 }}>
                {t.settingsTitle}
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 760 }}>

                {/* ── Card 1: Verification status ── */}
                <div style={{ background: 'var(--bg-hero)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-card)', padding: '22px 20px' }}>
                  <p style={{ fontSize: 10, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', letterSpacing: isAr ? 0 : '1.2px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 14 }}>
                    {isAr ? 'حالة التحقق' : lang === 'zh' ? '验证状态' : 'Verification Status'}
                  </p>
                  <h3 style={{ fontSize: isAr ? 18 : 20, fontWeight: 400, color: 'var(--text-primary)', marginBottom: 8, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', letterSpacing: isAr ? 0 : -0.2 }}>
                    {verificationStatusHeadline}
                  </h3>
                  <p style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--text-secondary)', marginBottom: 16, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {verificationStatusBody}
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ padding: '5px 12px', borderRadius: 'var(--radius-chip)', fontSize: 11, background: 'rgba(45,122,79,0.10)', border: '1px solid rgba(45,122,79,0.22)', color: 'var(--green)', fontFamily: 'var(--font-ar)' }}>
                      {isAr ? `اكتمال الملف: ${profileReadiness.completedRequiredCount}/${profileReadiness.totalRequiredCount}` : lang === 'zh' ? `资料完成度：${profileReadiness.completedRequiredCount}/${profileReadiness.totalRequiredCount}` : `Profile: ${profileReadiness.completedRequiredCount}/${profileReadiness.totalRequiredCount}`}
                    </span>
                    <span style={{ padding: '5px 12px', borderRadius: 'var(--radius-chip)', fontSize: 11, background: 'rgba(160,136,80,0.08)', border: '1px solid rgba(160,136,80,0.22)', color: 'var(--warn)', fontFamily: 'var(--font-ar)' }}>
                      {supplierState.isApprovedStage
                        ? (isAr ? 'موثّق' : lang === 'zh' ? '已认证' : 'Verified')
                        : supplierState.isUnderReviewStage
                          ? (isAr ? 'قيد المراجعة' : lang === 'zh' ? '审核中' : 'Under review')
                          : (isAr ? 'لم يُرسل بعد' : lang === 'zh' ? '尚未提交' : 'Not yet submitted')}
                    </span>
                  </div>
                </div>

                {/* ── Card 2: Visual identity ── */}
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '22px 20px' }}>
                  <p style={{ fontSize: 10, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', letterSpacing: isAr ? 0 : '1.2px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 18 }}>
                    {isAr ? 'الهوية البصرية' : lang === 'zh' ? '品牌素材' : 'Visual Identity'}
                  </p>

                  {/* Cover photo — full-width preview above the logo/factory grid */}
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', marginBottom: 12 }}>
                      {isAr ? 'صورة الغلاف' : lang === 'zh' ? '封面图片' : 'Cover Photo'}
                    </p>
                    {settings.cover_photo_url ? (
                      <div style={{ position: 'relative', width: '100%', height: 100, borderRadius: 'var(--radius-control)', overflow: 'hidden', border: '1px solid var(--border)' }}>
                        <img src={settings.cover_photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button
                          onClick={removeCoverPhoto}
                          title={isAr ? 'إزالة' : lang === 'zh' ? '移除' : 'Remove'}
                          style={{ position: 'absolute', top: 6, right: 6, background: 'var(--ink)', color: 'var(--on-dark)', border: 'none', width: 22, height: 22, borderRadius: 'var(--radius-full)', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                        <input ref={coverRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadCoverPhoto} />
                        <button
                          onClick={() => coverRef.current?.click()}
                          style={{ position: 'absolute', bottom: 6, right: 6, background: 'var(--ink)', color: 'var(--on-dark)', border: 'none', borderRadius: 'var(--radius-control)', padding: '5px 10px', fontSize: 11, fontFamily: 'var(--font-ar)', fontWeight: 500, cursor: 'pointer' }}>
                          {uploadingCover
                            ? (isAr ? 'جاري الرفع...' : lang === 'zh' ? '上传中...' : 'Uploading...')
                            : (isAr ? 'تغيير الغلاف' : lang === 'zh' ? '更换封面' : 'Change cover')}
                        </button>
                      </div>
                    ) : (
                      <>
                        <input ref={coverRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadCoverPhoto} />
                        <div
                          onClick={() => coverRef.current?.click()}
                          style={{ width: '100%', height: 100, borderRadius: 'var(--radius-control)', border: '1px dashed var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'border-color 0.2s', fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)' }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--ink)'}
                          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}>
                          {uploadingCover
                            ? (isAr ? 'جاري الرفع...' : lang === 'zh' ? '上传中...' : 'Uploading...')
                            : (isAr ? '+ رفع صورة الغلاف' : lang === 'zh' ? '+ 上传封面' : '+ Upload cover photo')}
                        </div>
                      </>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                    <div>
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', marginBottom: 12 }}>{t.logo}</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ width: 64, height: 64, borderRadius: 'var(--radius-full)', background: 'var(--border)', border: '1px solid var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                          {settings.avatar_url
                            ? <img src={settings.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <span style={{ fontSize: 20, opacity: 0.3 }}>◻</span>}
                        </div>
                        <div>
                          <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadLogo} />
                          <button
                            onClick={() => logoRef.current?.click()}
                            style={{ background: 'var(--ink)', color: 'var(--on-dark)', border: 'none', borderRadius: 'var(--radius-control)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--font-ar)', fontWeight: 500, cursor: 'pointer', marginBottom: 6, display: 'block' }}>
                            {uploadingLogo ? t.uploadingLogo : t.uploadLogo}
                          </button>
                          <p style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)' }}>
                            {isAr ? 'JPG أو PNG · حتى 5MB' : lang === 'zh' ? 'JPG 或 PNG · 最大 5MB' : 'JPG or PNG · Max 5MB'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', marginBottom: 12 }}>{t.factoryImages}</p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {(settings.factory_images || []).map((img, i) => (
                          <div key={i} style={{ width: 64, height: 64, borderRadius: 'var(--radius-control)', overflow: 'hidden', position: 'relative', flexShrink: 0, border: '1px solid var(--border)' }}>
                            <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            <button onClick={() => removeFactoryImage(img)} style={{ position: 'absolute', top: 3, right: 3, background: 'var(--ink)', color: 'var(--on-dark)', border: 'none', width: 16, height: 16, borderRadius: 'var(--radius-full)', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                          </div>
                        ))}
                        {(settings.factory_images || []).length < 3 && (
                          <>
                            <input ref={factoryRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadFactoryImage} />
                            <div
                              onClick={() => factoryRef.current?.click()}
                              style={{ width: 64, height: 64, borderRadius: 'var(--radius-control)', border: '1px dashed var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'border-color 0.2s' }}
                              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--ink)'}
                              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}>
                              {uploadingFactory
                                ? <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>...</span>
                                : <span style={{ fontSize: 20, color: 'var(--text-muted)' }}>+</span>}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Card 3: Company info ── */}
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '22px 20px' }}>
                  <p style={{ fontSize: 10, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', letterSpacing: isAr ? 0 : '1.2px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 18 }}>
                    {isAr ? 'معلومات الشركة' : lang === 'zh' ? '公司信息' : 'Company Info'}
                  </p>
                  <div className="settings-form-grid">
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>{t.companyName} *</label>
                      <input className="settings-input" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }} type="text" value={settings.company_name || ''} onChange={e => setSettings(prev => ({ ...prev, company_name: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>
                        {isAr ? 'نوع النشاط التجاري' : lang === 'zh' ? '企业类型' : 'Business type'}
                      </label>
                      <select className="settings-select" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', width: '100%' }} value={settings.business_type || ''} onChange={e => setSettings(prev => ({ ...prev, business_type: e.target.value }))}>
                        <option value="">{isAr ? 'اختر' : lang === 'zh' ? '请选择' : 'Select'}</option>
                        <option value="manufacturer">{isAr ? 'مصنع' : lang === 'zh' ? '制造商' : 'Manufacturer'}</option>
                        <option value="trading_company">{isAr ? 'شركة تجارية' : lang === 'zh' ? '贸易公司' : 'Trading Company'}</option>
                        <option value="agent">{isAr ? 'وكيل' : lang === 'zh' ? '代理商' : 'Agent'}</option>
                        <option value="distributor">{isAr ? 'موزع' : lang === 'zh' ? '经销商' : 'Distributor'}</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>{t.speciality}</label>
                      <select className="settings-select" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', width: '100%' }} value={settings.speciality || ''} onChange={e => setSettings(prev => ({ ...prev, speciality: e.target.value }))}>
                        <option value="">{isAr ? 'اختر' : lang === 'zh' ? '请选择' : 'Select'}</option>
                        {CATEGORIES[lang]?.filter(c => c.val !== 'all').map(c => <option key={c.val} value={c.val}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>
                        {isAr ? 'سنة التأسيس' : lang === 'zh' ? '成立年份' : 'Year established'}
                      </label>
                      <input className="settings-input" style={{ fontFamily: 'var(--font-sans)', direction: 'ltr' }} type="number" value={settings.year_established || ''} onChange={e => setSettings(prev => ({ ...prev, year_established: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>{t.city} *</label>
                      <input className="settings-input" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }} type="text" value={settings.city || ''} onChange={e => setSettings(prev => ({ ...prev, city: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>{t.country} *</label>
                      <input className="settings-input" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }} type="text" value={settings.country || ''} onChange={e => setSettings(prev => ({ ...prev, country: e.target.value }))} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>
                        {isAr ? 'عنوان الشركة' : lang === 'zh' ? '公司地址' : 'Company address'}
                      </label>
                      <input className="settings-input" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }} type="text" value={settings.company_address || ''} onChange={e => setSettings(prev => ({ ...prev, company_address: e.target.value }))} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>
                        {isAr ? 'موقع الشركة الإلكتروني' : lang === 'zh' ? '公司官网链接' : 'Company website URL'}
                      </label>
                      <input className="settings-input" style={{ fontFamily: 'var(--font-sans)', direction: 'ltr' }} type="url" value={settings.company_website || ''} onChange={e => setSettings(prev => ({ ...prev, company_website: e.target.value }))} />
                    </div>
                  </div>
                </div>

                {/* ── Card 4: Contact details ── */}
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '22px 20px' }}>
                  <p style={{ fontSize: 10, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', letterSpacing: isAr ? 0 : '1.2px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 18 }}>
                    {isAr ? 'بيانات التواصل' : lang === 'zh' ? '联系方式' : 'Contact Details'}
                  </p>
                  <div className="settings-form-grid">
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>{t.whatsapp}</label>
                      <input className="settings-input" style={{ fontFamily: 'var(--font-sans)', direction: 'ltr' }} type="tel" value={settings.whatsapp || ''} onChange={e => setSettings(prev => ({ ...prev, whatsapp: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>{t.wechat}</label>
                      <input className="settings-input" style={{ fontFamily: 'var(--font-sans)', direction: 'ltr' }} type="text" value={settings.wechat || ''} onChange={e => setSettings(prev => ({ ...prev, wechat: e.target.value }))} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>
                        {isAr ? 'اللغات' : lang === 'zh' ? '支持语言' : 'Languages'}
                      </label>
                      <input className="settings-input" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }} type="text" value={settings.languages || ''} onChange={e => setSettings(prev => ({ ...prev, languages: e.target.value }))} />
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', marginTop: 6 }}>
                        {isAr ? 'اكتبها مفصولة بفواصل أو أسطر جديدة' : lang === 'zh' ? '可用逗号或换行分隔' : 'Separate with commas or line breaks'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* ── Card 5: Commercial details ── */}
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '22px 20px' }}>
                  <p style={{ fontSize: 10, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', letterSpacing: isAr ? 0 : '1.2px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 18 }}>
                    {isAr ? 'التفاصيل التجارية' : lang === 'zh' ? '商业资料' : 'Commercial Details'}
                  </p>
                  <div className="settings-form-grid">
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>
                        {isAr ? 'الحد الأدنى لقيمة الطلب (USD)' : lang === 'zh' ? '最低订单金额 (USD)' : 'Minimum order value (USD)'}
                      </label>
                      <input className="settings-input" style={{ fontFamily: 'var(--font-sans)', direction: 'ltr' }} type="number" value={settings.min_order_value || ''} onChange={e => setSettings(prev => ({ ...prev, min_order_value: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>
                        {isAr ? 'عملة العرض' : lang === 'zh' ? '显示货币' : 'Display currency'}
                      </label>
                      <select className="settings-select" style={{ fontFamily: 'var(--font-sans)', direction: 'ltr', width: '100%' }} value={settings.preferred_display_currency || 'USD'} onChange={e => setSettings(prev => ({ ...prev, preferred_display_currency: e.target.value }))}>
                        {DISPLAY_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>
                        {isAr ? 'دعم التخصيص' : lang === 'zh' ? '定制支持' : 'Customization support'}
                      </label>
                      <select className="settings-select" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', width: '100%' }} value={settings.customization_support || ''} onChange={e => setSettings(prev => ({ ...prev, customization_support: e.target.value }))}>
                        <option value="">{isAr ? 'اختر' : lang === 'zh' ? '请选择' : 'Select'}</option>
                        <option value="yes">{isAr ? 'نعم' : lang === 'zh' ? '是' : 'Yes'}</option>
                        <option value="oem">OEM</option>
                        <option value="odm">ODM</option>
                        <option value="no">{isAr ? 'لا' : lang === 'zh' ? '否' : 'No'}</option>
                      </select>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>{t.tradeLink} *</label>
                      <input className="settings-input" style={{ fontFamily: 'var(--font-sans)', direction: 'ltr' }} type="url" value={settings.trade_link || ''} onChange={e => setSettings(prev => ({ ...prev, trade_link: e.target.value }))} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 6 }}>
                        {isAr ? 'الأسواق التي تصدّرون إليها' : lang === 'zh' ? '出口市场' : 'Export markets'}
                      </label>
                      <input className="settings-input" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }} type="text" value={settings.export_markets || ''} onChange={e => setSettings(prev => ({ ...prev, export_markets: e.target.value }))} />
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', marginTop: 6 }}>
                        {isAr ? 'اكتب الدول أو المناطق مفصولة بفواصل' : lang === 'zh' ? '填写国家或地区，逗号分隔' : 'List countries or regions, separated by commas'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* ── Card 6: Company description ── */}
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '22px 20px' }}>
                  <p style={{ fontSize: 10, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', letterSpacing: isAr ? 0 : '1.2px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 18 }}>
                    {isAr ? 'وصف الشركة' : lang === 'zh' ? '公司介绍' : 'Company Description'}
                  </p>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', display: 'block', marginBottom: 8 }}>
                    {isAr ? 'اكتب نبذة عن شركتك' : lang === 'zh' ? '请介绍您的公司' : 'Tell buyers about your company'}
                  </label>
                  <textarea
                    className="settings-textarea"
                    rows={5}
                    style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}
                    value={settings.company_description || ''}
                    onChange={e => setSettings(prev => ({ ...prev, company_description: e.target.value }))}
                    dir={isAr ? 'rtl' : 'ltr'}
                  />
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-ar)', marginTop: 8 }}>
                    {isAr ? 'يمكنك الكتابة بأي لغة' : lang === 'zh' ? '可使用任意语言填写' : 'You can write in any language'}
                  </p>
                </div>

                {/* ── Card 7: Certifications ── */}
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '22px 20px' }}>
                  <p style={{ fontSize: 10, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', letterSpacing: isAr ? 0 : '1.2px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 18 }}>
                    {isAr ? 'شهادات الجودة' : lang === 'zh' ? '质量认证' : 'Quality Certifications'}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 14 }}>
                    {(settings.certifications || []).map((cert, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: 8, flex: 1 }}>
                          <input
                            className="settings-input"
                            placeholder={isAr ? 'اسم الشهادة (مثال: ISO 9001)' : 'Cert name (e.g. ISO 9001)'}
                            style={{ fontFamily: 'var(--font-sans)', direction: 'ltr' }}
                            value={cert.name || ''}
                            onChange={e => {
                              setSettings(prev => {
                                const updated = [...(prev.certifications || [])];
                                updated[i] = { ...updated[i], name: e.target.value };
                                return { ...prev, certifications: updated };
                              });
                            }}
                          />
                          <input
                            className="settings-input"
                            placeholder={isAr ? 'الجهة المانحة' : 'Issuing body'}
                            style={{ fontFamily: 'var(--font-sans)', direction: 'ltr' }}
                            value={cert.issuer || ''}
                            onChange={e => {
                              setSettings(prev => {
                                const updated = [...(prev.certifications || [])];
                                updated[i] = { ...updated[i], issuer: e.target.value };
                                return { ...prev, certifications: updated };
                              });
                            }}
                          />
                          <input
                            className="settings-input"
                            placeholder={isAr ? 'صالحة حتى' : 'Valid until'}
                            style={{ fontFamily: 'var(--font-sans)', direction: 'ltr' }}
                            value={cert.valid_until || ''}
                            onChange={e => {
                              setSettings(prev => {
                                const updated = [...(prev.certifications || [])];
                                updated[i] = { ...updated[i], valid_until: e.target.value };
                                return { ...prev, certifications: updated };
                              });
                            }}
                          />
                        </div>
                        <button
                          onClick={() => {
                            setSettings(prev => ({
                              ...prev,
                              certifications: (prev.certifications || []).filter((_, j) => j !== i),
                            }));
                          }}
                          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, padding: '8px 4px', flexShrink: 0, lineHeight: 1 }}>
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, certifications: [...(prev.certifications || []), { name: '', issuer: '', valid_until: '' }] }))}
                    style={{ background: 'none', border: 'none', padding: 0, fontSize: 12, color: 'var(--ink)', cursor: 'pointer', fontFamily: 'var(--font-ar)', fontWeight: 600, textDecoration: 'underline' }}>
                    {isAr ? '+ إضافة شهادة' : lang === 'zh' ? '+ 添加认证' : '+ Add certification'}
                  </button>
                </div>

                {/* ── Save button ── */}
                <SaveFeedbackCard feedback={settingsFeedback} isAr={isAr} />
                <button
                  onClick={() => saveSettings()}
                  disabled={savingSettings}
                  style={{ background: 'var(--ink)', color: 'var(--on-dark)', border: 'none', borderRadius: 'var(--radius-control)', padding: '13px 32px', fontFamily: 'var(--font-ar)', fontSize: 14, fontWeight: 600, cursor: savingSettings ? 'default' : 'pointer', alignSelf: 'flex-start', fontVariantNumeric: 'lining-nums', opacity: savingSettings ? 0.7 : 1 }}>
                  {settingsPrimaryButtonLabel}
                </button>

              </div>
            </div>
          )}

        </div>
      </div>

      {/* ══════════════════════════════════════
          EDIT OFFER MODAL
      ══════════════════════════════════════ */}
      {selectedRequest && (
  <div className="mm-overlay" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', zIndex: 3000 }}>
    <div className="mm-card" style={{ background: 'var(--bg-overlay)', border: '1px solid var(--border-muted)', borderRadius: 'var(--radius-xl)', maxWidth: 520 }}>
     <div className="mm-body" style={{ padding: '36px 32px 8px' }}>
      <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 14 }}>
        {isAr ? 'تفاصيل الطلب' : lang === 'zh' ? '需求详情' : 'Request Details'}
      </p>
      <h3 style={{ fontSize: 20, fontWeight: 400, color: 'var(--text-primary)', marginBottom: 20, ...arFont }}>{getTitle(selectedRequest)}</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[
          // "Buyer" row REMOVED 2026-09-28: this modal only ever shows an OPEN
          // request (loadRequests filters open / offers_received), which is by
          // definition before any acceptance — so under the owner's rule the
          // trader's name can never appear here. The row could only say
          // "Trader" or "—", which is noise.
          // QUANTITY CARRIES ITS UNIT (2026-09-21). `500` alone was ambiguous
          // in the one place ambiguity costs money: 500 pieces and 500 cartons
          // are different quotes. requests.unit has had a writer since
          // 20260921122508 and this surface was never updated to read it.
          // Same expression as the public RFQ page (PublicRfqQuote.jsx:329-330,
          // 416) so a factory reads the identical string through either door.
          [isAr ? 'الكمية' : lang === 'zh' ? '数量' : 'Quantity',
            selectedRequest.quantity
              ? `${selectedRequest.quantity}${unitLabelFor(selectedRequest.unit) ? ` ${unitLabelFor(selectedRequest.unit)}` : ''}`
              : '—'],
          [isAr ? 'التصنيف' : lang === 'zh' ? '分类' : 'Category', cats.find(c => c.val === selectedRequest.category)?.label || selectedRequest.category || '—'],
          // The trader's target price (owner, 2026-09-28: the supplier sees it),
          // converted into the supplier's display currency as before.
          [termsCopy.targetPrice, selectedRequest.budget_per_unit
            ? formatPriceWithConversion({
                amount: parseFloat(selectedRequest.budget_per_unit),
                sourceCurrency: selectedRequest.budget_currency || 'SAR',
                displayCurrency: viewerCurrency,
                rates: exchangeRates,
                lang,
              })
            : '—'],
          // ── THE THREE FACTS A FACTORY CANNOT QUOTE WITHOUT (2026-09-21) ──
          // All three have been on `requests` since 20260921122508/122935 and
          // are already shown on the public /rfq/ link; this modal — the door a
          // ROUTED supplier comes through — was still showing the pre-2026-09-21
          // field set. Same columns, same joins, same trilingual sources.
          //
          // These four rows are CONDITIONAL while the rows around them render
          // '—'. That is deliberate, not drift: the columns are new, so every
          // request created before 2026-09-21 has them null, and four '—' rows
          // on an older request is noise that states nothing. The public RFQ
          // page makes the same call for the same reason
          // (PublicRfqQuote.jsx:415-441), and for the deadline it has a second
          // reason worth inheriting — an empty deadline row reads as an RFQ
          // nobody is waiting on, which is the opposite of the urgency that
          // makes a supplier quote today rather than next week.
          //
          // City first, then country: "Riyadh, Saudi Arabia" is what a factory
          // prices freight against; the country alone is what they assumed.
          ...(termValue(selectedRequest, 'destination') ? [[
            // Label and value from the shared builder since 2026-09-28 — the
            // city now in the reader's language, as on /rfq.
            termsCopy.destination, termValue(selectedRequest, 'destination'),
          ]] : []),
          // What the trader ASKED for, in their words — not an Incoterm. It is
          // what price_basis gets compared against (lib/deliveryMatch.js), so a
          // supplier who cannot see it here is choosing a basis blind and finds
          // out from the mismatch banner after submitting.
          // Told to the SUPPLIER with the Incoterm to quote (2026-09-28). The
          // trader's own label ("To my address") read, to a supplier, as the
          // supplier's address.
          ...(termValue(selectedRequest, 'delivery') ? [[
            termsCopy.delivery, termValue(selectedRequest, 'delivery'),
          ]] : []),
          ...(selectedRequest.response_deadline ? [[
            isAr ? 'آخر موعد للرد' : lang === 'zh' ? '报价截止' : 'Respond by',
            fmtDeadline(selectedRequest.response_deadline),
          ]] : []),
          [termsCopy.paymentTerms, termValue(selectedRequest, 'paymentPlan') || '—'],
          [termsCopy.sample, termValue(selectedRequest, 'sample') || '—'],
        ].map(([label, value]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ fontSize: 12, color: 'var(--text-disabled)', flexShrink: 0 }}>{label}</span>
            <span style={{ fontSize: 13, color: 'var(--text-primary)', textAlign: isAr ? 'left' : 'right', ...arFont }}>{value}</span>
          </div>
        ))}
        {/* Description row — rendered separately to handle Arabic-only fallback for ZH suppliers */}
        {(() => {
          const descLabel = isAr ? 'الوصف' : lang === 'zh' ? '描述' : 'Description';
          const zhFallback = lang === 'zh' && !selectedRequest.description_zh && !selectedRequest.description_en && !!(selectedRequest.description_ar || selectedRequest.description);
          const descText = isAr
            ? (selectedRequest.description_ar || selectedRequest.description || '—')
            : lang === 'zh'
              ? (selectedRequest.description_zh || selectedRequest.description_en || selectedRequest.description_ar || selectedRequest.description || '—')
              : (selectedRequest.description_en || selectedRequest.description || '—');
          return (
            <div style={{ padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontSize: 12, color: 'var(--text-disabled)', flexShrink: 0 }}>{descLabel}</span>
                <span style={{ fontSize: 13, color: 'var(--text-primary)', textAlign: isAr ? 'left' : 'right', direction: zhFallback ? 'rtl' : 'inherit', ...arFont }}>{descText}</span>
              </div>
              {zhFallback && (
                <p style={{ fontSize: 10, color: 'var(--text-disabled)', textAlign: 'right', margin: '4px 0 0', fontFamily: 'var(--font-ar)' }}>暂无中文翻译</p>
              )}
            </div>
          );
        })()}
        {/* The trader's photos and files (2026-10-11). Renders nothing when the
            request has none. */}
        <SupplierRequestAttachments sb={sb} lang={lang} requestId={selectedRequest.id}
          paths={selectedRequest.attachment_paths} referenceImage={selectedRequest.reference_image} />
      </div>
     </div>
      <div className="mm-foot" style={{ display: 'flex', gap: 10, padding: '16px 32px 28px', background: 'var(--bg-overlay)' }}>
        <button className="btn-dark-sm" onClick={() => { setSelectedRequest(null); toggleOfferForm(selectedRequest.id); }} style={{ flex: 1, minHeight: 44 }}>
          {isAr ? 'قدم عرضك' : lang === 'zh' ? '提交报价' : 'Submit Quote'}
        </button>
        <button className="btn-outline" onClick={() => setSelectedRequest(null)} style={{ minHeight: 44, padding: '0 18px' }}>
          {isAr ? 'إغلاق' : lang === 'zh' ? '关闭' : 'Close'}
        </button>
      </div>
    </div>
  </div>
)}
{editOfferModal && (
        /* LAYOUT REBUILT 2026-09-21. The card had no max-height and no
           scroller, so on a 768px-tall laptop it grew past the viewport and
           was clipped at BOTH ends — the Save and Cancel buttons were off
           screen with no way to reach them, which made the modal a trap
           rather than a form. It is now a three-row flex column: a pinned
           header, ONE scrolling body, and a pinned footer. Nothing about the
           fields, the values, the validation or the payload changed.
           Backdrop click closes, on mouseDown-target rather than click, so a
           drag that STARTS inside the card and happens to end on the backdrop
           does not throw the edit away. */
        <div
          onMouseDown={(e) => { if (e.target === e.currentTarget) setEditOfferModal(null); }}
          className="mm-overlay"
          style={{
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(4px)',
            zIndex: 3000,
            animation: 'fadeIn 0.2s ease',
          }}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-offer-title"
            dir={isAr ? 'rtl' : 'ltr'}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--radius-card)',
              width: '100%', maxWidth: 440,
              /* CORRECTED 2026-09-29 (item 13ب): this was
                 calc(100vh - 48px), which was right on a laptop and WRONG on
                 an iPhone — 100vh is the large viewport, so the card ran
                 ~85px past the visible screen, and centred in an overlay that
                 does not scroll, the bottom of the pinned footer (Save) was
                 cut off. The overlay is now .mm-overlay, sized to the visible
                 viewport with the home-indicator inset, so 100% of it is
                 exactly the room there is. */
              maxHeight: '100%',
              display: 'flex', flexDirection: 'column',
              /* Keeps the scrolling body's edges inside the rounded corners. */
              overflow: 'hidden',
              animation: 'slideUp 0.25s ease',
              boxShadow: 'none',
            }}>

            {/* ── HEADER, pinned ─────────────────────────────────────── */}
            <div style={{
              flex: '0 0 auto',
              padding: '22px 24px 16px',
              borderBottom: '1px solid var(--border-subtle)',
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-disabled)', margin: '0 0 8px' }}>
                  {isAr ? 'تعديل العرض' : lang === 'zh' ? '编辑报价' : 'Edit Offer'}
                </p>
                <p id="edit-offer-title" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', margin: 0, lineHeight: 1.5, ...arFont }}>
                  {getTitle(editOfferModal.requests)}
                </p>
              </div>
              {/* The close affordance the modal never had. Esc and a backdrop
                  click do the same thing; this is the one a visitor can SEE.
                  It carries no colour of its own — border-subtle and
                  text-disabled, the same pair the dismissable chips use. */}
              <button
                type="button"
                onClick={() => setEditOfferModal(null)}
                aria-label={isAr ? 'إغلاق' : lang === 'zh' ? '关闭' : 'Close'}
                style={{
                  flex: '0 0 auto',
                  width: 32, height: 32, lineHeight: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18,
                  background: 'transparent',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-disabled)',
                  cursor: 'pointer',
                }}>
                ×
              </button>
            </div>

            {/* ── BODY, the only scroller ────────────────────────────── */}
            <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '20px 24px' }}>

              {/* THE SHARED QUOTE CORE — the same component, the same words and
                  the same validation as both create doors. Before 2026-09-21
                  this modal was a hand-rolled form with its own field set:
                  it REQUIRED a shipping cost nothing collects any more, wrote
                  two deprecated columns, defaulted origin to 'China', and had
                  no price basis, validity or sample fields at all — so the one
                  field the trader compares on could not be corrected here.
                  The label/input styling now comes from the GLOBAL form CSS via
                  `classes`, so this modal's fields match the create form
                  instead of restating its rules as inline objects. Price and
                  currency already share a row inside the core. */}
              <QuoteFields
                t={qt}
                idPrefix="edit-offer"
                allowedBases={allowedBasesFor(deliveryTerms)}
                classes={{
                  label: `form-label${isAr ? ' ar' : ''}`,
                  input: 'form-input',
                }}
                styles={{
                  hint: { margin: '4px 0 0', fontSize: 12, color: 'var(--text-disabled)', lineHeight: 1.6, ...arFont },
                }}
                askSample={SAMPLE_ASKED.includes(String(editOfferModal?.requests?.sample_requirement || ''))}
                sampleRequirement={editOfferModal?.requests?.sample_requirement || ''}
                deliveryAsk={deliveryAskText(editOfferModal?.requests?.delivery_preference, lang)}
                basisWarning={deliveryShortfallText(editOfferForm.priceBasis, editOfferModal?.requests?.delivery_preference, lang)}
                isAr={isAr}
                cnyPerUsd={exchangeRates?.CNY}
                values={{
                  price: editOfferForm.price ?? '',
                  currency: editOfferForm.currency ?? 'USD',
                  moq: editOfferForm.moq ?? '',
                  leadTime: editOfferForm.leadTime ?? '',
                  origin: editOfferForm.origin ?? '',
                  note: editOfferForm.note ?? '',
                  priceBasis: editOfferForm.priceBasis ?? '',
                  validDays: editOfferForm.validDays ?? 30,
                  sampleAvailable: editOfferForm.sampleAvailable ?? false,
                  samplePrice: editOfferForm.samplePrice ?? '',
                  sampleDays: editOfferForm.sampleDays ?? '',
                }}
                onChange={(k, v) => setEditOfferForm((f) => ({ ...f, [k]: v }))}
              />

              {/* Est. Total is a computed DISPLAY, never an input — it must not
                  become a second pricing model. Shipping is not part of it: the
                  column has not been written since 20260919105244, and the
                  "Shipping: 0.00" tile that used to sit beside this one was
                  fed a hardcoded null. price_basis, collected above, is what
                  says who carries freight.
                  Now styled as the OFFER CARD's price grid on the My Offers
                  tab: same minmax(150px), same 10px/14px tile padding, and the
                  same 18px/300 lining-nums figure. The numbers a supplier reads
                  before saving should not be a size smaller than the ones they
                  read afterwards. */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 8,
                marginTop: 4,
              }}>
                <div style={{ padding: '10px 14px', background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                  <p style={{ fontSize: 10, color: 'var(--text-disabled)', marginBottom: 4 }}>{isAr ? 'إجمالي المنتجات' : lang === 'zh' ? '产品合计' : 'Products Total'}</p>
                  <p style={{ fontSize: 18, fontWeight: 300, color: 'var(--text-primary)', lineHeight: 1.1, direction: 'ltr', fontVariantNumeric: 'lining-nums' }}>
                    {formatCurrencyAmount(getOfferProductSubtotal({ price: editOfferForm.price }, editOfferModal?.requests), editOfferForm.currency || viewerCurrency, lang, { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div style={{ padding: '10px 14px', background: 'var(--bg-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
                  <p style={{ fontSize: 10, color: 'var(--text-disabled)', marginBottom: 4 }}>{isAr ? 'الإجمالي التقديري' : lang === 'zh' ? '预计总额' : 'Estimated Total'}</p>
                  <p style={{ fontSize: 18, fontWeight: 300, color: 'var(--text-primary)', lineHeight: 1.1, direction: 'ltr', fontVariantNumeric: 'lining-nums' }}>
                    {formatCurrencyAmount(getOfferEstimatedTotal({ price: editOfferForm.price }, editOfferModal?.requests), editOfferForm.currency || viewerCurrency, lang, { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>

            {/* ── FOOTER, pinned — the bug this rebuild exists for ────── */}
            <div style={{
              flex: '0 0 auto',
              padding: '16px 24px 20px',
              borderTop: '1px solid var(--border-subtle)',
              display: 'flex', gap: 10,
            }}>
              <button onClick={saveEditOffer} disabled={savingEditOffer} className="btn-primary"
                style={{ flex: 1, padding: '11px', fontSize: 12, minHeight: 44 }}>
                {savingEditOffer ? t.saving : t.save}
              </button>
              <button onClick={() => setEditOfferModal(null)} className="btn-outline"
                style={{ padding: '11px 18px', fontSize: 12, minHeight: 44 }}>
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          MOBILE BOTTOM NAVIGATION
      ══════════════════════════════════════ */}
      <nav className="supplier-bottom-nav" dir={isAr ? 'rtl' : 'ltr'}>
        {[
          {
            id: 'home',
            icon: '⌂',
            label: navLabel('supHome', lang),
            onClick: () => setActiveTab('overview'),
          },
          {
            id: 'requests',
            icon: '◈',
            label: navLabel('supRequests', lang),
            onClick: () => setActiveTab('requests'),
          },
          {
            id: 'products',
            icon: '▦',
            label: navLabel('supProducts', lang),
            onClick: () => setActiveTab('my-products'),
          },
          {
            id: 'messages',
            icon: '◉',
            label: navLabel('messages', lang),
            badge: stats.messages > 0 ? stats.messages : null,
            onClick: () => nav('/inbox'),
          },
          {
            id: 'more',
            icon: '⋯',
            label: navLabel('more', lang),
            badge: null,   // no red alert on the tab bar — the supplier has no error (decision #6)
            onClick: () => setMoreMenuOpen(true),
          },
        ].map(item => (
          <button
            key={item.id}
            className={`supplier-bottom-nav-item${activeBottomTab === item.id ? ' active' : ''}`}
            onClick={item.onClick}
          >
            {item.badge && <span className="nav-badge">{item.badge}</span>}
            <span className="nav-icon">{item.icon}</span>
            <span className={`nav-label${isAr ? ' ar' : ''}`}>{item.label}</span>
          </button>
        ))}
      </nav>

      {/* ══════════════════════════════════════
          MORE MENU (mobile slide-up sheet)
      ══════════════════════════════════════ */}
      {moreMenuOpen && (
        <>
          <div className="more-menu-overlay" onClick={() => setMoreMenuOpen(false)} />
          <div className="more-menu-sheet" dir={isAr ? 'rtl' : 'ltr'}>
            <div className="more-menu-handle" />
            {[
              // Same order as the desktop «المزيد» menu (SUPPLIER_MORE_TABS).
              { id: 'offers',             label: navLabel('supOffers', lang),          badge: null },
              { id: 'direct-orders',      label: isAr ? 'طلبات الشراء المباشر' : lang === 'zh' ? '直接采购订单' : 'Direct Purchase Orders', badge: directOrders.length > 0 ? directOrders.length : null },
              ...(SHOW_SUPPLIER_REFERRALS ? [{ id: 'wallet', label: isAr ? 'المحفظة والإحالات' : lang === 'zh' ? '钱包与推荐' : 'Wallet & Referrals', badge: null }] : []),
              { id: 'payout',             label: isAr ? 'المدفوعات' : lang === 'zh' ? '收款设置' : 'Payments',       badge: needsPayoutSetup ? '!' : null },
              { id: 'product-inquiries',  label: isAr ? 'استفسارات المنتجات' : lang === 'zh' ? '产品咨询' : 'Product Inquiries', badge: stats.productInquiries > 0 ? stats.productInquiries : null },
              { id: 'reviews',            label: isAr ? 'تقييماتي' : lang === 'zh' ? '我的评价' : 'Reviews',         badge: null },
              { id: 'samples',            label: isAr ? 'العينات' : lang === 'zh' ? '样品管理' : 'Samples',          badge: stats.pendingSamples > 0 ? stats.pendingSamples : null },
              { id: 'settings',           label: isAr ? 'الإعدادات' : lang === 'zh' ? '账户设置' : 'Settings',       badge: null },
            ].map(item => {
              const locked = lockedTabIds.includes(item.id);
              return (
                <button
                  key={item.id}
                  className={`more-menu-item${locked ? ' locked' : ''}`}
                  style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', textAlign: isAr ? 'right' : 'left' }}
                  onClick={() => { if (!locked) { setActiveTab(item.id); setMoreMenuOpen(false); } }}
                >
                  <span>{item.label}{locked && <span style={{ marginInlineStart: 8, fontSize: 10, color: 'var(--text-disabled)' }}>{isAr ? 'مقفل' : lang === 'zh' ? '锁定' : 'Locked'}</span>}</span>
                  {item.badge && <span className="item-badge">{item.badge}</span>}
                  {!item.badge && !locked && <span style={{ color: 'var(--text-disabled)', fontSize: 16 }}>{isAr ? '←' : '→'}</span>}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* marketing footer removed from post-login pages (decision #2) */}

      {invoiceOrder && (
        <OrderInvoiceModal
          requestId={invoiceOrder.requestId}
          order={invoiceOrder.order}
          role="supplier"
          lang={lang}
          onClose={() => setInvoiceOrder(null)}
        />
      )}
    </div>
  );
}